import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import {
  LayoutDashboard, Building2, FilePlus, FileText, Users, Package,
  HelpCircle, ChevronRight, Building, History, FileCheck, Bot, Archive, ClipboardList, Bug,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import api from './services/api';
import {
  cores, StatusBadge, Card, Input, Select, Textarea, Btn, Alerta,
  Modal, Tabela, fmtCnpj, fmtValor, MunicipioAutocomplete, ContadorCaracteres
} from './components/UI';
import { limparChaveNfe, validarChaveNfe } from './utils/chaveNfe';
import ModalTextoEvento from './components/ModalTextoEvento';
import CamposIcmsSt from './components/CamposIcmsSt';
import { ajusteTotalNota, calcularIcmsItem, dadosFiscaisDoProduto, precisaDadosFiscais } from './utils/icmsSt';
import { ExportacaoItem, ExportacaoNota, ImportacaoItem, ParceiroEstrangeiro, ehExportacao } from './components/Exterior';
import { LIMITE_CORRECAO, LIMITE_JUSTIFICATIVA } from './utils/textoEvento';
import { CHANGELOG_VERSAO, CHANGELOG_ITENS } from './changelog';
import {
  codigoIcmsAceito, opcoesIcmsDoRegime, produtoPrecisaRevisaoIcms, usaCsosn, codigoIcmsInicial, icmsPadraoDoRegime,
} from './utils/codigosIcms';
import { classificacaoPendente, resumoPendenciaEmpresa } from './utils/classificacao';
import {
  agruparPendencias, destinoCorrecao, pendenciasDoErro, sugestaoAplicavel,
} from './utils/pendencias';
import { corSituacao, fmtData, ibptPendente, podeConfirmar, rotuloIbpt, ROTULO_SITUACAO } from './utils/ibpt';
import { CATIVSN, TP_REGIME_CRT3, avisoSimples2027, rotuloCativsn, usaCativsn } from './utils/simples2027';
import { BASES_PIS_COFINS, basePisCofinsPendente, paraCsv, rotuloBasePisCofins } from './utils/conferencia';
import {
  consumidorFinalObrigatorio, erroConsumidorFinal, escolhaParaSelect, modoConsumidorFinal,
  selectParaEscolha, valorInicialConsumidorFinal,
} from './utils/consumidorFinal';

// Tabelas fiscais oficiais — usadas em múltiplas páginas (Produtos, Editor de NF-e).
// CST/CSOSN do ICMS por regime: utils/codigosIcms.js (F0a).
const ORIGEM_OPTS = [
  { codigo: 0, descricao: 'Nacional, exceto as indicadas nos códigos 3 a 5' },
  { codigo: 1, descricao: 'Estrangeira - Importação direta, exceto a indicada no código 6' },
  { codigo: 2, descricao: 'Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7' },
  { codigo: 3, descricao: 'Nacional, mercadoria ou bem com Conteúdo de Importação superior a 40%' },
  { codigo: 4, descricao: 'Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos (Dec-Lei 288/67, Leis 8.248/91, 8.387/91, 10.176/01 e 11.484/07)' },
  { codigo: 5, descricao: 'Nacional, mercadoria ou bem com Conteúdo de Importação inferior ou igual a 40%' },
  { codigo: 6, descricao: 'Estrangeira - Importação direta, sem similar nacional, constante em lista de Resolução CAMEX' },
  { codigo: 7, descricao: 'Estrangeira - Adquirida no mercado interno, sem similar nacional, constante em lista de Resolução CAMEX' },
];

// <option>s do regime. Código gravado que não está na lista (cadastro antigo)
// aparece marcado e desabilitado: o operador vê o que estava e escolhe outro.
function opcoesIcmsSelect(regime, valorAtual) {
  const atual = String(valorAtual || '').trim();
  const opts = opcoesIcmsDoRegime(regime).map(o => (
    <option key={o.codigo} value={o.codigo}>{o.codigo} - {o.descricao}</option>
  ));
  if (atual && !codigoIcmsAceito(regime, atual) && opcoesIcmsDoRegime(regime).length) {
    opts.unshift(<option key={'x' + atual} value={atual} disabled>{atual} - não aceito para esta empresa, escolha outro</option>);
  }
  return opts;
}

// Empresa do usuário logado (regime e código de ICMS padrão).
function useEmpresaAtual() {
  const { user } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  useEffect(() => {
    if (!user?.empresa_id) return;
    api.obterEmpresa(user.empresa_id).then(setEmpresa).catch(() => setEmpresa(null));
  }, [user?.empresa_id]);
  return empresa;
}


function normalizarDoc(doc) {
  // CNPJ alfanumérico (NT Conjunta 2025.001): letras ficam, em maiúsculas.
  return String(doc || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

// Coerência IE x Indicador IE (espelha backend utils/validacao_cliente.py).
// O XML só leva a IE quando o indicador é 1 — IE com outro indicador gera
// rejeição 232. "ISENTO" e só formatação/espaços contam como IE vazia.
function erroIeIndicador(ie, indicador) {
  const limpa = String(ie || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (limpa === '' || limpa === 'ISENTO') return null;
  const ind = Number(indicador ?? 9);
  if (ind === 2) return 'Cliente marcado como Isento (2) não pode ter IE informada. Deixe a IE em branco ou marque como Contribuinte ICMS (1).';
  if (ind !== 1) return 'Cliente com IE informada deve ser marcado como Contribuinte ICMS (1). Se o cliente for isento, deixe a IE em branco.';
  return null;
}

// Limites do layout NF-e 4.00 (conferidos no XSD). O backend recusa a emissão
// (HTTP 422) se um cadastro passar deles; aqui o campo não deixa digitar além e
// avisa valores antigos que já passaram. Nada é truncado automaticamente.
const LIMITE_NFE = {
  razao_social: 60, nome_fantasia: 60, logradouro: 60, numero: 60, complemento: 60,
  bairro: 60, municipio: 60, email: 60, descricao: 120, natureza_operacao: 60,
  info_complementar: 5000,
};
const ROTULO_LIMITE_NFE = {
  razao_social: 'Razão social', nome_fantasia: 'Nome fantasia', logradouro: 'Logradouro',
  numero: 'Número', complemento: 'Complemento', bairro: 'Bairro', municipio: 'Município',
  email: 'E-mail', descricao: 'Descrição',
};
function excessoLayoutNfe(obj, campos) {
  for (const c of campos) {
    const n = String(obj?.[c] ?? '').trim().length;
    if (n > LIMITE_NFE[c]) {
      return `${ROTULO_LIMITE_NFE[c] || c} tem ${n} caracteres (máximo ${LIMITE_NFE[c]} na NF-e). Abrevie antes de salvar.`;
    }
  }
  return null;
}
const CAMPOS_LIMITE_CLIENTE = ['razao_social', 'logradouro', 'numero', 'complemento', 'bairro', 'municipio', 'email'];
const CAMPOS_LIMITE_EMPRESA = ['razao_social', 'nome_fantasia', 'logradouro', 'numero', 'complemento', 'bairro', 'municipio'];

function detectarClientesDuplicados(clientes) {
  const contagem = {};
  (clientes || []).forEach(c => {
    if (c.ativo === false) return;
    const doc = normalizarDoc(c.cpf_cnpj);
    if (!doc || doc.length < 11) return;
    contagem[doc] = (contagem[doc] || 0) + 1;
  });
  return new Set(
    Object.entries(contagem)
      .filter(([, n]) => n > 1)
      .map(([d]) => d)
  );
}

function clienteEhDuplicado(cliente, setDuplicados) {
  if (!cliente || cliente.ativo === false) return false;
  const doc = normalizarDoc(cliente.cpf_cnpj);
  return setDuplicados.has(doc);
}

// ============================================================
// NAV CONTEXT — navegação entre páginas com payload opcional
// ============================================================
const NavContext = createContext({ navigate: () => {}, payload: null });
const useNav = () => useContext(NavContext);

// ============================================================
// AUTH CONTEXT
// ============================================================
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(api.getUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (api.token) {
      api.request('/auth/me').then(u => { setUser(u); api.setUser(u); })
        .catch(() => { api.clearToken(); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, senha) => {
    const data = await api.login(email, senha);
    setUser(data.usuario);
    return data;
  };

  const logout = () => { api.clearToken(); setUser(null); };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Carregando...</div>;

  return <AuthContext.Provider value={{ user, setUser, login, logout }}>{children}</AuthContext.Provider>;
}

const useAuth = () => useContext(AuthContext);

// ============================================================
// LOGIN
// ============================================================
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setErro('');
    try { await login(email, senha); }
    catch (err) { setErro(err.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f3' }}>
      <div style={{ background: '#fff', padding: '2.5rem', borderRadius: '16px', width: '380px', border: `1px solid ${cores.cinzaBorda}` }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ width: '48px', height: '48px', background: cores.verde, borderRadius: '12px', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontSize: '20px', fontWeight: 700 }}>NF</span>
          </div>
          <h2 style={{ fontSize: '20px', margin: 0 }}>Emissor NF-e Contabilizza</h2>
          <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>Contabilizza KT Prime</p>
        </div>
        {erro && <Alerta tipo="erro">{erro}</Alerta>}
        <form onSubmit={handleLogin}>
          <Input label="E-mail" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <Input label="Senha" type="password" value={senha} onChange={e => setSenha(e.target.value)} required />
          <Btn style={{ width: '100%', marginTop: '8px' }} size="lg" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// PAINEL CONTABILIDADE — Gestão de Empresas
// ============================================================
function EmpresasPage() {
  const { payload, navigate } = useNav();
  const [empresas, setEmpresas] = useState([]);
  const [modal, setModal] = useState(false);
  const [certModal, setCertModal] = useState(null);
  const [form, setForm] = useState({});
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [buscandoCnpjCliente, setBuscandoCnpjCliente] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [certSenha, setCertSenha] = useState('');
  const [msg, setMsg] = useState(null);
  const [usuariosPorEmpresa, setUsuariosPorEmpresa] = useState({});
  const [modalNovoOp, setModalNovoOp] = useState(null);
  const [modalEditarOp, setModalEditarOp] = useState(null);
  const [formOp, setFormOp] = useState({ email: '', nome: '', senha: '' });
  // S27b: produtos sem classificação IBS/CBS por empresa obrigada.
  const [pendClass, setPendClass] = useState({});
  // Quadro "Pendências por empresa" (o que travaria a emissão de cada uma).
  const [pendEmpresas, setPendEmpresas] = useState(null);
  // C2: componente do IBS/CBS sem alíquota oficial nos próximos 60 dias (alerta global).
  const [coberturaAliq, setCoberturaAliq] = useState([]);

  const carregar = useCallback(() => {
    api.listarEmpresas().then(setEmpresas).catch(console.error);
    api.pendenciasClassificacao()
      .then(lista => setPendClass(Object.fromEntries((lista || []).map(p => [p.empresa_id, p]))))
      .catch(() => setPendClass({}));
    api.pendenciasPorEmpresa().then(setPendEmpresas).catch(() => setPendEmpresas(null));
    api.coberturaAliquotasOficiais().then(d => setCoberturaAliq(d.alertas || [])).catch(() => setCoberturaAliq([]));
  }, []);

  // "Corrigir" das pendências da emissão: abre o cadastro (ou o certificado)
  // da empresa assim que a lista carregar.
  useEffect(() => {
    const id = payload?.editarEmpresaId;
    if (!id || empresas.length === 0) return;
    const row = empresas.find(e => e.id === id);
    if (!row) return;
    if (payload.abrirCertificado) setCertModal(row.id);
    else { setForm(row); setModal(true); }
  }, [payload?.editarEmpresaId, empresas.length]);

  const linhasPendEmpresas = (pendEmpresas || []).filter(p =>
    (p.sem_classificacao || 0) > 0 || p.codigo_invalido > 0
    || ['vencido', 'a_vencer', 'ausente'].includes(p.certificado)
    || ibptPendente(p) || basePisCofinsPendente(p) || !!avisoSimples2027(p));

  const carregarUsuarios = useCallback(() => {
    api.listarUsuarios()
      .then(lista => {
        const agrupado = {};
        lista.forEach(u => {
          const eid = u.empresa_id || '_admin';
          if (!agrupado[eid]) agrupado[eid] = [];
          agrupado[eid].push(u);
        });
        setUsuariosPorEmpresa(agrupado);
      })
      .catch(e => setMsg({ tipo: 'erro', texto: 'Erro ao carregar operadores: ' + e.message }));
  }, []);

  useEffect(() => { carregar(); carregarUsuarios(); }, [carregar, carregarUsuarios]);



  const buscarCnpjCliente = async () => {
    if (!form.cpf_cnpj || form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpjCliente(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        email: dados.email || prev.email,
        inscricao_estadual: dados.inscricao_estadual || prev.inscricao_estadual,
      }));
      alert('Dados carregados!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpjCliente(false);
  };

  const buscarCnpj = async () => {
    if (!form.cnpj || form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpj(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        cnae: dados.cnae || prev.cnae,
        regime_tributario: dados.regime_tributario || prev.regime_tributario,
      }));
      alert('Dados carregados com sucesso!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpj(false);
  };

  const salvar = async () => {
    const erroTam = excessoLayoutNfe(form, CAMPOS_LIMITE_EMPRESA);
    if (erroTam) { setMsg({ tipo: 'erro', texto: erroTam }); return; }
    // Validação local: garante que o IBGE foi preenchido antes de bater no backend.
    // Se o usuário escolheu Município mas o IBGE ficou vazio, é coerência: bloqueia
    // mesmo que o nome do município esteja correto, porque a SEFAZ exige o código.
    const ibge = (form.codigo_municipio || '').toString().trim();
    if (!ibge || ibge.replace(/\D/g, '').length !== 7) {
      setMsg({ tipo: 'erro', texto: 'Selecione o município pelo autocomplete — o Cód. Município IBGE (7 dígitos) é obrigatório.' });
      return;
    }
    try {
      if (form.id) {
        await api.atualizarEmpresa(form.id, form);
      } else {
        await api.criarEmpresa(form);
      }
      setModal(false); setForm({});
      carregar();
      setMsg({ tipo: 'sucesso', texto: 'Empresa salva com sucesso!' });
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const uploadCert = async () => {
    if (!certFile || !certSenha) {
      setMsg({ tipo: 'erro', texto: 'Selecione o arquivo .pfx e informe a senha' });
      return;
    }
    try {
      const res = await api.uploadCertificado(certModal, certFile, certSenha);
      setCertModal(null); setCertFile(null); setCertSenha('');
      carregar();
      setMsg({ tipo: 'sucesso', texto: `Certificado cadastrado! Validade: ${res.validade}` });
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const novaEmpresa = () => {
    setForm({
      razao_social: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '',
      logradouro: '', numero: '', complemento: '', bairro: '',
      codigo_municipio: '', municipio: '', uf: 'SP', cep: '',
      regime_tributario: 3, ambiente: 2, serie_nfe: 1,
    });
    setModal(true);
  };

  const regimeLabel = (r) => ({ 1: 'Simples Nacional', 2: 'Excesso Sublimite', 3: 'Regime Normal (LP/LR)', 4: 'Simples Nacional - MEI' }[r] || r);

  return (
    <div>
      <BannerVoltarEmissao retorno={payload?.retornoEmissao}
        onVoltar={() => navigate('emitir', { retomada: payload.retornoEmissao })} />
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '20px', margin: 0 }}>Empresas Clientes</h2>
        <Btn onClick={novaEmpresa}>+ Nova Empresa</Btn>
      </div>

      {/* Quadro do escritório: o que travaria a emissão de cada empresa. */}
      {pendEmpresas && (
        <div style={{ border: '1px solid #FCD34D', background: '#FFFBEB', borderRadius: 8, padding: '12px 16px', marginBottom: '1.25rem' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#78350F', marginBottom: 8 }}>
            Pendências por empresa {linhasPendEmpresas.length > 0 ? `(${linhasPendEmpresas.length})` : ''}
          </div>
          {coberturaAliq.map(a => (
            <div key={a.componente} style={{ fontSize: 13, color: cores.vermelho, marginBottom: 6 }}>
              ⚠️ Todas as empresas obrigadas: {a.mensagem} Cadastre em "Alíquotas IBS/CBS".
            </div>
          ))}
          {linhasPendEmpresas.length === 0 ? (
            <div style={{ fontSize: 13, color: cores.verde }}>Nenhuma empresa com pendência.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#78350F' }}>
                  <th style={{ padding: '4px 6px' }}>Empresa</th>
                  <th style={{ padding: '4px 6px' }}>Sem classificação IBS/CBS</th>
                  <th style={{ padding: '4px 6px' }}>Produtos com código inválido</th>
                  <th style={{ padding: '4px 6px' }}>Certificado</th>
                  <th style={{ padding: '4px 6px' }}>Tabela IBPT</th>
                  <th style={{ padding: '4px 6px' }}>Base do PIS/COFINS</th>
                  <th style={{ padding: '4px 6px' }}>Simples 2027</th>
                </tr>
              </thead>
              <tbody>
                {linhasPendEmpresas.map(p => (
                  <tr key={p.empresa_id} style={{ borderTop: '1px solid #FDE68A' }}>
                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{p.razao_social}</td>
                    <td style={{ padding: '4px 6px', color: p.sem_classificacao > 0 ? cores.vermelho : '#999' }}>
                      {p.sem_classificacao === null ? '— (não obrigada)' : p.sem_classificacao}
                    </td>
                    <td style={{ padding: '4px 6px', color: p.codigo_invalido > 0 ? cores.vermelho : '#999' }}>{p.codigo_invalido}</td>
                    <td style={{ padding: '4px 6px', color: ['vencido', 'ausente'].includes(p.certificado) ? cores.vermelho : (p.certificado === 'a_vencer' ? '#B45309' : '#999') }}>
                      {p.certificado === 'vencido' ? 'Vencido'
                        : p.certificado === 'ausente' ? 'Não cadastrado'
                        : p.certificado === 'a_vencer' ? `Vence em ${p.certificado_dias} dia(s)`
                        : 'Em dia'}
                    </td>
                    <td style={{ padding: '4px 6px', color: p.ibpt === 'pendente' ? cores.vermelho : (p.ibpt === 'vence_em_breve' ? '#B45309' : '#999') }}
                      title={p.ibpt_alerta || ''}>
                      {rotuloIbpt(p)}
                    </td>
                    <td style={{ padding: '4px 6px', color: basePisCofinsPendente(p) ? '#B45309' : '#999' }}
                      title={basePisCofinsPendente(p) ? 'Alerta (não bloqueia): enquanto não for salva, vale a receita da operação. Escolha em Editar.' : ''}>
                      {rotuloBasePisCofins(p)}
                    </td>
                    <td style={{ padding: '4px 6px', color: avisoSimples2027(p) ? '#B45309' : '#999' }}
                      title={avisoSimples2027(p) ? 'Aviso (não bloqueia em 2026): preencha em "Simples 2027" e no cadastro dos produtos.' : ''}>
                      {avisoSimples2027(p) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Tabela
        colunas={[
          { key: 'razao_social', label: 'Razão Social', bold: true },
          { key: 'cnpj', label: 'CNPJ', render: r => fmtCnpj(r.cnpj) },
          { key: 'uf', label: 'UF' },
          { key: 'regime_tributario', label: 'Regime', render: r => regimeLabel(r.regime_tributario) },
          { key: 'ambiente', label: 'Ambiente', render: r => r.ambiente === 1 ? 'Produção' : 'Homologação' },
          { key: 'certificado_validade', label: 'Certificado', render: r =>
            r.certificado_validade
              ? <span style={{ color: cores.verde }}>{r.certificado_validade.split('T')[0].split('-').reverse().join('/')}</span>
              : <span style={{ color: cores.vermelho }}>Não cadastrado</span>
          },
          { key: 'ibscbs', label: 'IBS/CBS', render: r => {
            const p = pendClass[r.id];
            const res = resumoPendenciaEmpresa(p);
            const cor = { nao_obrigada: '#999', pendente: cores.vermelho, ok: cores.verde }[res.tipo];
            return <span style={{ color: cor }} title={p ? `${p.produtos_ativos} produto(s) ativo(s)` : ''}>{res.texto}</span>;
          } },
        ]}
        dados={empresas}
        acoes={(row) => {
          const operadores = (usuariosPorEmpresa[row.id] || []).filter(u => u.perfil === 'operador');
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Btn variant="ghost" size="sm" onClick={() => { setForm(row); setModal(true); }}>Editar</Btn>
                <Btn variant="ghost" size="sm" onClick={() => setCertModal(row.id)} style={{ color: cores.roxo }}>Certificado</Btn>
              </div>
              {operadores.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                  {operadores.map(op => (
                    <div key={op.id} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontSize: '12px',
                      color: op.ativo ? cores.azul : '#999',
                      textDecoration: op.ativo ? 'none' : 'line-through',
                    }}>
                      <span title={op.nome}>{op.email}</span>
                      <button
                        onClick={() => { setFormOp({ email: op.email, nome: op.nome, senha: '' }); setModalEditarOp(op); }}
                        style={{ background: 'none', border: 'none', color: cores.azul, cursor: 'pointer', fontSize: '11px', padding: 0 }}
                      >Editar</button>
                      <button
                        onClick={() => {
                          if (!window.confirm(op.ativo
                            ? `Desativar operador ${op.email}? Ele não poderá mais fazer login.`
                            : `Reativar operador ${op.email}?`)) return;
                          api.alternarUsuarioAtivo(op.id, !op.ativo)
                            .then(() => {
                              setUsuariosPorEmpresa(prev => ({
                                ...prev,
                                [row.id]: prev[row.id].map(u => u.id === op.id ? { ...u, ativo: !op.ativo } : u),
                              }));
                              setMsg({ tipo: 'sucesso', texto: `Operador ${!op.ativo ? 'reativado' : 'desativado'}.` });
                            })
                            .catch(e => setMsg({ tipo: 'erro', texto: e.message }));
                        }}
                        style={{ background: 'none', border: 'none', color: op.ativo ? cores.vermelho : cores.verde, cursor: 'pointer', fontSize: '11px', padding: 0 }}
                      >{op.ativo ? 'Desativar' : 'Reativar'}</button>
                    </div>
                  ))}
                </div>
              )}
              <Btn variant="ghost" size="sm"
                onClick={() => { setFormOp({ email: '', nome: '', senha: '' }); setModalNovoOp({ empresa_id: row.id, empresa_nome: row.razao_social }); }}
                style={{ color: cores.amarelo, fontSize: '12px', marginTop: '4px' }}
              >+ Operador</Btn>
            </div>
          );
        }}
      />

      {/* Modal Nova/Editar Empresa */}
      <Modal titulo={form.id ? 'Editar Empresa' : 'Nova Empresa'} aberto={modal} onFechar={() => setModal(false)} largura="650px">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Input label="Razão Social *" value={form.razao_social || ''} onChange={e => setForm({ ...form, razao_social: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={60} contador />
          <Input label="Nome Fantasia" value={form.nome_fantasia || ''} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} maxLength={60} contador />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Input label="CNPJ *" value={form.cnpj || ''} onChange={e => setForm({ ...form, cnpj: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase() })} maxLength={14} placeholder="Digite o CNPJ" />
            </div>
            <button onClick={buscarCnpj} disabled={buscandoCnpj} style={{ padding: '8px 16px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap', height: '38px', fontSize: '13px', opacity: buscandoCnpj ? 0.6 : 1 }}>
              {buscandoCnpj ? 'Buscando...' : 'Buscar CNPJ'}
            </button>
          </div>
          <Input label="Inscrição Estadual *" value={form.inscricao_estadual || ''} onChange={e => setForm({ ...form, inscricao_estadual: e.target.value })} />
          <Input label="CNAE" value={form.cnae || ''} onChange={e => setForm({ ...form, cnae: e.target.value })} />
          <Select label="Regime Tributário *" value={form.regime_tributario || 3} onChange={e => {
            const regime = Number(e.target.value);
            // Código padrão de outro regime não vale mais: volta para "nenhum".
            setForm({ ...form, regime_tributario: regime,
                      icms_padrao: codigoIcmsAceito(regime, form.icms_padrao) ? form.icms_padrao : '',
                      // Simples 2027 (NT 2026.005, C21a): tipos 2, 3 e 4 só no Regime Normal.
                      tp_regime_ibscbs: regime === 3 ? (form.tp_regime_ibscbs ?? null) : null });
          }}>
            <option value={1}>Simples Nacional</option>
            <option value={2}>Excesso Sublimite</option>
            <option value={3}>Regime Normal (LP/LR)</option>
            <option value={4}>Simples Nacional - MEI</option>
          </Select>
          <Select label="Ambiente *" value={form.ambiente || 2} onChange={e => setForm({ ...form, ambiente: Number(e.target.value) })}>
            <option value={2}>Homologação (Testes)</option>
            <option value={1}>Produção</option>
          </Select>
          <Input label="Logradouro *" value={form.logradouro || ''} onChange={e => setForm({ ...form, logradouro: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={60} contador />
          <Input label="Número *" value={form.numero || ''} onChange={e => setForm({ ...form, numero: e.target.value })} maxLength={60} contador />
          <Input label="Complemento" value={form.complemento || ''} onChange={e => setForm({ ...form, complemento: e.target.value })} maxLength={60} contador />
          <Input label="Bairro *" value={form.bairro || ''} onChange={e => setForm({ ...form, bairro: e.target.value })} maxLength={60} contador />
          <Input label="UF *" value={form.uf || ''} onChange={e => setForm({ ...form, uf: e.target.value.toUpperCase() })} maxLength={2} />
          <Input label="CEP *" value={form.cep || ''} onChange={e => setForm({ ...form, cep: e.target.value.replace(/\D/g, '') })} maxLength={8} />
          <MunicipioAutocomplete
            uf={form.uf}
            municipio={form.municipio || ''}
            codigoIbge={form.codigo_municipio || ''}
            onChange={(nome, codigo) => setForm({ ...form, municipio: nome, codigo_municipio: codigo })}
            required
          />
          <Input label="Telefone" value={form.telefone || ''} onChange={e => setForm({ ...form, telefone: e.target.value })} />
          <Input label="Série NF-e" type="number" value={form.serie_nfe || 1} onChange={e => setForm({ ...form, serie_nfe: Number(e.target.value) })} />
          <Select label="Código de ICMS padrão" value={String(form.icms_padrao || '').trim()}
            onChange={e => setForm({ ...form, icms_padrao: e.target.value || null })}>
            <option value="">Padrão do regime ({icmsPadraoDoRegime(form.regime_tributario || 3)})</option>
            {opcoesIcmsSelect(form.regime_tributario || 3, form.icms_padrao)}
          </Select>
          {form.regime_tributario === 3 && (
            <Select label="Base do PIS/COFINS" value={form.base_pis_cofins || ''} style={{ gridColumn: '1/-1' }}
              onChange={e => setForm({ ...form, base_pis_cofins: e.target.value || null })}>
              <option value="">— confirmar (até salvar, vale a receita da operação) —</option>
              {BASES_PIS_COFINS.map(b => <option key={b.valor} value={b.valor}>{b.rotulo}</option>)}
            </Select>
          )}
          {form.regime_tributario === 3 && (
            <Select label="Contribuinte do IPI (indústria ou equiparado)" value={form.contribuinte_ipi ? 'sim' : ''} style={{ gridColumn: '1/-1' }}
              onChange={e => setForm({ ...form, contribuinte_ipi: e.target.value === 'sim' })}>
              <option value="">Não — as notas saem sem o IPI</option>
              <option value="sim">Sim — os itens levam o IPI do cadastro do produto</option>
            </Select>
          )}
          {form.regime_tributario === 3 && (
            <Select label="Simples 2027: opção pendente pelo Simples Nacional (tpRegimeIBSCBS — NT 2026.005, p. 8)"
              value={form.tp_regime_ibscbs ? String(form.tp_regime_ibscbs) : ''} style={{ gridColumn: '1/-1' }}
              onChange={e => setForm({ ...form, tp_regime_ibscbs: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Não — sem opção pendente pelo Simples (não informar)</option>
              {TP_REGIME_CRT3.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
            </Select>
          )}
        </div>
        {form.regime_tributario === 3 && (
          <Alerta tipo="reforma">
            <b>Reforma Tributária (LC 214/2025):</b> Empresa do regime normal — o IBS e a CBS de cada item seguem a classificação tributária (cClassTrib) cadastrada no produto. Produto sem classificação aparece como pendente na tela de Produtos.
          </Alerta>
        )}
        {(form.regime_tributario === 1 || form.regime_tributario === 2 || form.regime_tributario === 4) && (
          <Alerta tipo="info">Simples Nacional/MEI: dispensado de CBS/IBS em 2026.</Alerta>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="outline" onClick={() => setModal(false)}>Cancelar</Btn>
          <Btn onClick={salvar}>Salvar Empresa</Btn>
        </div>
      </Modal>

      {/* Modal Upload Certificado */}
      <Modal titulo="Upload Certificado Digital A1" aberto={!!certModal} onFechar={() => setCertModal(null)} largura="450px">
        <Alerta tipo="info">Selecione o arquivo .pfx (eCNPJ A1) e informe a senha do certificado.</Alerta>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Arquivo .pfx *</label>
          <input type="file" accept=".pfx,.p12" onChange={e => setCertFile(e.target.files[0])} style={{ fontSize: '14px' }} />
        </div>
        <Input label="Senha do certificado *" type="password" value={certSenha} onChange={e => setCertSenha(e.target.value)} />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setCertModal(null)}>Cancelar</Btn>
          <Btn onClick={uploadCert} style={{ background: cores.roxo }}>Enviar Certificado</Btn>
        </div>
      </Modal>

      {/* Modal Novo Operador */}
      <Modal
        titulo={`Novo Operador — ${modalNovoOp?.empresa_nome || ''}`}
        aberto={!!modalNovoOp}
        onFechar={() => setModalNovoOp(null)}
        largura="420px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Input label="Email" type="email" value={formOp.email} onChange={e => setFormOp({ ...formOp, email: e.target.value })} placeholder="operador@empresa.com" />
          <Input label="Nome completo" value={formOp.nome} onChange={e => setFormOp({ ...formOp, nome: e.target.value })} />
          <Input label="Senha" type="password" value={formOp.senha} onChange={e => setFormOp({ ...formOp, senha: e.target.value })} placeholder="Mínimo 6 caracteres" />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <Btn variant="outline" onClick={() => setModalNovoOp(null)}>Cancelar</Btn>
            <Btn onClick={() => {
              if (!formOp.email || !formOp.nome || !formOp.senha) {
                setMsg({ tipo: 'erro', texto: 'Preencha todos os campos.' });
                return;
              }
              if (formOp.senha.length < 6) {
                setMsg({ tipo: 'erro', texto: 'Senha deve ter no mínimo 6 caracteres.' });
                return;
              }
              api.registrarUsuario({
                empresa_id: modalNovoOp.empresa_id,
                email: formOp.email,
                nome: formOp.nome,
                senha: formOp.senha,
                perfil: 'operador',
              })
                .then(novo => {
                  setUsuariosPorEmpresa(prev => ({
                    ...prev,
                    [modalNovoOp.empresa_id]: [...(prev[modalNovoOp.empresa_id] || []), novo],
                  }));
                  setMsg({ tipo: 'sucesso', texto: `Operador ${formOp.email} criado.` });
                  setModalNovoOp(null);
                })
                .catch(e => setMsg({ tipo: 'erro', texto: e.message }));
            }}>Criar</Btn>
          </div>
        </div>
      </Modal>

      {/* Modal Editar Operador */}
      <Modal
        titulo={`Editar Operador — ${modalEditarOp?.email || ''}`}
        aberto={!!modalEditarOp}
        onFechar={() => setModalEditarOp(null)}
        largura="420px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Input label="Email" type="email" value={formOp.email} onChange={e => setFormOp({ ...formOp, email: e.target.value })} />
          <Input label="Nome completo" value={formOp.nome} onChange={e => setFormOp({ ...formOp, nome: e.target.value })} />
          <Input label="Nova senha (opcional)" type="password" value={formOp.senha} onChange={e => setFormOp({ ...formOp, senha: e.target.value })} placeholder="Deixe em branco para não alterar" />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <Btn variant="outline" onClick={() => setModalEditarOp(null)}>Cancelar</Btn>
            <Btn onClick={() => {
              if (!formOp.email || !formOp.nome) {
                setMsg({ tipo: 'erro', texto: 'Email e nome são obrigatórios.' });
                return;
              }
              if (formOp.senha && formOp.senha.length < 6) {
                setMsg({ tipo: 'erro', texto: 'Senha deve ter no mínimo 6 caracteres.' });
                return;
              }
              const payload = { email: formOp.email, nome: formOp.nome };
              if (formOp.senha) payload.senha = formOp.senha;
              api.atualizarUsuario(modalEditarOp.id, payload)
                .then(atualizado => {
                  setUsuariosPorEmpresa(prev => ({
                    ...prev,
                    [modalEditarOp.empresa_id]: prev[modalEditarOp.empresa_id].map(u =>
                      u.id === modalEditarOp.id ? { ...u, ...atualizado } : u
                    ),
                  }));
                  setMsg({ tipo: 'sucesso', texto: 'Operador atualizado.' });
                  setModalEditarOp(null);
                })
                .catch(e => setMsg({ tipo: 'erro', texto: e.message }));
            }}>Salvar</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.dashboard().then(setData).catch(e => setErro(e.message));
  }, []);

  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  if (!data) return <p style={{ color: '#888' }}>Carregando dashboard...</p>;

  const m = data.metricas;
  const r = data.reforma_tributaria_mes;

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '1.25rem' }}>Dashboard</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '1.5rem' }}>
        <Card titulo="Autorizadas" valor={m.total_autorizadas} sub={`Este mês: ${m.autorizadas_mes}`} cor={cores.verde} />
        <Card titulo="Canceladas" valor={m.total_canceladas} sub={`Este mês: ${m.canceladas_mes}`} cor={cores.vermelho} />
        <Card titulo="Faturamento mês" valor={`R$ ${fmtValor(m.faturamento_mes)}`} />
        <Card titulo="Clientes" valor={m.total_clientes} sub={`Produtos: ${m.total_produtos}`} cor={cores.azul} />
      </div>

      {r && r.total_cbs_ibs > 0 && (
        <Alerta tipo="reforma">
          <b>Reforma Tributária — Fase Teste (LC 214/2025)</b><br/>
          CBS: R$ {fmtValor(r.valor_cbs)} | IBS: R$ {fmtValor(r.valor_ibs)} | Total: R$ {fmtValor(r.total_cbs_ibs)}<br/>
          <span style={{ fontSize: '11px' }}>{r.nota}</span>
        </Alerta>
      )}

      <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Últimas notas</h3>

      <Tabela
        colunas={[
          { key: 'numero', label: 'Número', bold: true, render: r => String(r.numero).padStart(9, '0') },
          { key: 'data_emissao', label: 'Data', render: r => r.data_emissao ? new Date(r.data_emissao).toLocaleDateString('pt-BR') : '' },
          { key: 'valor_total', label: 'Valor', align: 'right', render: r => `R$ ${fmtValor(r.valor_total)}` },
          { key: 'status', label: 'Status', align: 'center', render: r => <StatusBadge status={r.status} /> },
        ]}
        dados={data.ultimas_notas || []}
      />
    </div>
  );
}

// ============================================================
// CLIENTES (DESTINATÁRIOS)
// ============================================================
function ClientesPage() {
  // CE1: tabela oficial de países (para o parceiro estrangeiro).
  const [paises, setPaises] = useState([]);
  useEffect(() => { api.listarPaises().then(setPaises).catch(() => setPaises([])); }, []);
  const { payload } = useNav();
  const [clientes, setClientes] = useState({ items: [] });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  // Atalho "Abrir cadastro do cliente" (banner de rejeição da emissão).
  useEffect(() => {
    if (!payload?.editarClienteId) return;
    api.obterCliente(payload.editarClienteId)
      .then(c => { setForm(c); setModal(true); })
      .catch(() => {});
  }, [payload?.editarClienteId]);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [buscandoCnpjCliente, setBuscandoCnpjCliente] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepErro, setCepErro] = useState('');
  const cepAbortRef = useRef(null);
  const [msg, setMsg] = useState(null);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [soProblemas, setSoProblemas] = useState(false);
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  const [mensagemInicialIA, setMensagemInicialIA] = useState('');

  const carregar = useCallback(() => {
    api.listarClientes(pagina, busca).then(setClientes).catch(console.error);
  }, [pagina, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  // Nova busca sempre recomeça da primeira página.
  useEffect(() => { setPagina(1); }, [busca]);

  // Operador editou manualmente sempre vence sobre auto-fill (CNPJ ou CEP).
  const manterSeJaPreenchido = (prevVal, fetchedVal) => {
    const t = (prevVal || '').toString().trim();
    return t ? prevVal : (fetchedVal || prevVal || '');
  };

  const buscarCep = async (cepDigits) => {
    const cep = (cepDigits || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    if (cepAbortRef.current) cepAbortRef.current.abort();
    const ctrl = new AbortController();
    cepAbortRef.current = ctrl;
    setBuscandoCep(true);
    setCepErro('');
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: ctrl.signal });
      if (!resp.ok) return;
      const dados = await resp.json();
      if (dados.erro) {
        setCepErro('CEP não encontrado');
        return;
      }
      setForm(prev => ({
        ...prev,
        logradouro: manterSeJaPreenchido(prev.logradouro, dados.logradouro),
        bairro: manterSeJaPreenchido(prev.bairro, dados.bairro),
        complemento: manterSeJaPreenchido(prev.complemento, dados.complemento),
        municipio: manterSeJaPreenchido(prev.municipio, dados.localidade),
        uf: manterSeJaPreenchido(prev.uf, (dados.uf || '').toUpperCase()),
        // IBGE: não preencher se ViaCEP retornar vazio (preserva autocomplete manual).
        codigo_municipio: dados.ibge ? manterSeJaPreenchido(prev.codigo_municipio, dados.ibge) : (prev.codigo_municipio || ''),
      }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Erro de rede / ViaCEP fora do ar: silencioso, cadastro continua manual.
    } finally {
      if (cepAbortRef.current === ctrl) {
        cepAbortRef.current = null;
        setBuscandoCep(false);
      }
    }
  };

  const fmtCepVisual = (digits) => {
    const d = (digits || '').toString().replace(/\D/g, '').slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  const buscarCnpjCliente = async () => {
    if (!form.cpf_cnpj || form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpjCliente(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        // Complemento manual nunca é sobrescrito por auto-fill.
        complemento: manterSeJaPreenchido(prev.complemento, dados.complemento),
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        email: dados.email || prev.email,
        inscricao_estadual: dados.inscricao_estadual || prev.inscricao_estadual,
      }));
      alert('Dados carregados!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpjCliente(false);
  };

  const buscarCnpj = async () => {
    if (!form.cnpj || form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpj(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        cnae: dados.cnae || prev.cnae,
        regime_tributario: dados.regime_tributario || prev.regime_tributario,
      }));
      alert('Dados carregados com sucesso!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpj(false);
  };

  const salvar = async () => {
    // CE1: parceiro estrangeiro — o backend fixa UF EX / 9999999 / EXTERIOR.
    if (form.tipo_documento === 'E') {
      try {
        const dados = { ...form, estrangeiro: true };
        if (form.id) await api.atualizarCliente(form.id, dados);
        else await api.criarCliente(dados);
        setModal(false); setForm({}); carregar();
        setMsg({ tipo: 'sucesso', texto: 'Parceiro estrangeiro salvo!' });
      } catch (err) {
        const extras = err.camposFaltantes?.length > 0 ? ' ' + err.camposFaltantes.join('; ') : '';
        setMsg({ tipo: 'erro', texto: err.message + extras });
      }
      return;
    }
    // Mesma regra do EmpresaModal: o IBGE do destinatário é obrigatório
    // (rejeição 272 da SEFAZ se vier inválido).
    const ibge = (form.codigo_municipio || '').toString().trim();
    if (!ibge || ibge.replace(/\D/g, '').length !== 7) {
      setMsg({ tipo: 'erro', texto: 'Selecione o município pelo autocomplete — o Cód. Município IBGE (7 dígitos) é obrigatório.' });
      return;
    }
    const erroIe = erroIeIndicador(form.inscricao_estadual, form.indicador_ie);
    if (erroIe) { setMsg({ tipo: 'erro', texto: erroIe }); return; }
    const erroTam = excessoLayoutNfe(form, CAMPOS_LIMITE_CLIENTE);
    if (erroTam) { setMsg({ tipo: 'erro', texto: erroTam }); return; }
    try {
      if (form.id) await api.atualizarCliente(form.id, form);
      else await api.criarCliente(form);
      setModal(false); setForm({}); carregar();
      setMsg({ tipo: 'sucesso', texto: 'Cliente salvo!' });
    } catch (err) {
      const extras = err.camposFaltantes?.length > 0 ? ' ' + err.camposFaltantes.join('; ') : '';
      setMsg({ tipo: 'erro', texto: err.message + extras });
    }
  };

  const excluir = async (cliente) => {
    const nome = cliente.razao_social || 'sem nome';
    const ok = window.confirm(
      `Desativar o cliente "${nome}"? Ele deixará de aparecer na listagem e na ` +
      `emissão de notas, mas o histórico de NF-e já emitidas será preservado.`
    );
    if (!ok) return;
    try {
      await api.excluirCliente(cliente.id);
      // Se este era o último item de uma página > 1, recua uma página para não
      // ficar numa página vazia. Caso contrário, recarrega a página atual.
      if ((clientes.items || []).length === 1 && pagina > 1) {
        setPagina(p => p - 1);
      } else {
        carregar();
      }
      setMsg({ tipo: 'sucesso', texto: 'Cliente desativado.' });
    } catch (err) {
      setMsg({ tipo: 'erro', texto: 'Erro ao desativar cliente: ' + err.message });
    }
  };

  const clientesItems = clientes.items || [];
  const docsDuplicados = detectarClientesDuplicados(clientesItems);
  const clientesDuplicados = clientesItems.filter(c => clienteEhDuplicado(c, docsDuplicados));
  const clientesIncompletos = clientesItems.filter(c => camposFaltantesDestinatario(c).length > 0);
  const clientesComProblema = clientesItems.filter(c =>
    clienteEhDuplicado(c, docsDuplicados) || camposFaltantesDestinatario(c).length > 0
  );
  const clientesExibidos = soProblemas ? clientesComProblema : clientesItems;
  const totalIncompletos = clientesIncompletos.length;

  const explicarComIA = () => {
    const exemplosDup = [...docsDuplicados].slice(0, 3).map(doc => {
      const ocorrencias = clientesDuplicados.filter(c => normalizarDoc(c.cpf_cnpj) === doc);
      const nomes = ocorrencias.map(c => c.razao_social || c.nome).join(' | ');
      return `CPF/CNPJ ${doc}: ${ocorrencias.length} cadastros — ${nomes}`;
    }).join('\n');

    setMensagemInicialIA(
      `Tenho problemas no cadastro de clientes:\n` +
      `- ${clientesDuplicados.length} cliente(s) duplicado(s) (mesmo CPF/CNPJ cadastrado mais de uma vez)\n` +
      `- ${clientesIncompletos.length} cliente(s) com cadastro incompleto\n\n` +
      (exemplosDup ? `Exemplos de duplicatas:\n${exemplosDup}\n\n` : '') +
      `Pode me explicar:\n` +
      `1. Por que cadastros duplicados são um problema fiscal?\n` +
      `2. Quais campos são obrigatórios em cliente PJ para emissão de NF-e?\n` +
      `3. Quais campos são obrigatórios em cliente PF (CPF)?\n` +
      `4. Posso simplesmente excluir um cliente duplicado se ele já tem notas emitidas?`
    );
    setAssistenteAberto(true);
  };

  return (
    <div>
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '20px', margin: 0 }}>Clientes</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', fontSize: '13px' }} />
          <Btn onClick={() => { setForm({ tipo_documento: 'J', indicador_ie: 9 }); setModal(true); }}>+ Novo Cliente</Btn>
        </div>
      </div>

      {clientesDuplicados.length > 0 && (
        <div style={{
          background: '#FEE2E2',
          border: '1px solid #DC2626',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: '260px' }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>🔴</span>
            <div>
              <div style={{ fontWeight: 600, color: '#991B1B' }}>
                {clientesDuplicados.length} cliente{clientesDuplicados.length > 1 ? 's' : ''} duplicado{clientesDuplicados.length > 1 ? 's' : ''} ({docsDuplicados.size} CPF/CNPJ{docsDuplicados.size > 1 ? 's' : ''} distinto{docsDuplicados.size > 1 ? 's' : ''})
              </div>
              <div style={{ fontSize: '13px', color: '#7F1D1D', marginTop: '4px' }}>
                Cadastros com mesmo CPF/CNPJ podem gerar inconsistências fiscais.
                ⚠️ Não exclua clientes que já tenham notas emitidas — consulte o contador antes de qualquer ação.
              </div>
            </div>
          </div>
        </div>
      )}

      {totalIncompletos > 0 && (
        <div style={{ fontSize: 12, color: '#92400E', marginBottom: 8 }}>
          ⚠️ {totalIncompletos} cliente{totalIncompletos > 1 ? 's' : ''} com cadastro incompleto — passe o mouse no ícone para ver os campos faltantes.
        </div>
      )}

      {(clientesDuplicados.length > 0 || clientesIncompletos.length > 0) && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={explicarComIA}
            style={{
              background: '#fff',
              color: '#92400E',
              border: '1px solid #F59E0B',
              padding: '8px 14px',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="Pergunte ao assistente de IA sobre cadastros duplicados/incompletos"
          >
            ✨ Explicar com IA
          </button>
          <button
            onClick={() => setSoProblemas(v => !v)}
            style={{
              background: soProblemas ? '#92400E' : '#F59E0B',
              color: '#fff',
              border: 'none',
              padding: '8px 14px',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {soProblemas ? 'Mostrar todos' : `Mostrar só problemas (${clientesComProblema.length})`}
          </button>
        </div>
      )}

      <Tabela
        colunas={[
          { key: 'razao_social', label: 'Razão Social', bold: true, render: r => {
            const faltam = camposFaltantesDestinatario(r);
            const duplicado = clienteEhDuplicado(r, docsDuplicados);
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {duplicado && (
                  <span title="CPF/CNPJ duplicado" style={{ fontSize: '12px', cursor: 'help' }}>🔴</span>
                )}
                {r.razao_social}
                {faltam.length > 0 && (
                  <span
                    title={`Cadastro incompleto. Falta: ${faltam.join(', ')}`}
                    style={{ marginLeft: 2, color: '#F59E0B', cursor: 'help' }}
                  >⚠️</span>
                )}
              </span>
            );
          } },
          { key: 'cpf_cnpj', label: 'CPF/CNPJ', render: r => r.cpf_cnpj?.length === 14 ? fmtCnpj(r.cpf_cnpj) : r.cpf_cnpj },
          { key: 'uf', label: 'UF' },
          { key: 'municipio', label: 'Município' },
          { key: 'email', label: 'E-mail' },
        ]}
        dados={clientesExibidos}
        acoes={(row) => (
          <span style={{ display: 'inline-flex', gap: '6px' }}>
            <Btn variant="ghost" size="sm" onClick={() => { setForm(row); setModal(true); }}>Editar</Btn>
            <Btn variant="ghost" size="sm" onClick={() => excluir(row)}>Excluir</Btn>
          </span>
        )}
      />

      {clientes.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => setPagina(p => Math.max(1, p - 1))}
            disabled={clientes.page === 1}
          >« Anterior</Btn>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>
            Página {clientes.page} de {clientes.pages} · {clientes.total} cliente{clientes.total === 1 ? '' : 's'}
          </span>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => setPagina(p => Math.min(clientes.pages, p + 1))}
            disabled={clientes.page === clientes.pages}
          >Próxima »</Btn>
        </div>
      )}

      <Modal titulo={form.id ? 'Editar Cliente' : 'Novo Cliente'} aberto={modal} onFechar={() => setModal(false)} largura="600px">
        {form.tipo_documento === 'E' ? (
          <ParceiroEstrangeiro form={form} setForm={setForm} paises={paises} />
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Input label="Razão Social *" value={form.razao_social || ''} onChange={e => setForm({ ...form, razao_social: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={60} contador />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Input label="CPF/CNPJ *" value={form.cpf_cnpj || ''} onChange={e => setForm({ ...form, cpf_cnpj: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase() })} maxLength={14} />
              </div>
              {form.tipo_documento === 'J' && (
                <button onClick={buscarCnpjCliente} disabled={buscandoCnpjCliente} style={{ padding: '8px 12px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap', height: '38px', fontSize: '12px', opacity: buscandoCnpjCliente ? 0.6 : 1 }}>
                  {buscandoCnpjCliente ? 'Buscando...' : 'Buscar CNPJ'}
                </button>
              )}
            </div>
          <Select label="Tipo" value={form.tipo_documento || 'J'} onChange={e => setForm({ ...form, tipo_documento: e.target.value })}>
            <option value="J">Pessoa Jurídica</option>
            <option value="F">Pessoa Física</option>
            <option value="E">Estrangeiro (exterior)</option>
          </Select>
          <Input label="Inscrição Estadual" value={form.inscricao_estadual || ''} onChange={e => setForm({ ...form, inscricao_estadual: e.target.value })} />
          <Select label="Indicador IE" value={form.indicador_ie || 9} onChange={e => setForm({ ...form, indicador_ie: Number(e.target.value) })}>
            <option value={1}>Contribuinte ICMS</option>
            <option value={2}>Isento</option>
            <option value={9}>Não contribuinte</option>
          </Select>
          {!consumidorFinalObrigatorio(form) && (
            <Select label="Compra para uso e consumo? (padrão na emissão)" value={escolhaParaSelect(form.consumidor_final)}
                    onChange={e => setForm({ ...form, consumidor_final: selectParaEscolha(e.target.value) })}>
              <option value="">Não definido — perguntar em cada nota</option>
              <option value="sim">Sim — uso e consumo ou ativo (consumidor final)</option>
              <option value="nao">Não — revenda ou industrialização</option>
            </Select>
          )}
          <Input label="E-mail" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} maxLength={60} contador />
          <Input label="Telefone" value={form.telefone || ''} onChange={e => setForm({ ...form, telefone: e.target.value.replace(/\D/g, '').slice(0, 14) })} maxLength={14} />
          {/* CEP é a porta de entrada do endereço — autopreenche logradouro/bairro/município/UF/IBGE via ViaCEP. */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>CEP</label>
            <div style={{ position: 'relative' }}>
              <input
                value={fmtCepVisual(form.cep)}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                  setForm({ ...form, cep: digits });
                  if (cepErro) setCepErro('');
                }}
                onBlur={() => buscarCep(form.cep)}
                maxLength={9}
                placeholder="00000-000"
                style={{
                  width: '100%', padding: '9px 12px', paddingRight: buscandoCep ? '78px' : '12px',
                  border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px',
                  fontSize: '14px', boxSizing: 'border-box', outline: 'none',
                }}
              />
              {buscandoCep && (
                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#666' }}>
                  Buscando…
                </span>
              )}
            </div>
            {cepErro && <div style={{ fontSize: '11px', color: '#B91C1C', marginTop: '4px' }}>{cepErro}</div>}
          </div>
          <div />
          <Input label="Logradouro" value={form.logradouro || ''} onChange={e => setForm({ ...form, logradouro: e.target.value })} maxLength={60} contador />
          <Input label="Número" value={form.numero || ''} onChange={e => setForm({ ...form, numero: e.target.value })} maxLength={60} contador />
          <Input label="Complemento" value={form.complemento || ''} onChange={e => setForm({ ...form, complemento: e.target.value })} maxLength={60} contador />
          <Input label="Bairro" value={form.bairro || ''} onChange={e => setForm({ ...form, bairro: e.target.value })} maxLength={60} contador />
          <Input label="UF *" value={form.uf || ''} onChange={e => setForm({ ...form, uf: e.target.value.toUpperCase() })} maxLength={2} />
          <MunicipioAutocomplete
            uf={form.uf}
            municipio={form.municipio || ''}
            codigoIbge={form.codigo_municipio || ''}
            onChange={(nome, codigo) => setForm({ ...form, municipio: nome, codigo_municipio: codigo })}
            required
          />
        </div>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="outline" onClick={() => setModal(false)}>Cancelar</Btn>
          <Btn onClick={salvar}>Salvar</Btn>
        </div>
      </Modal>

      <AssistenteIA
        aberto={assistenteAberto}
        fechar={() => { setAssistenteAberto(false); setMensagemInicialIA(''); }}
        nota={null}
        mensagemInicial={mensagemInicialIA}
      />
    </div>
  );
}

// ============================================================
// NCM AUTOCOMPLETE
// ============================================================
function NcmAutocomplete({ value, onChange, label = 'NCM *', style }) {
  const [texto, setTexto] = useState(value || '');
  const [sugestoes, setSugestoes] = useState([]);
  const [descOficial, setDescOficial] = useState('');
  const [aberto, setAberto] = useState(false);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => { setTexto(value || ''); }, [value]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const h = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setAberto(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const consultar = useCallback((termo) => {
    if (!termo || termo.length < 2) { setSugestoes([]); setLoading(false); return; }
    setLoading(true);
    api.buscarNcm(termo, 10)
      .then(res => { setSugestoes(res || []); setLoading(false); })
      .catch(() => { setSugestoes([]); setLoading(false); });
  }, []);

  const onDigitar = (v) => {
    setTexto(v);
    onChange(v);
    setAberto(true);
    setDescOficial('');
    setNaoEncontrado(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => consultar(v.trim()), 300);
    // Se for exatamente 8 dígitos, tenta casar com a tabela
    const apenasDigitos = v.replace(/\D/g, '');
    if (apenasDigitos.length === 8) {
      api.buscarNcm(apenasDigitos, 1).then(res => {
        const match = (res || []).find(r => r.codigo === apenasDigitos);
        if (match) { setDescOficial(match.descricao); setNaoEncontrado(false); }
        else { setDescOficial(''); setNaoEncontrado(true); }
      }).catch(() => {});
    }
  };

  const selecionar = (item) => {
    setTexto(item.codigo);
    onChange(item.codigo);
    setDescOficial(item.descricao);
    setNaoEncontrado(false);
    setAberto(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: '10px', ...style }}>
      <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>{label}</label>
      <input
        value={texto}
        onChange={e => onDigitar(e.target.value)}
        onFocus={() => { if (sugestoes.length) setAberto(true); }}
        maxLength={8}
        placeholder="8 dígitos ou descrição"
        autoComplete="off"
        style={{
          width: '100%', padding: '8px 10px', fontSize: '13px',
          border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px',
          boxSizing: 'border-box', background: '#fff',
        }}
      />
      {descOficial && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#2b6a1e' }}>✓ {descOficial}</p>
      )}
      {naoEncontrado && !descOficial && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#a67100' }}>
          ⚠️ NCM não encontrado na tabela oficial
        </p>
      )}
      {aberto && (sugestoes.length > 0 || loading) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px',
          marginTop: '2px', maxHeight: '240px', overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {loading && sugestoes.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '12px', color: '#888' }}>Buscando...</div>
          )}
          {sugestoes.map(s => (
            <div
              key={s.codigo}
              onMouseDown={e => { e.preventDefault(); selecionar(s); }}
              style={{
                padding: '7px 10px', fontSize: '12px', cursor: 'pointer',
                borderBottom: `1px solid ${cores.cinzaBorda}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <b>{s.codigo}</b> — {s.descricao.length > 80 ? s.descricao.slice(0, 80) + '…' : s.descricao}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Classificação tributária do IBS/CBS (cClassTrib), mesmo padrão do
// NcmAutocomplete: busca por código (prefixo) ou descrição na tabela oficial.
// O CST do IBS/CBS não é escolhido aqui: vem da classificação (o backend
// sempre deriva). onChange(codigo, linha) — codigo '' = "não classificado".
function CclasstribAutocomplete({ value, onChange, erro }) {
  const [texto, setTexto] = useState(value || '');
  const [nome, setNome] = useState('');
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Código já gravado: busca o nome para mostrar (código fora de vigência não
  // volta na busca; aí aparece só o código).
  useEffect(() => {
    setTexto(value || '');
    if (!value) { setNome(''); return; }
    let vivo = true;
    api.buscarCclasstrib(value, 1)
      .then(res => { if (vivo) setNome(((res || []).find(r => r.cclasstrib === value) || {}).nome || ''); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [value]);

  useEffect(() => {
    const h = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setAberto(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const consultar = useCallback((termo) => {
    if (!termo || termo.length < 2) { setSugestoes([]); setLoading(false); return; }
    setLoading(true);
    api.buscarCclasstrib(termo, 15)
      .then(res => { setSugestoes(res || []); setLoading(false); })
      .catch(() => { setSugestoes([]); setLoading(false); });
  }, []);

  const onDigitar = (v) => {
    setTexto(v);
    setAberto(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => consultar(v.trim()), 300);
  };

  const selecionar = (linha) => {
    setTexto(linha.cclasstrib);
    setNome(linha.nome);
    setAberto(false);
    onChange(linha.cclasstrib, linha);
  };

  const limpar = () => {
    setTexto(''); setNome(''); setSugestoes([]); setAberto(false);
    onChange('', null);
  };

  // Texto digitado sem escolher da lista não vale: ao sair, volta ao código atual.
  const aoSair = () => { if (texto !== (value || '')) setTexto(value || ''); };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: '10px' }}>
      <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>
        Classificação tributária (cClassTrib)
      </label>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          value={texto}
          onChange={e => onDigitar(e.target.value)}
          onFocus={() => { if (sugestoes.length) setAberto(true); }}
          onBlur={aoSair}
          placeholder="Código ou descrição — escolha na lista"
          autoComplete="off"
          style={{
            flex: 1, padding: '8px 10px', fontSize: '13px',
            border: `1px solid ${erro ? cores.vermelho : cores.cinzaBorda}`, borderRadius: '6px',
            boxSizing: 'border-box', background: '#fff',
          }}
        />
        <button
          type="button"
          onClick={limpar}
          disabled={!value}
          title="Deixar o produto sem classificação"
          style={{
            padding: '0 10px', fontSize: '12px', borderRadius: '6px', whiteSpace: 'nowrap',
            border: `1px solid ${cores.cinzaBorda}`, background: '#fff',
            color: value ? '#555' : '#bbb', cursor: value ? 'pointer' : 'default',
          }}
        >Não classificado</button>
      </div>
      {value && nome && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#2b6a1e' }}>✓ {value} — {nome}</p>
      )}
      {!value && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#a67100' }}>Não classificado (pendente)</p>
      )}
      {erro && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: cores.vermelho }}>{erro}</p>
      )}
      {aberto && (sugestoes.length > 0 || loading) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px',
          marginTop: '2px', maxHeight: '260px', overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {loading && sugestoes.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '12px', color: '#888' }}>Buscando...</div>
          )}
          {sugestoes.map(s => (
            <div
              key={s.cclasstrib}
              onMouseDown={e => { e.preventDefault(); selecionar(s); }}
              style={{
                padding: '7px 10px', fontSize: '12px', cursor: 'pointer',
                borderBottom: `1px solid ${cores.cinzaBorda}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <b>{s.cclasstrib}</b> — {s.nome && s.nome.length > 90 ? s.nome.slice(0, 90) + '…' : s.nome}
              <span style={{ color: '#888' }}> (CST {s.cst})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PRODUTOS
// ============================================================
function ProdutosPage() {
  const { payload, navigate } = useNav();
  // Classificação IBS/CBS (cClassTrib): só o escritório (admin) altera.
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'admin';
  const empresaAtual = useEmpresaAtual();
  const regime = empresaAtual?.regime_tributario ?? null;
  const rotuloIcms = regime === null ? 'CST/CSOSN' : (usaCsosn(regime) ? 'CSOSN' : 'CST');
  const [produtos, setProdutos] = useState({ items: [] });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  // Atalho "Abrir cadastro do produto" (banner de rejeição da emissão).
  useEffect(() => {
    if (!payload?.editarProdutoId) return;
    api.obterProduto(payload.editarProdutoId)
      .then(p => { setErroModal(null); setForm(p); setModal(true); })
      .catch(() => {});
  }, [payload?.editarProdutoId]);
  // "Corrigir" das pendências (item sem produto cadastrado): abre o Novo
  // Produto com o código e a descrição do item.
  useEffect(() => {
    const novo = payload?.novoProduto;
    if (!novo) return;
    setErroModal(null);
    setForm({ origem: 0, cst_icms: codigoIcmsInicial(regime, empresaAtual?.icms_padrao), cst_pis: '99', cst_cofins: '99', unidade: 'UN', cfop: '5102', cclasstrib: null, cst_ibscbs: null, codigo: novo.codigo || '', descricao: novo.descricao || '' });
    setModal(true);
  }, [payload?.novoProduto]);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [buscandoCnpjCliente, setBuscandoCnpjCliente] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busca, setBusca] = useState('');
  // S27b: erro do salvar mostrado dentro do modal (o 400 do backend) e filtro
  // "não classificados" (IBS/CBS) feito no servidor.
  const [erroModal, setErroModal] = useState(null);   // { texto, campo }
  const [soNaoClassificados, setSoNaoClassificados] = useState(false);

  // Exclusão lógica
  const [excluirConfirm, setExcluirConfirm] = useState(null);   // produto pendente de exclusão
  const [excluindo, setExcluindo] = useState(false);

  // Importação via XML ou PDF de NF-e
  const [importModal, setImportModal] = useState(false);       // modal de upload
  const [importFile, setImportFile] = useState(null);
  const [importErro, setImportErro] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [previewItens, setPreviewItens] = useState(null);       // null = sem preview; array = mostrar
  const [previewOrigem, setPreviewOrigem] = useState('xml');    // 'xml' ou 'pdf'
  const [salvandoImport, setSalvandoImport] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null); // resposta do confirmar com avisos/erros

  // Banner de CSOSN inválido + integração com assistente IA
  const [soPendentes, setSoPendentes] = useState(false);
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  const [mensagemInicialIA, setMensagemInicialIA] = useState('');

  const carregar = useCallback(() => {
    api.listarProdutos(1, busca, 50, soNaoClassificados ? 'pendente' : '')
      .then(setProdutos).catch(console.error);
  }, [busca, soNaoClassificados]);

  const abrirModal = (dados) => { setErroModal(null); setForm(dados); setModal(true); };

  useEffect(() => { carregar(); }, [carregar]);

  const abrirImport = () => {
    setImportFile(null); setImportErro(''); setPreviewItens(null); setPreviewOrigem('xml'); setResultadoImport(null); setImportModal(true);
  };

  const enviarXml = async () => {
    setImportErro('');
    if (!importFile) { setImportErro('Selecione um arquivo .xml ou .pdf.'); return; }
    const isPdf = /\.pdf$/i.test(importFile.name);
    const isXml = /\.xml$/i.test(importFile.name);
    if (!isPdf && !isXml) { setImportErro('Apenas arquivos .xml ou .pdf são aceitos.'); return; }
    const limite = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (importFile.size > limite) { setImportErro(`Arquivo excede ${isPdf ? '10' : '5'} MB.`); return; }
    setImportLoading(true);
    try {
      const res = await api.importarProdutosXml(importFile);
      const itens = (res.itens || []).map(it => ({
        ...it,
        selecionado: !it.duplicado,                     // novos marcados por padrão
        acaoDup: it.duplicado ? 'pular' : 'criar',      // default para duplicados = pular
      }));
      setPreviewOrigem(res.origem || 'xml');
      setPreviewItens(itens);
    } catch (err) { setImportErro(err.message); }
    setImportLoading(false);
  };

  const togglePreview = (idx, patch) => {
    setPreviewItens(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const confirmarImport = async () => {
    const selecionados = previewItens.filter(it => it.selecionado);
    if (selecionados.length === 0) { setImportErro('Selecione ao menos um produto.'); return; }
    if (previewOrigem === 'pdf') {
      const erros = [];
      selecionados.forEach((it, i) => {
        if (!it.descricao || !it.descricao.trim()) erros.push(`Linha ${i + 1}: descrição vazia`);
        if (!/^\d{8}$/.test(String(it.ncm || '').trim())) erros.push(`Linha ${i + 1}: NCM deve ter 8 dígitos`);
        if (!(Number(it.valor_unitario) > 0)) erros.push(`Linha ${i + 1}: valor unitário deve ser > 0`);
      });
      if (erros.length) { setImportErro(erros.join(' • ')); return; }
    }
    const itens = selecionados.map(it => ({
      codigo: it.codigo, descricao: it.descricao, ncm: it.ncm, cest: it.cest,
      cfop: it.cfop, unidade: it.unidade, valor_unitario: it.valor_unitario,
      ean: it.ean, origem: it.origem, cst_icms: it.cst_icms,
      aliquota_icms: it.aliquota_icms, cst_pis: it.cst_pis,
      aliquota_pis: it.aliquota_pis, cst_cofins: it.cst_cofins,
      aliquota_cofins: it.aliquota_cofins, cst_ipi: it.cst_ipi,
      aliquota_ipi: it.aliquota_ipi,
      id_existente: it.id_existente,
      acao: it.duplicado ? (it.acaoDup === 'atualizar' ? 'atualizar' : 'pular') : 'criar',
    }));
    setSalvandoImport(true); setImportErro('');
    try {
      const res = await api.importarProdutosConfirmar(itens);
      const parts = [];
      if (res.criados) parts.push(`${res.criados} novo(s) cadastrado(s)`);
      if (res.atualizados) parts.push(`${res.atualizados} atualizado(s)`);
      if (res.pulados) parts.push(`${res.pulados} pulado(s)`);
      setMsg({ tipo: 'sucesso', texto: 'Importação: ' + (parts.join(', ') || 'sem alterações') });
      carregar();
      // F0a-2: códigos trocados ou itens recusados ficam na tela até o
      // operador fechar; sem nada a mostrar, fecha como antes.
      if ((res.avisos || []).length || (res.erros || []).length) {
        setResultadoImport({ ...res, resumo: parts.join(', ') || 'sem alterações' });
        setPreviewItens(null);
      } else {
        setImportModal(false); setPreviewItens(null); setImportFile(null);
      }
    } catch (err) { setImportErro(err.message); }
    setSalvandoImport(false);
  };



  const buscarCnpjCliente = async () => {
    if (!form.cpf_cnpj || form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpjCliente(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cpf_cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        email: dados.email || prev.email,
        inscricao_estadual: dados.inscricao_estadual || prev.inscricao_estadual,
      }));
      alert('Dados carregados!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpjCliente(false);
  };

  const buscarCnpj = async () => {
    if (!form.cnpj || form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase().length !== 14) {
      alert('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    setBuscandoCnpj(true);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + form.cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      setForm(prev => ({
        ...prev,
        razao_social: dados.razao_social || prev.razao_social,
        nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        codigo_municipio: dados.codigo_municipio || prev.codigo_municipio,
        municipio: dados.municipio || prev.municipio,
        uf: dados.uf || prev.uf,
        cep: dados.cep || prev.cep,
        telefone: dados.telefone || prev.telefone,
        cnae: dados.cnae || prev.cnae,
        regime_tributario: dados.regime_tributario || prev.regime_tributario,
      }));
      alert('Dados carregados com sucesso!');
    } catch (err) {
      alert('Erro ao consultar CNPJ: ' + err.message);
    }
    setBuscandoCnpj(false);
  };

  const salvar = async () => {
    const erroTam = excessoLayoutNfe(form, ['descricao']);
    if (erroTam) { setErroModal({ texto: erroTam, campo: 'descricao' }); return; }
    try {
      if (form.id) await api.atualizarProduto(form.id, form);
      else await api.criarProduto(form);
      setModal(false); setForm({}); setErroModal(null); carregar();
      setMsg({ tipo: 'sucesso', texto: 'Produto salvo!' });
    } catch (err) { setErroModal({ texto: err.message, campo: err.dados?.campo || null }); }
  };

  const confirmarExcluir = async () => {
    if (!excluirConfirm) return;
    setExcluindo(true);
    try {
      await api.excluirProduto(excluirConfirm.id);
      setMsg({ tipo: 'sucesso', texto: 'Produto excluído com sucesso' });
      setExcluirConfirm(null);
      carregar();
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
    setExcluindo(false);
  };

  const todosProdutos = produtos.items || [];
  const produtosPendentes = todosProdutos.filter(p => produtoPrecisaRevisaoIcms(p, regime));
  const produtosExibidos = soPendentes ? produtosPendentes : todosProdutos;

  const explicarComIA = () => {
    const codigosInvalidos = [...new Set(
      produtosPendentes.map(p => String(p.cst_icms || '').trim())
    )].filter(Boolean).join(', ');

    setMensagemInicialIA(
      `Tenho ${produtosPendentes.length} produto(s) cadastrados com ${rotuloIcms} ` +
      `que o emissor não aceita para o regime da minha empresa. Os códigos atuais são: ${codigosInvalidos}.\n\n` +
      `Pode me explicar:\n` +
      `1. Por que esses códigos não são aceitos na minha NF-e?\n` +
      `2. Quais ${rotuloIcms} valem para o regime da minha empresa?\n` +
      `3. Como decidir qual ${rotuloIcms} aplicar em cada tipo de produto?`
    );
    setAssistenteAberto(true);
  };

  return (
    <div>
      <BannerVoltarEmissao retorno={payload?.retornoEmissao}
        onVoltar={() => navigate('emitir', { retomada: payload.retornoEmissao })} />
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '20px', margin: 0 }}>Produtos / Serviços</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', fontSize: '13px' }} />
          <Btn variant="outline" onClick={abrirImport}>📄 Importar de NF-e</Btn>
          <Btn onClick={() => abrirModal({ origem: 0, cst_icms: codigoIcmsInicial(regime, empresaAtual?.icms_padrao), cst_pis: '99', cst_cofins: '99', unidade: 'UN', cfop: '5102', cclasstrib: null, cst_ibscbs: null })}>+ Novo Produto</Btn>
        </div>
      </div>

      {produtosPendentes.length > 0 && (
        <div style={{
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: '260px' }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, color: '#92400E' }}>
                {produtosPendentes.length} produto{produtosPendentes.length > 1 ? 's' : ''} com código de ICMS não aceito para esta empresa
              </div>
              <div style={{ fontSize: '13px', color: '#78350F', marginTop: '4px' }}>
                Esses produtos têm {rotuloIcms} que não vale para o regime desta empresa ou que o emissor ainda não gera; a nota com eles é recusada.
                Edite cada um e selecione um código da lista ({opcoesIcmsDoRegime(regime).map(o => o.codigo).join(', ')}). Na dúvida, fale com o escritório.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={explicarComIA}
              style={{
                background: '#fff',
                color: '#92400E',
                border: '1px solid #F59E0B',
                padding: '8px 14px',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              title={`Pergunte ao assistente de IA sobre ${rotuloIcms}`}
            >
              ✨ Explicar com IA
            </button>
            <button
              onClick={() => setSoPendentes(v => !v)}
              style={{
                background: soPendentes ? '#92400E' : '#F59E0B',
                color: '#fff',
                border: 'none',
                padding: '8px 14px',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {soPendentes ? 'Mostrar todos' : 'Mostrar só pendentes'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontSize: '13px', color: '#555' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="checkbox" checked={soNaoClassificados} onChange={e => setSoNaoClassificados(e.target.checked)} />
          Só não classificados (IBS/CBS)
        </label>
        {produtos.sem_classificacao > 0 && (
          <span style={{ color: '#a67100' }}>
            {produtos.sem_classificacao} produto{produtos.sem_classificacao > 1 ? 's' : ''} sem classificação
          </span>
        )}
      </div>

      <Tabela
        colunas={[
          {
            key: 'codigo',
            label: 'Código',
            bold: true,
            render: (p) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {produtoPrecisaRevisaoIcms(p, regime) && (
                  <span title={`${rotuloIcms} não aceito para esta empresa — precisa correção`} style={{ fontSize: '14px' }}>⚠️</span>
                )}
                {p.codigo}
              </span>
            ),
          },
          { key: 'descricao', label: 'Descrição' },
          { key: 'ncm', label: 'NCM' },
          { key: 'cfop', label: 'CFOP' },
          { key: 'unidade', label: 'UN' },
          { key: 'valor_unitario', label: 'V.Unit', align: 'right', render: r => `R$ ${fmtValor(r.valor_unitario)}` },
          { key: 'aliquota_icms', label: 'ICMS %', align: 'right', render: r => `${r.aliquota_icms || 0}%` },
          { key: 'cclasstrib', label: 'Classif. IBS/CBS', render: r => classificacaoPendente(r)
            ? <span style={{ color: '#a67100', fontWeight: 600 }}>pendente</span>
            : <span title={`CST IBS/CBS ${r.cst_ibscbs || ''}`}>{r.cclasstrib}</span> },
        ]}
        dados={produtosExibidos}
        acoes={(row) => (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            <Btn variant="ghost" size="sm" onClick={() => abrirModal(row)}>Editar</Btn>
            <Btn variant="ghost" size="sm" style={{ color: cores.vermelho }} onClick={() => setExcluirConfirm(row)}>Excluir</Btn>
          </div>
        )}
      />

      <AssistenteIA
        aberto={assistenteAberto}
        fechar={() => { setAssistenteAberto(false); setMensagemInicialIA(''); }}
        nota={null}
        mensagemInicial={mensagemInicialIA}
      />

      <Modal titulo={form.id ? 'Editar Produto' : 'Novo Produto'} aberto={modal} onFechar={() => setModal(false)} largura="650px">
        {erroModal && <Alerta tipo="erro">{erroModal.texto}</Alerta>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Input label="Código *" value={form.codigo || ''} onChange={e => setForm({ ...form, codigo: e.target.value })} />
          <Input label="Descrição *" value={form.descricao || ''} onChange={e => setForm({ ...form, descricao: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={120} contador />
          <NcmAutocomplete value={form.ncm || ''} onChange={v => setForm({ ...form, ncm: v })} />
          <Input label="CEST" value={form.cest || ''} onChange={e => setForm({ ...form, cest: e.target.value })} maxLength={7} />
          <Input label="CFOP *" value={form.cfop || ''} onChange={e => setForm({ ...form, cfop: e.target.value })} maxLength={4} />
          <Input label="Unidade *" value={form.unidade || ''} onChange={e => setForm({ ...form, unidade: e.target.value.toUpperCase() })} maxLength={6} />
          <Input label="Valor Unitário *" type="number" step="0.01" value={form.valor_unitario || ''} onChange={e => setForm({ ...form, valor_unitario: Number(e.target.value) })} />
          <Input label="EAN/GTIN" value={form.ean || ''} onChange={e => setForm({ ...form, ean: e.target.value })} />
        </div>

        <h4 style={{ fontSize: '14px', margin: '16px 0 8px', color: cores.azul }}>Tributação ICMS</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
          <Select label="Origem" value={form.origem ?? 0} onChange={e => setForm({ ...form, origem: Number(e.target.value) })}>
            {ORIGEM_OPTS.map(o => (
              <option key={o.codigo} value={o.codigo}>{o.codigo} - {o.descricao}</option>
            ))}
          </Select>
          <Select
            label={`${rotuloIcms} ICMS`}
            value={String(form.cst_icms || '').trim()}
            onChange={e => setForm({ ...form, cst_icms: e.target.value })}
          >
            <option value="">— Selecione —</option>
            {opcoesIcmsSelect(regime, form.cst_icms)}
          </Select>
          <Input label="Alíquota ICMS %" type="number" step="0.01" value={form.aliquota_icms || ''} onChange={e => setForm({ ...form, aliquota_icms: Number(e.target.value) })} />
        </div>
        <CamposIcmsSt regime={regime} valores={form} editavel={isAdmin}
          onChange={(campo, v) => setForm(f => ({ ...f, [campo]: v }))} />
        {empresaAtual?.contribuinte_ipi && (
          <>
            <h4 style={{ fontSize: '14px', margin: '16px 0 8px', color: cores.azul }}>IPI</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
              <Input label="CST IPI" value={form.cst_ipi || ''} maxLength={2} onChange={e => setForm({ ...form, cst_ipi: e.target.value })} />
              <Input label="Alíquota IPI %" type="number" step="0.01" value={form.aliquota_ipi ?? ''} onChange={e => setForm({ ...form, aliquota_ipi: e.target.value === '' ? null : Number(e.target.value) })} />
              {isAdmin ? (
                <Input label="Enquadramento do IPI (cEnq)" value={form.enquadramento_ipi || ''} maxLength={3}
                  onChange={e => setForm({ ...form, enquadramento_ipi: e.target.value || null })} />
              ) : (
                <Input label="Enquadramento do IPI (cEnq) — escritório" value={form.enquadramento_ipi || ''} disabled />
              )}
            </div>
          </>
        )}

        <h4 style={{ fontSize: '14px', margin: '16px 0 8px', color: cores.azul }}>PIS / COFINS</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 16px' }}>
          <Input label="CST PIS" value={form.cst_pis || ''} onChange={e => setForm({ ...form, cst_pis: e.target.value })} />
          <Input label="Alíq. PIS %" type="number" step="0.01" value={form.aliquota_pis || ''} onChange={e => setForm({ ...form, aliquota_pis: Number(e.target.value) })} />
          <Input label="CST COFINS" value={form.cst_cofins || ''} onChange={e => setForm({ ...form, cst_cofins: e.target.value })} />
          <Input label="Alíq. COFINS %" type="number" step="0.01" value={form.aliquota_cofins || ''} onChange={e => setForm({ ...form, aliquota_cofins: Number(e.target.value) })} />
        </div>

        <h4 style={{ fontSize: '14px', margin: '16px 0 8px', color: cores.azul }}>IBS / CBS</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '0 16px' }}>
          {isAdmin ? (
            <CclasstribAutocomplete
              value={form.cclasstrib || ''}
              erro={erroModal?.campo === 'cclasstrib' ? erroModal.texto : null}
              onChange={(codigo, linha) => setForm({ ...form, cclasstrib: codigo || null, cst_ibscbs: linha ? linha.cst : null })}
            />
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                Classificação tributária (cClassTrib)
              </label>
              <div title="Definida pelo escritório" style={{
                padding: '8px 10px', fontSize: '13px', borderRadius: '6px',
                border: `1px solid ${cores.cinzaBorda}`, background: '#f5f5f5', color: '#555', minHeight: '17px',
              }}>{form.cclasstrib || 'Não classificado'}</div>
              {erroModal?.campo === 'cclasstrib' && (
                <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '4px' }}>{erroModal.texto}</div>
              )}
            </div>
          )}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>CST IBS/CBS</label>
            <div title="Definido pela classificação tributária" style={{
              padding: '8px 10px', fontSize: '13px', borderRadius: '6px',
              border: `1px solid ${cores.cinzaBorda}`, background: '#f5f5f5', color: '#555', minHeight: '17px',
            }}>{form.cclasstrib ? (form.cst_ibscbs || '—') : '—'}</div>
          </div>
        </div>
        {usaCativsn(empresaAtual) && (isAdmin ? (
          <Select label="Atividade do Simples (cAtivSN) — NT 2026.005, p. 8 (usado a partir de 2027)"
            value={form.cativsn || ''} onChange={e => setForm({ ...form, cativsn: e.target.value || null })}>
            <option value="">— não informado —</option>
            {CATIVSN.map(c => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
          </Select>
        ) : (
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>
              Atividade do Simples (cAtivSN) — definida pelo escritório
            </label>
            <div title="Definida pelo escritório" style={{
              padding: '8px 10px', fontSize: '13px', borderRadius: '6px',
              border: `1px solid ${cores.cinzaBorda}`, background: '#f5f5f5', color: '#555', minHeight: '17px',
            }}>{rotuloCativsn(form.cativsn)}</div>
          </div>
        ))}
        {isAdmin ? (
          <p style={{ fontSize: '11px', color: '#888', margin: '0 0 4px' }}>
            O CST do IBS/CBS vem da classificação escolhida e não é editado à mão. Na dúvida sobre a classificação, fale com o escritório.
          </p>
        ) : (
          <p style={{ fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 10px', margin: '0 0 4px' }}>
            A classificação IBS/CBS é definida pelo escritório. Para classificar ou trocar, solicite ao escritório.
          </p>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <Btn variant="outline" onClick={() => setModal(false)}>Cancelar</Btn>
          <Btn onClick={salvar}>Salvar Produto</Btn>
        </div>
      </Modal>

      {/* Confirmação de exclusão */}
      <Modal
        titulo="Excluir Produto"
        aberto={!!excluirConfirm}
        onFechar={() => { if (!excluindo) setExcluirConfirm(null); }}
        largura="460px"
      >
        <p style={{ fontSize: '14px', color: '#222', marginTop: 0 }}>
          Tem certeza que deseja excluir o produto <b>'{excluirConfirm?.descricao}'</b>?
        </p>
        <p style={{ fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
          O produto será removido da lista, mas o histórico de NF-e emitidas será preservado.
          Você pode restaurá-lo futuramente se necessário.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Btn variant="outline" onClick={() => setExcluirConfirm(null)} disabled={excluindo}>Cancelar</Btn>
          <Btn variant="danger" onClick={confirmarExcluir} disabled={excluindo}>
            {excluindo ? 'Excluindo...' : 'Excluir'}
          </Btn>
        </div>
      </Modal>

      {/* Importação via XML */}
      <Modal
        titulo={resultadoImport ? 'Importar Produtos — Resultado' : previewItens ? 'Importar Produtos — Preview' : 'Importar Produtos de NF-e'}
        aberto={importModal}
        onFechar={() => { if (!salvandoImport && !importLoading) { setImportModal(false); setPreviewItens(null); setImportFile(null); setImportErro(''); setResultadoImport(null); } }}
        largura={previewItens ? '900px' : '520px'}
      >
        {importErro && <Alerta tipo="erro">{importErro}</Alerta>}

        {resultadoImport && (
          <div>
            <Alerta tipo="sucesso">Importação: {resultadoImport.resumo}.</Alerta>
            {(resultadoImport.avisos || []).length > 0 && (
              <div>
                <p style={{ fontSize: '13px', color: '#7a5800', margin: '0 0 6px', fontWeight: 600 }}>
                  Códigos trocados na importação — confira cada produto:
                </p>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px', padding: '8px 10px', fontSize: '12px' }}>
                  {resultadoImport.avisos.map((a, i) => (
                    <div key={i} style={{ marginBottom: '6px' }}>
                      <b>{a.codigo}</b> — {a.descricao}
                      {a.mensagens.map((m, k) => <div key={k} style={{ color: '#7a5800' }}>⚠️ {m}</div>)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(resultadoImport.erros || []).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ fontSize: '13px', color: cores.vermelho, margin: '0 0 6px', fontWeight: 600 }}>
                  Itens não cadastrados:
                </p>
                <div style={{ fontSize: '12px', color: cores.vermelho }}>
                  {resultadoImport.erros.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Btn onClick={() => { setImportModal(false); setResultadoImport(null); setImportFile(null); }}>Fechar</Btn>
            </div>
          </div>
        )}

        {!previewItens && !resultadoImport && (
          <div>
            <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
              Envie um XML de NF-e recebida. O sistema extrai os produtos e permite selecionar quais cadastrar.
            </p>
            <div style={{
              border: `1.5px dashed ${cores.cinzaBorda}`, borderRadius: '8px',
              padding: '20px', textAlign: 'center', background: '#fafafa',
            }}>
              <input
                id="import-xml-input"
                type="file"
                accept=".xml,.pdf,text/xml,application/xml,application/pdf"
                onChange={e => setImportFile(e.target.files?.[0] || null)}
                style={{ display: 'block', margin: '0 auto 8px' }}
              />
              {importFile && (
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#555' }}>
                  Selecionado: <b>{importFile.name}</b> ({(importFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
              <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#888' }}>
                Formatos aceitos: .xml (até 5 MB) ou .pdf DANFE vetorial (até 10 MB). PDFs escaneados não são suportados.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Btn variant="outline" onClick={() => setImportModal(false)} disabled={importLoading}>Cancelar</Btn>
              <Btn onClick={enviarXml} disabled={!importFile || importLoading}>
                {importLoading ? 'Processando...' : 'Processar XML'}
              </Btn>
            </div>
          </div>
        )}

        {previewItens && (
          <div>
            {previewOrigem === 'pdf' && (
              <div style={{
                padding: '10px 12px', marginBottom: '10px', borderRadius: '6px',
                background: '#fff4d6', border: '1px solid #f0cf7a', color: '#6a4b00', fontSize: '12px',
              }}>
                ⚠️ Dados extraídos do PDF — revise antes de cadastrar. A extração automática pode conter imprecisões.
              </div>
            )}
            <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
              {previewItens.length} produto(s) encontrado(s).
              Duplicados aparecem como <b style={{ color: '#a67100' }}>Já existe</b> e vêm desmarcados por padrão.
            </p>

            <div style={{ maxHeight: '420px', overflowY: 'auto', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5' }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'center', width: '34px' }}></th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Código</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Descrição</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>NCM</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>CFOP</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>UN</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Valor</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItens.map((it, i) => {
                    const editavel = previewOrigem === 'pdf';
                    const cellInput = (key, opts = {}) => (
                      <input
                        value={it[key] == null ? '' : it[key]}
                        onChange={e => {
                          let v = e.target.value;
                          if (opts.upper) v = v.toUpperCase();
                          if (opts.digits) v = v.replace(/\D/g, '').slice(0, opts.maxLen || 20);
                          if (opts.number) v = v === '' ? '' : Number(v);
                          togglePreview(i, { [key]: v });
                        }}
                        type={opts.number ? 'number' : 'text'}
                        step={opts.number ? '0.0001' : undefined}
                        maxLength={opts.maxLen}
                        style={{
                          width: opts.width || '100%', padding: '3px 5px',
                          border: `1px solid ${cores.cinzaBorda}`, borderRadius: '4px',
                          fontSize: '12px', boxSizing: 'border-box',
                        }}
                      />
                    );
                    return (
                    <React.Fragment key={i}>
                    <tr style={{ borderTop: `1px solid ${cores.cinzaBorda}` }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={it.selecionado}
                          onChange={e => togglePreview(i, { selecionado: e.target.checked })}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', minWidth: 70 }}>{editavel ? cellInput('codigo', { maxLen: 60 }) : it.codigo}</td>
                      <td style={{ padding: '6px 8px', minWidth: 180 }}>{editavel ? cellInput('descricao', { maxLen: 255 }) : it.descricao}</td>
                      <td style={{ padding: '6px 8px', minWidth: 90 }}>{editavel ? cellInput('ncm', { digits: true, maxLen: 8 }) : it.ncm}</td>
                      <td style={{ padding: '6px 8px', minWidth: 60 }}>{editavel ? cellInput('cfop', { digits: true, maxLen: 4 }) : it.cfop}</td>
                      <td style={{ padding: '6px 8px', minWidth: 60 }}>{editavel ? cellInput('unidade', { upper: true, maxLen: 6 }) : it.unidade}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 90 }}>{editavel ? cellInput('valor_unitario', { number: true }) : `R$ ${fmtValor(it.valor_unitario)}`}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        {it.duplicado ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                              background: '#fff4d6', color: '#7a5800', fontSize: '11px', fontWeight: 600,
                            }}>Já existe</span>
                            <select
                              value={it.acaoDup}
                              onChange={e => togglePreview(i, { acaoDup: e.target.value, selecionado: e.target.value !== 'pular' })}
                              style={{ fontSize: '11px', padding: '2px 4px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '4px' }}
                            >
                              <option value="pular">Pular</option>
                              <option value="atualizar">Atualizar existente</option>
                            </select>
                          </div>
                        ) : (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                            background: '#e3f5e1', color: '#2b6a1e', fontSize: '11px', fontWeight: 600,
                          }}>Novo</span>
                        )}
                      </td>
                    </tr>
                    {(it.avisos_codigos || []).length > 0 && (
                      <tr>
                        <td></td>
                        <td colSpan={7} style={{ padding: '0 8px 6px', fontSize: '11px', color: '#7a5800' }}>
                          {it.avisos_codigos.map((a, k) => <div key={k}>⚠️ {a}</div>)}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Btn variant="outline" onClick={() => { setPreviewItens(null); setImportErro(''); }} disabled={salvandoImport}>Voltar</Btn>
              <Btn onClick={confirmarImport} disabled={salvandoImport}>
                {salvandoImport ? 'Salvando...' : `Cadastrar selecionados (${previewItens.filter(it => it.selecionado).length})`}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// EMITIR NF-e (com campos tributários)
// ============================================================
// Hoisted out of EmitirPage so React doesn't remount these wrappers on every
// keystroke (which would steal focus from any input nested inside them).
const SectionHeader = ({ title, children }) => (
  <div style={{ background: '#5b7aa2', color: '#fff', padding: '8px 16px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: 600 }}>
    {title}{children}
  </div>
);

const Section = ({ title, children, tabs, activeTab, onTabChange }) => (
  <div style={{ border: '1px solid #d0d5dd', borderRadius: '8px', marginBottom: '16px' }}>
    <SectionHeader title={title}>
      {tabs && <div style={{ display: 'flex', gap: '2px' }}>
        {tabs.map(t => <button key={t.key} onClick={() => onTabChange(t.key)} style={{ padding: '4px 12px', fontSize: '12px', background: activeTab === t.key ? '#fff' : 'transparent', color: activeTab === t.key ? '#5b7aa2' : '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>{t.label}</button>)}
      </div>}
    </SectionHeader>
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
);

const F = ({ label, children, required, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '3px', fontWeight: 500 }}>{label}{required && <span style={{ color: '#e53e3e' }}> *</span>}</label>
    {children}
  </div>
);

// Aceita formato pt-BR ("1.500,00", "150,50") e também o formato simples ("150.50").
const parseBR = (str) => {
  if (str == null || str === '') return 0;
  const s = String(str).trim();
  if (s === ',' || s === '.' || s === '-') return 0;
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return Number(s) || 0;
};

const MoneyInput = ({ value, onChange, style }) => {
  const [draft, setDraft] = useState(() => {
    const n = Number(value);
    return n ? String(n).replace('.', ',') : '';
  });
  const handleChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d.,]/g, '');
    setDraft(cleaned);
    onChange(parseBR(cleaned));
  };
  return (
    <input
      style={style}
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={handleChange}
      placeholder="0,00"
    />
  );
};

function camposFaltantesDestinatario(c) {
  if (!c) return [];
  const faltantes = [];
  const checar = (campo, rotulo) => {
    const v = c[campo];
    if (v === null || v === undefined || String(v).trim() === '') {
      faltantes.push(rotulo);
    }
  };
  // CE1: parceiro no exterior não tem CPF/CNPJ nem CEP obrigatório.
  const exterior = String(c.uf || '').toUpperCase() === 'EX';
  if (!exterior) checar('cpf_cnpj', 'CPF/CNPJ');
  checar('logradouro', 'Logradouro');
  checar('numero', 'Número');
  checar('bairro', 'Bairro');
  checar('municipio', 'Município');
  checar('codigo_municipio', 'Código IBGE do município');
  checar('uf', 'UF');
  if (!exterior) checar('cep', 'CEP');
  return faltantes;
}

// Editor de chaves de NF-e referenciadas (grupo <NFref>). Renderizado pelo
// formulário de emissão somente quando finalidade != 1. Definido em escopo
// de módulo para preservar foco entre keystrokes (cada re-render do EmitirPage
// recriaria o componente se estivesse aninhado, derrubando o input ativo —
// mesmo bug do select que já travou a tela da Ana Maria no passado).
const NF_REF_MAX = 10;

function NotasReferenciadas({ chaves, onChange }) {
  const setAt = (i, valor) => {
    const novo = chaves.slice();
    novo[i] = valor;
    onChange(novo);
  };
  const removeAt = (i) => {
    const novo = chaves.slice();
    novo.splice(i, 1);
    onChange(novo);
  };
  const handleBlur = (i) => {
    const limpa = limparChaveNfe(chaves[i]);
    if (limpa !== chaves[i]) setAt(i, limpa);
  };
  const adicionar = () => {
    if (chaves.length >= NF_REF_MAX) return;
    onChange([...chaves, '']);
  };

  const linhaStyle = { display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' };
  const inputBase = {
    padding: '7px 10px', border: '1px solid #d0d5dd', borderRadius: '6px',
    fontSize: '13px', flex: 1, boxSizing: 'border-box', fontFamily: 'monospace',
  };
  const inputErroBorder = { border: '1px solid #e53e3e' };
  const removerStyle = {
    padding: '6px 10px', background: '#fee', color: '#c00',
    border: '1px solid #fbb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  };
  const adicionarStyle = {
    padding: '7px 14px', background: '#5b7aa2', color: '#fff', border: 'none',
    borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
  };
  const adicionarDisabledStyle = { ...adicionarStyle, background: '#cbd5e0', cursor: 'not-allowed' };
  const erroMsgStyle = { color: '#c00', fontSize: '11px', marginTop: '2px' };

  return (
    <div>
      {chaves.map((chave, i) => {
        const limpa = limparChaveNfe(chave);
        const mostrarValidacao = limpa.length > 0;
        const result = mostrarValidacao ? validarChaveNfe(limpa) : { valida: true, erro: null };
        const invalida = mostrarValidacao && !result.valida;
        return (
          <div key={i}>
            <div style={linhaStyle}>
              <input
                type="text"
                autoCapitalize="characters"
                maxLength={54}
                placeholder="Chave de acesso (44 dígitos)"
                value={chave}
                onChange={e => setAt(i, e.target.value)}
                onBlur={() => handleBlur(i)}
                style={{ ...inputBase, ...(invalida ? inputErroBorder : {}) }}
              />
              <button type="button" onClick={() => removeAt(i)} style={removerStyle} title="Remover">✕</button>
            </div>
            {invalida && <div style={erroMsgStyle}>{result.erro}</div>}
          </div>
        );
      })}
      <button
        type="button"
        onClick={adicionar}
        disabled={chaves.length >= NF_REF_MAX}
        style={chaves.length >= NF_REF_MAX ? adicionarDisabledStyle : adicionarStyle}
      >
        + Adicionar chave de referência
      </button>
      {chaves.length >= NF_REF_MAX && (
        <span style={{ marginLeft: '12px', fontSize: '12px', color: '#666' }}>
          Limite de {NF_REF_MAX} chaves atingido
        </span>
      )}
    </div>
  );
}

// "Provável causa" de uma rejeição (diagnóstico do backend) + atalho para o
// cadastro a corrigir. Module-level: não recria inputs da EmitirPage.
const ROTULO_ABRIR_CADASTRO = {
  cliente: 'Abrir cadastro do cliente',
  produto: 'Abrir cadastro do produto',
  empresa: 'Abrir cadastro da empresa',
};
// Faixa "Voltar para a emissão" nos cadastros abertos pelo "Corrigir" das
// pendências. Componente de módulo (nunca definido dentro de um render).
function BannerVoltarEmissao({ retorno, onVoltar }) {
  if (!retorno) return null;
  return (
    <div style={{
      background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 8,
      padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 12,
      alignItems: 'center', color: '#78350F', fontSize: 13,
    }}>
      <span style={{ flex: 1 }}>
        Você veio das <b>pendências para emitir</b>. Corrija aqui e volte: a nota continua como estava.
      </span>
      <Btn size="sm" onClick={onVoltar}>← Voltar para a emissão</Btn>
    </div>
  );
}

// Detalhe técnico do erro (exceção, texto cru da SEFAZ): só para admin.
function DetalheTecnico({ texto }) {
  const { user } = useAuth();
  if (!texto || user?.perfil !== 'admin') return null;
  return (
    <details style={{ marginTop: 8, fontSize: 12 }}>
      <summary style={{ cursor: 'pointer', color: '#475569' }}>Ver detalhes técnicos</summary>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '6px 0 0', padding: 8, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, color: '#334155' }}>{texto}</pre>
    </details>
  );
}

// Botão "Relatar erro": envia os detalhes do erro ao e-mail do suporte. Depois
// de um envio com sucesso fica desabilitado enquanto o banner estiver na tela.
function RelatarErroBotao({ notaId, mensagemExibida, detalheTecnico, tela }) {
  const [aberto, setAberto] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [falha, setFalha] = useState('');
  const enviar = async () => {
    setEnviando(true); setFalha('');
    try {
      await api.relatarErro({
        nota_id: notaId || undefined,
        comentario: comentario.slice(0, 500),
        mensagem_exibida: mensagemExibida || '',
        detalhe_tecnico: detalheTecnico || '',
        tela,
      });
      setEnviado(true); setAberto(false);
    } catch (err) {
      // 429 (muitos relatos) traz mensagem própria; o resto, contato direto.
      setFalha(err.status === 429 ? err.message
        : 'Não foi possível enviar o relato agora. Fale com o suporte pelo WhatsApp (11) 4781-2596.');
    }
    setEnviando(false);
  };
  return (
    <div style={{ marginTop: 10 }}>
      <Btn variant="outline" size="sm" onClick={() => { setFalha(''); setAberto(true); }} disabled={enviado}
           style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff' }}>
        <Bug size={14} /> {enviado ? 'Erro relatado' : 'Relatar erro'}
      </Btn>
      {enviado && <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, color: '#166534' }}>Erro relatado. O suporte foi avisado.</span>}
      {falha && !aberto && <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600 }}>{falha}</div>}
      <Modal titulo="Relatar erro" aberto={aberto} onFechar={() => !enviando && setAberto(false)}>
        <p style={{ marginTop: 0, fontSize: 14, color: '#334155' }}>Vamos enviar os detalhes deste erro para o suporte.</p>
        <Textarea label="Descreva o que estava fazendo (opcional)" value={comentario} maxLength={500}
                  onChange={e => setComentario(e.target.value.slice(0, 500))} />
        <div style={{ fontSize: 11, color: '#64748b', marginTop: -10, marginBottom: 12, textAlign: 'right' }}>{comentario.length}/500</div>
        {falha && <Alerta tipo="erro">{falha}</Alerta>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setAberto(false)} disabled={enviando}>Cancelar</Btn>
          <Btn onClick={enviar} disabled={enviando}>{enviando ? 'Enviando...' : 'Enviar'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

function CausaProvavel({ diag, onAbrir, mostrarTexto = true, dica }) {
  if (!diag) return null;
  const cad = diag.cadastro;
  const podeAbrir = cad && ROTULO_ABRIR_CADASTRO[cad.tipo] && (cad.tipo === 'empresa' || cad.id);
  if (!mostrarTexto && !(podeAbrir && onAbrir)) return null;
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEE2E2', border: '1px solid #F87171', borderRadius: 6, color: '#7F1D1D' }}>
      {mostrarTexto && (
        <div style={{ fontSize: 14 }}><b>Provável causa:</b> {diag.mensagem}</div>
      )}
      {podeAbrir && onAbrir && (
        <div style={{ marginTop: mostrarTexto ? 8 : 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => onAbrir(cad)} style={{
            background: '#B91C1C', color: '#fff', border: 'none', borderRadius: 6,
            padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>{ROTULO_ABRIR_CADASTRO[cad.tipo]}</button>
          {dica && <span style={{ fontSize: 12 }}>{dica}</span>}
        </div>
      )}
    </div>
  );
}

function EmitirPage() {
  const { payload, navigate } = useNav();
  const { user } = useAuth();
  // Regime e código de ICMS padrão da empresa (F0a): a lista de CST/CSOSN do
  // item segue o regime, e item novo nunca nasce com código fixo.
  const empresaAtual = useEmpresaAtual();
  const regimeEmp = empresaAtual?.regime_tributario ?? null;
  // Código com que item novo nasce: padrão da empresa, senão o do regime
  // (102 no Simples/MEI, 00 no Regime Normal). Visível e editável no item.
  const icmsPadrao = codigoIcmsInicial(regimeEmp, empresaAtual?.icms_padrao);
  const rotuloIcmsItem = regimeEmp === null ? 'CST/CSOSN' : (usaCsosn(regimeEmp) ? 'CSOSN' : 'CST');
  const reemissao = payload?.reemissao || null;
  const rascunho = payload?.rascunho || null;
  // `pre` é o objeto de dados pré-carregados — vem de reemissao OU de rascunho.
  // Reaproveita exatamente os mesmos campos (nota, itens, pagamentos, cliente_id).
  const pre = reemissao || rascunho || null;
  // Volta de um cadastro aberto pelo "Corrigir" das pendências: a nota volta
  // exatamente como estava (estado bruto da tela), no último passo.
  const retomada = payload?.retomada || null;
  const isAdmin = user?.perfil === 'admin';
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [clienteId, setClienteId] = useState(retomada ? retomada.clienteId : (pre?.cliente_id || ''));
  const [clienteInfo, setClienteInfo] = useState(null);
  const [proximoNum, setProximoNum] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  // Trava anti-reenvio (656): só UX — a proteção real é o backend (HTTP 409).
  const [bloqueadoAte, setBloqueadoAte] = useState(null); // timestamp ms
  const [agoraMs, setAgoraMs] = useState(Date.now());
  useEffect(() => {
    if (!bloqueadoAte) return undefined;
    const t = setInterval(() => {
      const agora = Date.now();
      setAgoraMs(agora);
      if (agora >= bloqueadoAte) setBloqueadoAte(null);
    }, 1000);
    return () => clearInterval(t);
  }, [bloqueadoAte]);
  const segundosBloqueio = bloqueadoAte ? Math.max(0, Math.ceil((bloqueadoAte - agoraMs) / 1000)) : 0;
  const bloqueioEmissao = segundosBloqueio > 0;
  const [abaItem, setAbaItem] = useState(0);
  const [abaTributo, setAbaTributo] = useState('icms');
  const [bannerVisivel, setBannerVisivel] = useState(!!reemissao);
  const [reemissaoDeId] = useState(retomada ? retomada.reemissaoDeId : (reemissao?.reemissao_de_id || null));
  const [notaIdExistente] = useState(retomada ? retomada.notaIdExistente : (rascunho?.nota_id_existente || null));
  // Pendências para emitir: a última conferência do backend e o "retrato" da
  // nota em que ela foi feita. Mudou a nota, a lista fica desatualizada e o
  // Emitir confere de novo antes de enviar.
  const [conferencia, setConferencia] = useState(null);   // { chave, lista, alertas }
  // Avisos que não impedem a emissão (ex.: rejeição 679 antes da vigência): o operador
  // confirma uma vez por versão da nota antes de transmitir.
  const [alertasCientes, setAlertasCientes] = useState(null);
  const ultimosAlertas = useRef([]);
  const [modalPendencias, setModalPendencias] = useState(false);
  const [conferindo, setConferindo] = useState(false);
  const [itemDestacado, setItemDestacado] = useState(null);

  // Combobox de busca de produto (por linha de item). Estado mantido aqui no
  // EmitirPage — NUNCA extrair o combobox para subcomponente, senão o input
  // remonta a cada tecla e perde o foco. comboAbertoIdx = índice do item com a
  // lista aberta (ou null); comboBusca = texto digitado. Só um abre por vez.
  const [comboAbertoIdx, setComboAbertoIdx] = useState(null);
  const [comboBusca, setComboBusca] = useState('');

  // Combobox de busca de cliente (passo 2). Mesmo padrão do combobox de produto:
  // input SEMPRE montado, lista condicional, tudo inline no render — NUNCA extrair
  // para subcomponente (o input remontaria a cada tecla e perderia o foco).
  // clienteComboAberto = lista visível; clienteBusca = texto digitado.
  const [clienteComboAberto, setClienteComboAberto] = useState(false);
  const [clienteBusca, setClienteBusca] = useState('');

  // Wizard de emissão (UI somente — não afeta payload, cálculo, nem API)
  const [etapaAtual, setEtapaAtual] = useState(retomada?.etapa || 1);
  const [erroEtapa, setErroEtapa] = useState(null);
  const NOMES_ETAPAS = ['Dados NF-e', 'Dados Cliente', 'Itens da Nota', 'Transportador', 'Cobrança', 'Emissão'];

  // Modal de cadastro rápido de cliente (etapa 2). Estado mantido aqui para
  // que os inputs do modal sejam JSX inline do EmitirPage — caso fossem
  // extraídos para um sub-componente, o React recriaria a árvore a cada
  // setState e o foco do input em digitação seria perdido.
  const [modalClienteAberto, setModalClienteAberto] = useState(false);
  const [novoCliente, setNovoCliente] = useState({ tipo_documento: 'J', indicador_ie: 9 });
  const [salvandoCliente, setSalvandoCliente] = useState(false);
  const [erroModalCliente, setErroModalCliente] = useState(null);
  const [buscandoCnpjNC, setBuscandoCnpjNC] = useState(false);
  const [buscandoCepNC, setBuscandoCepNC] = useState(false);

  // Dados NF-e
  const [dadosNfe, setDadosNfe] = useState(() => {
    if (retomada) return retomada.dadosNfe;
    if (pre?.nota) {
      return {
        natureza_operacao: pre.nota.natureza_operacao || 'VENDA',
        tipo_operacao: pre.nota.tipo_operacao ?? 1,
        finalidade: pre.nota.finalidade ?? 1,
        forma_emissao: 1,
        tipo_atendimento: 0,
        intermediador: 0,
        modalidade_frete: pre.nota.modalidade_frete ?? 9,
        info_complementar: pre.nota.info_complementar || '',
        notas_referenciadas: pre.nota.notas_referenciadas || [],
        consumidor_final: typeof pre.nota.consumidor_final === 'boolean' ? pre.nota.consumidor_final : null,
        ...(pre.nota.exportacao ? { exportacao: pre.nota.exportacao } : {}),
      };
    }
    return {
      natureza_operacao: 'VENDA',
      tipo_operacao: 1,
      finalidade: 1,
      forma_emissao: 1,
      tipo_atendimento: 0,
      intermediador: 0,
      modalidade_frete: 9,
      info_complementar: '',
      notas_referenciadas: [],
      consumidor_final: null,
    };
  });

  // Itens
  const [itens, setItens] = useState(() => {
    if (retomada) return retomada.itens;
    if (pre?.itens?.length) {
      return pre.itens.map(it => ({
        codigo_produto: it.codigo_produto || '',
        descricao: it.descricao || '',
        ncm: it.ncm || '',
        cest: it.cest || '',
        cfop: it.cfop || '5102',
        unidade: it.unidade || 'UN',
        quantidade: Number(it.quantidade) || 1,
        valor_unitario: Number(it.valor_unitario) || 0,
        ean: it.ean || '',
        valor_desconto: Number(it.valor_desconto || 0),
        valor_frete: Number(it.valor_frete || 0),
        valor_seguro: Number(it.valor_seguro || 0),
        valor_outros: Number(it.valor_outros || 0),
        origem: it.origem ?? 0,
        cst_icms: String(it.cst_icms || '').trim(),
        aliquota_icms: Number(it.aliquota_icms || 0),
        bc_icms_manual: Number(it.bc_icms_manual || 0),
        cst_ipi: it.cst_ipi || '99',
        aliquota_ipi: Number(it.aliquota_ipi || 0),
        cst_pis: it.cst_pis || '99',
        aliquota_pis: Number(it.aliquota_pis || 0),
        cst_cofins: it.cst_cofins || '99',
        aliquota_cofins: Number(it.aliquota_cofins || 0),
        info_adicional: '', num_pedido: '', item_pedido: '',
        chave_ref_devolucao: it.chave_ref_devolucao || '',
        item_ref_devolucao: it.item_ref_devolucao ?? '',
        // F0c/F0d: dados fiscais gravados no item (rascunho/reemissão) e o valor desonerado.
        ...dadosFiscaisDoProduto(it),
        valor_icms_desonerado: it.valor_icms_desonerado ?? null,
        enquadramento_ipi: it.enquadramento_ipi || '',
        percentual_devolucao: it.percentual_devolucao ?? null,
        valor_ipi_devolvido: it.valor_ipi_devolvido ?? null,
        // CE2: dados de importação gravados (rascunho/reemissão).
        ...(it.importacao ? { importacao: it.importacao } : {}),
        ...(it.imposto_importacao ? { imposto_importacao: it.imposto_importacao } : {}),
        // CE5: detalhe da exportação gravado (rascunho/reemissão).
        ...(it.exportacao ? { exportacao: it.exportacao } : {}),
      }));
    }
    return [];
  });

  // Item incluído antes de a empresa carregar ficou sem código: preenche
  // com o padrão assim que ele for conhecido (aparece no item, editável).
  useEffect(() => {
    if (!icmsPadrao) return;
    setItens(atual => atual.some(it => !String(it.cst_icms || '').trim())
      ? atual.map(it => (String(it.cst_icms || '').trim() ? it : { ...it, cst_icms: icmsPadrao }))
      : atual);
  }, [icmsPadrao]);

  // Pagamento
  const [pagamentos, setPagamentos] = useState(() => {
    if (retomada) return retomada.pagamentos;
    if (pre?.pagamentos?.length) {
      return pre.pagamentos.map(p => ({
        forma_pagamento: p.forma_pagamento || '01',
        valor: Number(p.valor) || 0,
      }));
    }
    return [{ forma_pagamento: '01', valor: 0 }];
  });

  // Transportador / Veículo / Volume — pré-carrega de rascunho/reemissão.
  const [transp, setTransp] = useState(() => {
    if (retomada) return retomada.transp;
    const n = pre?.nota || {};
    return {
      tipo_frete: String(n.modalidade_frete ?? 9),
      cpf_cnpj: n.transportador_cpf_cnpj || '',
      razao_social: n.transportador_razao_social || '',
      ie: n.transportador_ie || '',
      endereco: n.transportador_endereco || '',
      municipio: n.transportador_municipio || '',
      uf: n.transportador_uf || '',
      antt: n.transportador_antt || '',
      placa: n.transportador_placa || '',
      placa_uf: n.transportador_placa_uf || '',
      quantidade: n.volume_quantidade != null ? String(n.volume_quantidade) : '',
      especie: n.volume_especie || '',
      marca: n.volume_marca || '',
      numeracao: n.volume_numeracao || '',
      peso_liquido: n.peso_liquido != null ? String(n.peso_liquido) : '',
      peso_bruto: n.peso_bruto != null ? String(n.peso_bruto) : '',
    };
  });

  useEffect(() => {
    api.listarClientes(1, '', 2000).then(r => setClientes(r.items || [])).catch(() => {});
    api.listarProdutos(1, '', 2000).then(r => setProdutos(r.items || [])).catch(() => {});
    api.proximoNumero().then(r => {
      setProximoNum(r);
      // Bloqueio 656 gravado no banco: o contador mostra o prazo real mesmo
      // depois de recarregar a tela ou trocar de computador.
      const ate = r?.bloqueio_sefaz?.liberado_em ? Date.parse(r.bloqueio_sefaz.liberado_em) : NaN;
      if (!Number.isNaN(ate) && ate > Date.now()) { setAgoraMs(Date.now()); setBloqueadoAte(ate); }
    }).catch(() => {});
  }, []);

  // Quando seleciona cliente, carrega info
  useEffect(() => {
    if (clienteId) {
      const c = clientes.find(cl => cl.id === clienteId);
      setClienteInfo(c || null);
    } else {
      setClienteInfo(null);
    }
  }, [clienteId, clientes]);

  // Consumidor final (F0h): ao TROCAR de destinatário, a nota recebe o valor do
  // cadastro (CPF/não contribuinte = sim; contribuinte = padrão ou vazio). O
  // destinatário que veio do rascunho/reemissão mantém a escolha gravada.
  const clienteDoConsumidorFinal = useRef(clienteId);
  useEffect(() => {
    if (!clienteInfo || clienteInfo.id === clienteDoConsumidorFinal.current) return;
    clienteDoConsumidorFinal.current = clienteInfo.id;
    setDadosNfe(d => ({ ...d, consumidor_final: valorInicialConsumidorFinal(clienteInfo, d.finalidade) }));
  }, [clienteInfo]);

  const camposFaltam = camposFaltantesDestinatario(clienteInfo);
  const destinatarioIncompleto = !!clienteInfo && camposFaltam.length > 0;

  const addItem = () => setItens([...itens, {
    codigo_produto: '', descricao: '', ncm: '', cest: '', cfop: '5102', unidade: 'UN',
    quantidade: 1, valor_unitario: 0, ean: '', valor_desconto: 0, valor_frete: 0,
    valor_seguro: 0, valor_outros: 0,
    origem: 0, cst_icms: icmsPadrao, aliquota_icms: 0, bc_icms_manual: 0,
    cst_ipi: '99', aliquota_ipi: 0,
    cst_pis: '99', aliquota_pis: 0,
    cst_cofins: '99', aliquota_cofins: 0,
    info_adicional: '', num_pedido: '', item_pedido: '',
    chave_ref_devolucao: '', item_ref_devolucao: '',
  }]);

  const selectProduto = (idx, prodId) => {
    const prod = produtos.find(p => p.id === prodId);
    if (!prod) return;
    const novo = [...itens];
    novo[idx] = {
      ...novo[idx],
      codigo_produto: prod.codigo, descricao: prod.descricao, ncm: prod.ncm,
      cfop: prod.cfop, unidade: prod.unidade, valor_unitario: prod.valor_unitario,
      cst_icms: String(prod.cst_icms || '').trim() || icmsPadrao,
      aliquota_icms: prod.aliquota_icms || 0,
      // S27b: o cadastro do produto passou a devolver CEST, EAN, origem e
      // PIS/COFINS/IPI, mas a emissão continua com os mesmos valores de antes
      // (que eram estes, porque o cadastro não os devolvia). Usar os do
      // produto mudaria as notas — produtos importados trazem o PIS/COFINS
      // do fornecedor — e fica para decisão do escritório.
      cest: '', ean: '', origem: 0,
      cst_pis: '99', aliquota_pis: 0,
      cst_cofins: '99', aliquota_cofins: 0,
      cst_ipi: '99', aliquota_ipi: 0,
      // F0c/F0d: ST, redução, diferimento, FCP e desoneração vêm do cadastro (o
      // backend confere). O CEST do produto só vem quando o código depende desses
      // dados (com ST ele é obrigatório); os demais itens seguem como antes.
      ...dadosFiscaisDoProduto(prod),
      valor_icms_desonerado: null,
      // F0f: empresa contribuinte do IPI usa o IPI do cadastro; as demais, como antes.
      ...(empresaAtual?.contribuinte_ipi ? {
        cst_ipi: prod.cst_ipi || '', aliquota_ipi: Number(prod.aliquota_ipi || 0),
        enquadramento_ipi: prod.enquadramento_ipi || '',
      } : {}),
    };
    const cstNovo = novo[idx].cst_icms;
    if (precisaDadosFiscais(regimeEmp, { ...novo[idx], cst_icms: cstNovo })) novo[idx].cest = prod.cest || '';
    setItens(novo);
  };

  const upd = (idx, field, value) => {
    const novo = [...itens];
    novo[idx][field] = value;
    setItens(novo);
  };

  const removeItem = (idx) => setItens(itens.filter((_, i) => i !== idx));

  const totalProdutos = itens.reduce((s, i) => s + (Number(i.quantidade) * Number(i.valor_unitario)), 0);
  const totalDesconto = itens.reduce((s, i) => s + Number(i.valor_desconto || 0), 0);
  const totalFrete = itens.reduce((s, i) => s + Number(i.valor_frete || 0), 0);
  const totalSeguro = itens.reduce((s, i) => s + Number(i.valor_seguro || 0), 0);
  const totalOutros = itens.reduce((s, i) => s + Number(i.valor_outros || 0), 0);
  // ST, FCP-ST e desoneração com dedução (F0d; regra W16-10): mesmo total do XML.
  const ajusteIcms = ajusteTotalNota(regimeEmp, itens, transp.tipo_frete, !!empresaAtual?.contribuinte_ipi);
  // CE3: o II informado entra no total da nota (W16-10).
  const totalII = itens.reduce((s, i) => s + Number(i.imposto_importacao?.vII || 0), 0);
  const totalNF = totalProdutos + totalFrete + totalSeguro + totalOutros - totalDesconto + ajusteIcms + totalII;

  // Atualizar valor do pagamento quando total muda.
  // Devolução (finalidade=4): SEFAZ exige tPag=90 + vPag=0, então NÃO espelhamos totalNF.
  useEffect(() => {
    if (dadosNfe.finalidade === 4) return;
    if (pagamentos.length === 1) {
      setPagamentos([{ ...pagamentos[0], valor: totalNF }]);
    }
  }, [totalNF, dadosNfe.finalidade]);

  // Forçar tPag=90 + vPag=0 em devolução (finNFe=4) — exigência SEFAZ (cStat 729/731).
  // Ao sair de devolução, reset para o default editável '01' / 0. Usa ref para
  // não disparar na montagem inicial (rascunho/reemissão preserva o que veio do DB).
  const _finalidadeAnterior = useRef(dadosNfe.finalidade);
  useEffect(() => {
    const prev = _finalidadeAnterior.current;
    const curr = dadosNfe.finalidade;
    if (prev === curr) return;
    _finalidadeAnterior.current = curr;
    if (curr === 4) {
      setPagamentos([{ forma_pagamento: '90', valor: 0 }]);
    } else if (prev === 4) {
      setPagamentos([{ forma_pagamento: '01', valor: 0 }]);
    }
  }, [dadosNfe.finalidade]);

  const updPag = (idx, field, value) => {
    const novo = [...pagamentos];
    novo[idx][field] = value;
    setPagamentos(novo);
  };

  // Converte transp (UI) → campos do body (DB/XML). Valores em '' viram null
  // implícito no backend (helper _aplicar_transp_volume).
  const _toNumOrUndefined = (v) => {
    if (v === '' || v == null) return undefined;
    const s = String(v).replace(/\./g, '').replace(',', '.').trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };
  const transpToBody = () => ({
    modalidade_frete: Number(transp.tipo_frete),
    transportador_cpf_cnpj: transp.cpf_cnpj || undefined,
    transportador_razao_social: transp.razao_social || undefined,
    transportador_ie: transp.ie || undefined,
    transportador_endereco: transp.endereco || undefined,
    transportador_municipio: transp.municipio || undefined,
    transportador_uf: transp.uf || undefined,
    transportador_antt: transp.antt || undefined,
    transportador_placa: transp.placa || undefined,
    transportador_placa_uf: transp.placa_uf || undefined,
    volume_quantidade: _toNumOrUndefined(transp.quantidade),
    volume_especie: transp.especie || undefined,
    volume_marca: transp.marca || undefined,
    volume_numeracao: transp.numeracao || undefined,
    peso_liquido: _toNumOrUndefined(transp.peso_liquido),
    peso_bruto: _toNumOrUndefined(transp.peso_bruto),
  });

  // Limpa + valida o array de chaves de NF-e referenciadas antes de enviar.
  // Retorna { ok: true, refs } com as chaves normalizadas (44 dígitos puros,
  // entradas vazias descartadas), ou { ok: false, erro } com a primeira
  // mensagem de erro encontrada. Mesma lógica usada para /emitir e /rascunho.
  const validarRefsParaEnvio = () => {
    const refsLimpas = dadosNfe.notas_referenciadas
      .map(c => limparChaveNfe(c))
      .filter(c => c.length > 0);
    for (let i = 0; i < refsLimpas.length; i++) {
      const r = validarChaveNfe(refsLimpas[i]);
      if (!r.valida) return { ok: false, erro: `Nota referenciada #${i + 1}: ${r.erro}` };
    }
    return { ok: true, refs: refsLimpas };
  };

  // Devolução (finNFe=4): valida a referência por ITEM antes de enviar — NT
  // 2025.002-RTC / VC02-14. Exige ao menos 1 item com chave (44 díg. + DV); o nº
  // do item de origem é opcional desde a v1.53 (se preenchido, tem de ser válido).
  // Itens em branco = não devolvidos (devolução parcial).
  const validarDevolucaoParaEnvio = () => {
    if (dadosNfe.finalidade !== 4) return { ok: true };
    let algum = false;
    for (let i = 0; i < itens.length; i++) {
      const it = itens[i];
      const chLimpa = limparChaveNfe(it.chave_ref_devolucao || '');
      const nItem = String(it.item_ref_devolucao ?? '').trim();
      if (!chLimpa && !nItem) continue; // item não devolvido (parcial)
      const v = validarChaveNfe(chLimpa);
      if (!v.valida) return { ok: false, erro: `Item ${i + 1}: chave da nota de origem inválida: ${v.erro}` };
      if (nItem && (!/^\d+$/.test(nItem) || Number(nItem) < 1)) return { ok: false, erro: `Item ${i + 1}: nº do item na nota de origem inválido.` };
      algum = true;
    }
    if (!algum) return { ok: false, erro: 'Devolução exige referência por item: preencha, em ao menos um item, a chave da nota de origem (44 dígitos) e, se souber, o nº do item correspondente.' };
    return { ok: true };
  };

  // ---- Devolução de VENDA própria ----
  // As posições 7-20 da chave referenciada são o CNPJ do emitente da nota de
  // origem. Se batem com o da própria empresa, a devolução é de uma VENDA
  // própria: pela regra contábil de 2026-09-13 vira Nota Fiscal de ENTRADA
  // simbólica (tipo_operacao=0), com a própria empresa no destinatário e CFOP
  // pela tabela de correspondência. O plano vem do backend; aqui ele é aplicado
  // ao formulário E exibido no painel de confirmação — nunca em silêncio.
  // Devolução de COMPRA (chave de fornecedor) não entra neste caminho.
  const cnpjEmpresa = (user?.empresa?.cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  const [planoDev, setPlanoDev] = useState(null);
  const [planoDevErro, setPlanoDevErro] = useState(null);

  // Ref para ler os itens dentro do efeito sem colocá-los na lista de
  // dependências (o efeito ALTERA o cfop dos itens — dependência causaria laço).
  const itensRef = useRef(itens);
  itensRef.current = itens;

  // Assinatura só das referências de venda própria: muda quando o operador
  // digita/apaga uma chave ou o nº do item, e não quando o CFOP é aplicado.
  const refsVendaPropria = (dadosNfe.finalidade === 4 && cnpjEmpresa.length === 14)
    ? itens.map(it => {
        const ch = limparChaveNfe(it.chave_ref_devolucao || '');
        const n = String(it.item_ref_devolucao ?? '').trim();
        return (ch.length === 44 && n && ch.slice(6, 20) === cnpjEmpresa) ? `${ch}:${n}` : '';
      }).join('|')
    : '';

  useEffect(() => {
    if (!refsVendaPropria.replace(/\|/g, '')) { setPlanoDev(null); setPlanoDevErro(null); return; }
    let cancelado = false;
    api.planoDevolucao(itensRef.current.map(it => ({
      chave_ref_devolucao: limparChaveNfe(it.chave_ref_devolucao || ''),
      item_ref_devolucao: String(it.item_ref_devolucao ?? '').trim(),
    })))
      .then(plano => {
        if (cancelado) return;
        setPlanoDev(plano);
        setPlanoDevErro(plano.bloqueio || null);
        if (!plano.aplicavel || plano.bloqueio) return;
        setDadosNfe(d => (d.tipo_operacao === 0 ? d : { ...d, tipo_operacao: 0 }));
        if (plano.destinatario?.id) setClienteId(plano.destinatario.id);
        setItens(prev => {
          let mudou = false;
          const novo = prev.map((it, idx) => {
            const p = (plano.itens || []).find(x => x.indice === idx + 1);
            if (!p || it.cfop === p.cfop_devolucao) return it;
            mudou = true;
            return { ...it, cfop: p.cfop_devolucao };
          });
          return mudou ? novo : prev;
        });
      })
      .catch(e => { if (!cancelado) { setPlanoDev(null); setPlanoDevErro(e.message); } });
    return () => { cancelado = true; };
  }, [refsVendaPropria]);

  const devolucaoVendaAtiva = !!planoDev?.aplicavel;

  // ---- Devolução: consumidor final copiado da nota original (F0h) ----
  // Com a original no sistema, a nota segue o indicador dela, sem perguntar; sem
  // ela (ou com originais divergentes), o passo "Dados Cliente" pergunta.
  const refsDevolucao = dadosNfe.finalidade === 4
    ? Array.from(new Set(itens.map(it => limparChaveNfe(it.chave_ref_devolucao || ''))
        .filter(ch => ch.length === 44))).join('|')
    : '';
  const [cfDevolucao, setCfDevolucao] = useState(null);
  useEffect(() => {
    if (!refsDevolucao) { setCfDevolucao(null); return; }
    let cancelado = false;
    api.consumidorFinalDevolucao(refsDevolucao.split('|').map(ch => ({ chave_ref_devolucao: ch })))
      .then(r => {
        if (cancelado) return;
        setCfDevolucao(r);
        if (typeof r.consumidor_final === 'boolean') {
          setDadosNfe(d => (d.consumidor_final === r.consumidor_final ? d : { ...d, consumidor_final: r.consumidor_final }));
        }
      })
      .catch(() => { if (!cancelado) setCfDevolucao({ chaves: 1, consumidor_final: null }); });
    return () => { cancelado = true; };
  }, [refsDevolucao]);
  const modoCf = modoConsumidorFinal(clienteInfo, dadosNfe.finalidade, cfDevolucao);

  // ---- Pendências para emitir ----
  const chaveConferencia = JSON.stringify([dadosNfe, clienteId, itens, pagamentos, transp]);
  const pendenciasAtuais = conferencia && conferencia.chave === chaveConferencia ? conferencia.lista : null;
  const temPendencias = !!pendenciasAtuais && pendenciasAtuais.length > 0;
  const gruposPendencias = agruparPendencias(conferencia?.lista);

  // Mesmo payload da emissão (o backend confere tudo; nada é gravado).
  // Devolve a lista, ou null se a conferência falhou (erro já na tela).
  const conferirPendencias = async ({ abrirSeHouver = true } = {}) => {
    const refsCheck = validarRefsParaEnvio();
    const chave = chaveConferencia;
    setConferindo(true);
    try {
      const r = await api.pendenciasEmissao({
        nota: { ...dadosNfe, notas_referenciadas: refsCheck.ok ? refsCheck.refs : dadosNfe.notas_referenciadas, ...transpToBody() },
        cliente_id: clienteId || undefined,
        itens,
        pagamentos,
        reemissao_de_id: reemissaoDeId || undefined,
        nota_id_existente: notaIdExistente || undefined,
      });
      const lista = r.pendencias || [];
      const alertas = r.alertas || [];
      ultimosAlertas.current = alertas;
      setConferencia({ chave, lista, alertas });
      if ((lista.length > 0 || alertas.length > 0) && abrirSeHouver) setModalPendencias(true);
      if (lista.length === 0 && alertas.length === 0) setModalPendencias(false);
      return lista;
    } catch (err) {
      setResultado({ sucesso: false, erro: err.message, codigo: err.codigo, camposFaltantes: err.camposFaltantes });
      return null;
    } finally {
      setConferindo(false);
    }
  };

  // Lista recebida no 400 do /emitir ou da prévia (mesmo serviço do backend).
  const mostrarPendenciasDoErro = (err) => {
    const lista = pendenciasDoErro(err);
    if (!lista) return false;
    setConferencia({ chave: chaveConferencia, lista });
    setModalPendencias(true);
    return true;
  };

  // "Corrigir": item da própria nota (passo Itens) ou o cadastro já existente
  // (produto / empresa), levando a nota junto para voltar sem perder nada.
  const corrigirPendencia = (p) => {
    const destino = destinoCorrecao(p, { isAdmin, empresaId: empresaAtual?.id || user?.empresa_id || null });
    if (!destino) return;
    setModalPendencias(false);
    if (destino.tipo === 'item') {
      setErroEtapa(null);
      setEtapaAtual(3);
      if (destino.aba) setAbaTributo(destino.aba);
      setItemDestacado(destino.indice);
      return;
    }
    if (destino.tipo === 'etapa') {
      setErroEtapa(null);
      setEtapaAtual(destino.etapa);
      return;
    }
    navigate(destino.pagina, {
      ...destino.payload,
      retornoEmissao: { dadosNfe, clienteId, itens, pagamentos, transp, reemissaoDeId, notaIdExistente, etapa: 6 },
    });
  };

  // Sugestão do backend (código padrão da empresa/regime, NCM do cadastro):
  // só troca no clique do operador; a lista fica desatualizada até conferir.
  const aplicarSugestao = (p) => {
    setItens(prev => prev.map((it, i) => (i === p.item - 1 ? { ...it, [p.campo]: p.sugestao } : it)));
  };

  // Item apontado pelo "Corrigir": rola até ele no passo Itens.
  useEffect(() => {
    if (etapaAtual !== 3 || itemDestacado === null) return;
    const el = document.getElementById(`item-emissao-${itemDestacado}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [etapaAtual, itemDestacado]);

  // Voltou de um cadastro: confere de novo na hora.
  useEffect(() => {
    if (retomada) conferirPendencias();
  }, []);

  const emitir = async ({ cienteAlertas = false } = {}) => {
    if (itens.length === 0) { setResultado({ sucesso: false, erro: 'Adicione pelo menos um item' }); return; }
    const refsCheck = validarRefsParaEnvio();
    if (!refsCheck.ok) { setResultado({ sucesso: false, erro: refsCheck.erro }); return; }
    const devCheck = validarDevolucaoParaEnvio();
    if (!devCheck.ok) { setResultado({ sucesso: false, erro: devCheck.erro }); return; }
    setEnviando(true); setResultado(null);
    // Só envia com a lista de pendências vazia (o backend recusa de novo se faltar algo).
    const lista = await conferirPendencias();
    if (lista === null || lista.length > 0) { setEnviando(false); return; }
    if (ultimosAlertas.current.length > 0 && !cienteAlertas && alertasCientes !== chaveConferencia) {
      setModalPendencias(true); setEnviando(false); return;
    }
    try {
      const payloadDadosNfe = { ...dadosNfe, notas_referenciadas: refsCheck.refs };
      const res = await api.emitirNFe({
        nota: { ...payloadDadosNfe, ...transpToBody() },
        cliente_id: clienteId || undefined,
        itens,
        pagamentos,
        reemissao_de_id: reemissaoDeId || undefined,
        nota_id_existente: notaIdExistente || undefined,
      });
      setResultado(res);
      if (res.sucesso) api.proximoNumero().then(setProximoNum).catch(() => {});
    } catch (err) {
      if (mostrarPendenciasDoErro(err)) { setEnviando(false); return; }
      // Bloqueios com prazo (656 gravado no banco; tentativa anterior sem resposta
      // conclusiva aguardando consulta): o botão mostra a contagem regressiva.
      if (err.status === 409 && ['BLOQUEIO_656', 'AGUARDANDO_CONFIRMACAO', 'CONFIRMACAO_PENDENTE'].includes(err.codigo)) {
        const ate = Date.parse(err.liberadoEm);
        setAgoraMs(Date.now());
        setBloqueadoAte(Number.isNaN(ate) ? Date.now() + 60 * 60 * 1000 : ate);
      } else if (err.codigo === '656') {
        // 656 devolvido pela SEFAZ: prazo gravado no banco (liberado_em);
        // sem ele, agora + 60 min.
        const ate = Date.parse(err.liberadoEm);
        setAgoraMs(Date.now());
        setBloqueadoAte(Number.isNaN(ate) ? Date.now() + 60 * 60 * 1000 : ate);
      }
      const dados = err.dados || {};
      setResultado({
        sucesso: false,
        erro: err.message,
        codigo: err.codigo,
        // Rejeição devolvida pela SEFAZ (cStat numérico + nota gravada).
        rejeicaoSefaz: /^\d+$/.test(String(err.codigo || '')) && !!dados.nota_id,
        camposFaltantes: err.camposFaltantes,
        acaoSugerida: err.acaoSugerida,
        diagnostico: dados.diagnostico || null,
        rejeicaoOriginal: dados.rejeicao_original || null,
        // Falha de comunicação com a SEFAZ: texto simples no lugar da exceção.
        mensagemAmigavel: dados.mensagem_amigavel || null,
        detalheTecnico: dados.detalhe_tecnico || dados.falha_comunicacao_anterior?.detalhe_tecnico || null,
        notaId: dados.nota_id || null,
      });
    }
    setEnviando(false);
  };

  const salvarRascunho = async () => {
    if (itens.length === 0) { setResultado({ sucesso: false, erro: 'Adicione pelo menos um item' }); return; }
    const refsCheck = validarRefsParaEnvio();
    if (!refsCheck.ok) { setResultado({ sucesso: false, erro: refsCheck.erro }); return; }
    try {
      const payloadDadosNfe = { ...dadosNfe, notas_referenciadas: refsCheck.refs };
      const res = await api.salvarRascunho({
        nota: { ...payloadDadosNfe, ...transpToBody() },
        cliente_id: clienteId || undefined,
        itens,
        pagamentos,
        nota_id_existente: notaIdExistente || undefined,
      });
      setResultado({ sucesso: true, erro: null, mensagem: notaIdExistente ? 'Rascunho salvo com sucesso (atualizado).' : 'Rascunho salvo com sucesso.' });
    } catch (err) { setResultado({ sucesso: false, erro: err.message }); }
  };

  const previewDanfe = async () => {
    if (itens.length === 0) { setResultado({ sucesso: false, erro: 'Adicione pelo menos um item' }); return; }
    try {
      const blob = await api.previewDanfe({
        nota: { ...dadosNfe, ...transpToBody() },
        cliente_id: clienteId || undefined,
        itens,
        pagamentos,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      if (mostrarPendenciasDoErro(err)) return;
      setResultado({
        sucesso: false,
        erro: err.message,
        codigo: err.codigo,
        acaoSugerida: err.acaoSugerida,
        diagnostico: err.dados?.diagnostico || null,
        camposFaltantes: err.dados?.campos_faltantes || undefined,
      });
    }
  };

  // Atalho da "Provável causa" para o cadastro a corrigir.
  const abrirCadastro = (cad) => {
    if (!cad) return;
    if (cad.tipo === 'cliente') navigate('clientes', { editarClienteId: cad.id });
    else if (cad.tipo === 'produto') navigate('produtos', { editarProdutoId: cad.id });
    else if (cad.tipo === 'empresa') navigate('minha_empresa');
  };

  // Download autenticado (JWT via fetch+Blob) da nota recém-autorizada — reusa as
  // mesmas funções api.* da NotasPage. NÃO usa window.open com URL crua (perderia o token).
  const baixarDoc = async (tipo) => {
    if (!resultado?.nota_id) return;
    try {
      const blob = tipo === 'danfe'
        ? await api.downloadDanfe(resultado.nota_id)
        : await api.downloadXml(resultado.nota_id);
      const url = URL.createObjectURL(blob);
      if (tipo === 'danfe') window.open(url, '_blank');
      else { const a = document.createElement('a'); a.href = url; a.download = `nfe_${tipo}.xml`; a.click(); }
    } catch (err) {
      alert('Erro ao baixar ' + (tipo === 'danfe' ? 'DANFE' : 'XML') + ': ' + err.message);
    }
  };

  // ---- Modal de cadastro rápido de cliente ----
  const abrirModalCliente = () => {
    setNovoCliente({ tipo_documento: 'J', indicador_ie: 9 });
    setErroModalCliente(null);
    setModalClienteAberto(true);
  };
  const fecharModalCliente = () => {
    setModalClienteAberto(false);
    setErroModalCliente(null);
  };

  const buscarCepNovoCliente = async () => {
    const cep = (novoCliente.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) { setErroModalCliente('CEP deve ter 8 dígitos.'); return; }
    setBuscandoCepNC(true);
    setErroModalCliente(null);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const dados = await resp.json();
      if (dados.erro) { setErroModalCliente('CEP não encontrado.'); return; }
      setNovoCliente(prev => ({
        ...prev,
        logradouro: prev.logradouro || dados.logradouro || '',
        bairro: prev.bairro || dados.bairro || '',
        municipio: prev.municipio || dados.localidade || '',
        uf: prev.uf || (dados.uf || '').toUpperCase(),
        codigo_municipio: prev.codigo_municipio || (dados.ibge || ''),
      }));
    } catch {
      setErroModalCliente('Falha ao consultar ViaCEP.');
    } finally {
      setBuscandoCepNC(false);
    }
  };

  const buscarCnpjNovoCliente = async () => {
    const cnpj = (novoCliente.cpf_cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (cnpj.length !== 14) { setErroModalCliente('CNPJ deve ter 14 dígitos.'); return; }
    setBuscandoCnpjNC(true);
    setErroModalCliente(null);
    try {
      const dados = await api.request('/empresas/consulta-cnpj/' + cnpj);
      setNovoCliente(prev => ({
        ...prev,
        razao_social: prev.razao_social || dados.razao_social || '',
        logradouro: prev.logradouro || dados.logradouro || '',
        numero: prev.numero || dados.numero || '',
        complemento: prev.complemento || dados.complemento || '',
        bairro: prev.bairro || dados.bairro || '',
        codigo_municipio: prev.codigo_municipio || dados.codigo_municipio || '',
        municipio: prev.municipio || dados.municipio || '',
        uf: prev.uf || dados.uf || '',
        cep: prev.cep || (dados.cep || '').replace(/\D/g, ''),
        telefone: prev.telefone || dados.telefone || '',
        email: prev.email || dados.email || '',
      }));
    } catch (err) {
      setErroModalCliente(err.message || 'Falha ao consultar CNPJ.');
    } finally {
      setBuscandoCnpjNC(false);
    }
  };

  const validarNovoCliente = () => {
    const ehPJ = (novoCliente.tipo_documento || 'J') === 'J';
    const docDigits = (novoCliente.cpf_cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (ehPJ && docDigits.length !== 14) return 'Informe um CNPJ válido (14 dígitos).';
    if (!ehPJ && docDigits.length !== 11) return 'Informe um CPF válido (11 dígitos).';
    if (!String(novoCliente.razao_social || '').trim()) return ehPJ ? 'Informe a Razão Social.' : 'Informe o Nome.';
    if (!String(novoCliente.municipio || '').trim()) return 'Informe o Município.';
    const cmun = (novoCliente.codigo_municipio || '').replace(/\D/g, '');
    if (cmun.length !== 7) return 'Código IBGE do Município deve ter 7 dígitos (use "Buscar CEP" para preencher).';
    if (Number(novoCliente.indicador_ie) === 1 && !String(novoCliente.inscricao_estadual || '').trim()) {
      return 'Para Contribuinte ICMS é obrigatório informar a Inscrição Estadual.';
    }
    return null;
  };

  const salvarNovoCliente = async () => {
    const erro = validarNovoCliente() || excessoLayoutNfe(novoCliente, CAMPOS_LIMITE_CLIENTE);
    if (erro) { setErroModalCliente(erro); return; }
    setSalvandoCliente(true);
    setErroModalCliente(null);
    try {
      // Monta payload — só dígitos no documento, sem empresa_id (backend força via JWT).
      const payload = {
        tipo_documento: novoCliente.tipo_documento || 'J',
        cpf_cnpj: (novoCliente.cpf_cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase(),
        razao_social: novoCliente.razao_social.trim(),
        indicador_ie: Number(novoCliente.indicador_ie ?? 9),
      };
      const opcionais = ['inscricao_estadual', 'cep', 'logradouro', 'numero', 'complemento',
        'bairro', 'codigo_municipio', 'municipio', 'uf', 'email', 'telefone'];
      for (const k of opcionais) {
        // O campo IE só aparece para Contribuinte (1): IE escondida não é enviada
        // (evita IE com indicador ≠ 1 → rejeição 232).
        if (k === 'inscricao_estadual' && payload.indicador_ie !== 1) continue;
        const v = novoCliente[k];
        if (v != null && String(v).trim() !== '') {
          payload[k] = (k === 'cep' || k === 'codigo_municipio') ? String(v).replace(/\D/g, '') : v;
        }
      }
      const novo = await api.criarCliente(payload);
      setClientes(prev => [novo, ...prev]);
      setClienteId(novo.id);
      setModalClienteAberto(false);
      setNovoCliente({ tipo_documento: 'J', indicador_ie: 9 });
    } catch (err) {
      if (err.camposFaltantes?.length > 0) {
        setErroModalCliente(`Cadastro incompleto: ${err.camposFaltantes.join('; ')}`);
      } else {
        setErroModalCliente(err.message || 'Falha ao cadastrar cliente.');
      }
    } finally {
      setSalvandoCliente(false);
    }
  };

  const inp = { padding: '7px 10px', border: '1px solid #d0d5dd', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const sel = { ...inp, background: '#fff' };

  // Validação por etapa do wizard. Bloqueia "Avançar" se faltar obrigatório.
  // O submit final continua passando pela validação completa em `emitir`.
  const validarEtapa = (n) => {
    if (n === 1) {
      if (!dadosNfe.natureza_operacao?.toString().trim()) return 'Informe a Natureza da Operação.';
      if (dadosNfe.tipo_operacao == null) return 'Selecione a Operação (Saída ou Entrada).';
      if (dadosNfe.finalidade == null) return 'Selecione a Finalidade.';
    }
    if (n === 2 && !clienteId) return 'Selecione o destinatário.';
    if (n === 2) {
      const erroCf = erroConsumidorFinal(clienteInfo, dadosNfe.consumidor_final, modoCf);
      if (erroCf) return erroCf;
    }
    if (n === 3) {
      if (itens.length === 0) return 'Adicione pelo menos um item à nota.';
      for (let i = 0; i < itens.length; i++) {
        const it = itens[i]; const k = i + 1;
        if (!String(it.codigo_produto || '').trim()) return `Item ${k}: informe o Código.`;
        if (!String(it.descricao || '').trim())       return `Item ${k}: informe a Descrição.`;
        if (!String(it.ncm || '').trim())             return `Item ${k}: informe o NCM.`;
        if (!String(it.cfop || '').trim())            return `Item ${k}: informe o CFOP.`;
        if (!String(it.unidade || '').trim())         return `Item ${k}: informe a Unidade.`;
        if (!Number(it.quantidade))                   return `Item ${k}: informe a Quantidade.`;
        if (!Number(it.valor_unitario))               return `Item ${k}: informe o Valor Unitário.`;
        if (it.origem === null || it.origem === undefined || it.origem === '') return `Item ${k}: selecione a Origem.`;
        if (!it.cst_icms)                             return `Item ${k}: selecione o ${rotuloIcmsItem}.`;
        if (regimeEmp !== null && !codigoIcmsAceito(regimeEmp, it.cst_icms))
          return `Item ${k}: o código ${String(it.cst_icms).trim()} não é aceito para esta empresa. Escolha um ${rotuloIcmsItem} da lista; na dúvida, fale com o escritório.`;
      }
    }
    if (n === 4 && !transp.tipo_frete) return 'Selecione o Tipo de Frete.';
    if (n === 5) {
      if (!(dadosNfe.ind_pag || '0')) return 'Selecione a Forma de Pagamento.';
      if (dadosNfe.finalidade === 4) {
        const p = pagamentos[0];
        if (!p || p.forma_pagamento !== '90' || parseFloat(p.valor) !== 0 || pagamentos.length !== 1) {
          return "Em NF-e de devolução, a forma de pagamento deve ser '90 - Sem Pagamento' com valor zero (corrija na etapa Cobrança).";
        }
      }
    }
    return null;
  };
  const avancar = () => {
    const erro = validarEtapa(etapaAtual);
    if (erro) { setErroEtapa(erro); return; }
    setErroEtapa(null);
    setEtapaAtual(e => Math.min(6, e + 1));
    // Último passo: o sistema já confere o que falta para emitir.
    if (etapaAtual === 5) conferirPendencias();
  };
  const voltar = () => { setErroEtapa(null); setEtapaAtual(e => Math.max(1, e - 1)); };

  const FORMAS_PAG = [
    { v: '01', l: 'Dinheiro' }, { v: '02', l: 'Cheque' }, { v: '03', l: 'Cartão Crédito' },
    { v: '04', l: 'Cartão Débito' }, { v: '05', l: 'Crédito Loja' }, { v: '10', l: 'Vale Alimentação' },
    { v: '11', l: 'Vale Refeição' }, { v: '12', l: 'Vale Presente' }, { v: '13', l: 'Vale Combustível' },
    { v: '15', l: 'Boleto' }, { v: '16', l: 'Depósito Bancário' }, { v: '17', l: 'PIX' },
    { v: '18', l: 'Transferência Bancária' }, { v: '19', l: 'Programa Fidelidade' },
    { v: '90', l: 'Sem Pagamento' }, { v: '99', l: 'Outros' },
  ];

  const CST_IPI_SAIDA = ['50-Saída tributada','51-Saída tributada alíquota zero','52-Saída isenta','53-Saída não-tributada','54-Saída imune','55-Saída com suspensão','99-Outras saídas'];
  const CST_PIS_COFINS = [
    '01-Oper. tributável BC normal','02-Oper. tributável alíq. diferenciada','04-Oper. tributável monofásica zero',
    '06-Oper. tributável alíquota zero','07-Oper. isenta','08-Oper. sem incidência','09-Oper. com suspensão','49-Outras operações de saída','99-Outras operações',
  ];
  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '12px' }}>
        {notaIdExistente ? 'Editar Rascunho de NF-e' : 'Emitir NF-e'}
      </h2>

      {/* Banner de edição de rascunho */}
      {notaIdExistente && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
          display: 'flex', gap: '12px', alignItems: 'flex-start',
          color: '#1e40af', fontSize: '13px', lineHeight: 1.5,
        }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>📝</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: '2px' }}>Editando rascunho</div>
            <div>
              Você está editando um rascunho existente. Ao salvar ou emitir, o mesmo registro será atualizado — não será criada uma nova nota duplicada.
            </div>
          </div>
        </div>
      )}

      {/* Banner de reemissão */}
      {reemissao && bannerVisivel && (
        <div style={{
          background: '#FEF3C7', border: '1px solid #FCD34D',
          borderRadius: '8px', padding: '14px 18px', marginBottom: '16px',
          display: 'flex', gap: '12px', alignItems: 'flex-start',
          color: '#92400E',
        }}>
          <span style={{ fontSize: '22px', lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1, fontSize: '13px', lineHeight: 1.55 }}>
            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>AVISO DE REEMISSÃO</div>
            <div>
              Esta nota é uma reemissão da NF-e nº <b>{String(reemissao.numero_original).padStart(9,'0')}</b>{' '}
              (Série {reemissao.serie_original}), rejeitada anteriormente.
            </div>
            <div style={{ marginTop: '6px' }}>
              <b>Motivo da rejeição:</b>{' '}
              {reemissao.falha_comunicacao
                ? <>Falha de comunicação com a SEFAZ. {reemissao.falha_comunicacao.mensagem_amigavel}</>
                : <>{reemissao.codigo_rejeicao ? `[${reemissao.codigo_rejeicao}] ` : ''}{reemissao.mensagem_rejeicao || '—'}</>}
            </div>
            <DetalheTecnico texto={reemissao.falha_comunicacao?.detalhe_tecnico} />
            <CausaProvavel diag={reemissao.diagnostico} onAbrir={abrirCadastro} />
            {reemissao.rejeicao_info?.acao_sugerida && (
              <div style={{ marginTop: '4px' }}>
                <b>Ação sugerida:</b> {reemissao.rejeicao_info.acao_sugerida}
              </div>
            )}
            <RelatarErroBotao
              notaId={reemissao.reemissao_de_id}
              mensagemExibida={reemissao.falha_comunicacao?.mensagem_amigavel
                || `${reemissao.codigo_rejeicao ? `[${reemissao.codigo_rejeicao}] ` : ''}${reemissao.mensagem_rejeicao || ''}`}
              detalheTecnico={reemissao.mensagem_rejeicao || ''}
              tela="Reemissão de NF-e" />
            <div style={{
              marginTop: '8px', fontWeight: 700,
              background: '#FDE68A', borderRadius: '6px', padding: '6px 10px',
            }}>
              {reemissao.reaproveita_numero === false
                ? 'Novo número será gerado (rejeição por duplicidade).'
                : `Esta reemissão reutilizará o nº ${String(reemissao.numero_original).padStart(9,'0')} (série ${reemissao.serie_original}).`}
            </div>
            <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
              Regularize a pendência antes de emitir novamente.
            </div>
          </div>
          <button onClick={() => setBannerVisivel(false)} title="Fechar"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                     fontSize: '18px', color: '#92400E', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Devolução de venda própria — resumo do que será emitido, para conferência */}
      {planoDevErro && (
        <Alerta tipo="erro">
          <b>⛔ Devolução de venda própria — confirmação manual necessária.</b>
          <div style={{ marginTop: '6px' }}>{planoDevErro}</div>
          <div style={{ marginTop: '6px' }}>
            A emissão fica bloqueada até que o CFOP de devolução seja definido com a
            contabilidade. Nada foi enviado à SEFAZ.
          </div>
        </Alerta>
      )}
      {devolucaoVendaAtiva && !planoDevErro && (
        <Alerta tipo="aviso">
          <b>⚠️ Devolução de venda própria detectada.</b> A nota de origem foi emitida
          por esta empresa, então esta NF-e sai como <b>Nota Fiscal de Entrada
          simbólica</b> (cliente não contribuinte). Confira antes de emitir:
          <ul style={{ margin: '8px 0 4px 18px', padding: 0 }}>
            <li>Tipo de operação: <b>0 — Entrada</b> (fixo neste caso)</li>
            <li>Destinatário: <b>{planoDev?.destinatario?.razao_social}</b> ({fmtCnpj(planoDev?.destinatario?.cpf_cnpj || '')})</li>
            {(planoDev?.itens || []).map(p => (
              <li key={p.indice}>
                Item {p.indice}: CFOP <b>{p.cfop_origem}</b> (venda original) →{' '}
                <b>{p.cfop_devolucao}</b> (devolução)
              </li>
            ))}
          </ul>
          {planoDev?.destinatario?.criado_agora && (
            <div style={{ marginTop: '6px' }}>
              A sua empresa foi cadastrada automaticamente como cliente de si mesma
              para servir de destinatário desta nota de entrada. O registro fica
              visível em <b>Clientes</b> e pode ser editado normalmente.
            </div>
          )}
          <div style={{ marginTop: '6px' }}>
            Se a intenção era devolver uma <b>compra a fornecedor</b>, confira a chave
            da nota de origem — ela deveria ser do fornecedor, não desta empresa.
          </div>
        </Alerta>
      )}

      {/* Próximo número */}
      {proximoNum && <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', gap: '24px', fontSize: '14px' }}>
        <span><b>Próximo Nº:</b> {String(proximoNum.proximo_numero).padStart(9, '0')}</span>
        <span><b>Série:</b> {proximoNum.serie}</span>
        <span><b>Modelo:</b> 55</span>
      </div>}

      {/* ---- Indicador de progresso do wizard + Visualizar Prévia + Salvar Rascunho persistente ---- */}
      <div style={{ background: '#5b7aa2', color: '#fff', padding: '10px 16px', borderRadius: '8px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: 600 }}>
        <span>Passo {etapaAtual} de 6 — {NOMES_ETAPAS[etapaAtual - 1]}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" variant="outline" onClick={previewDanfe}
               disabled={itens.length === 0}
               title={itens.length === 0
                 ? 'Adicione ao menos um item para visualizar a prévia.'
                 : 'Abre o PDF prévio da DANFE (sem valor fiscal)'}
               style={{ background: '#fff' }}>
            Visualizar Prévia
          </Btn>
          <Btn size="sm" variant="outline" onClick={salvarRascunho} disabled={itens.length === 0}
               style={{ background: '#fff' }}>
            Salvar Rascunho
          </Btn>
        </div>
      </div>

      {erroEtapa && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#92400E', fontSize: 13 }}>
          ⚠ {erroEtapa}
        </div>
      )}

      {/* ============ ETAPA 1: DADOS NF-e ============ */}
      {etapaAtual === 1 && (
      <>
      <Section title="Dados NF-e">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
          <F label="Modelo"><input style={inp} value="55" disabled /></F>
          <F label="Série"><input style={inp} value={proximoNum?.serie || ''} disabled /></F>
          <F label="Número"><input style={inp} value={proximoNum ? String(proximoNum.proximo_numero).padStart(9,'0') : ''} disabled /></F>
          <F label="Data Emissão"><input style={inp} type="date" value={new Date().toISOString().split('T')[0]} disabled /></F>
          <F label="Hora Emissão"><input style={inp} value={new Date().toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})} disabled /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="Natureza Operação" required><input style={inp} maxLength={60} value={dadosNfe.natureza_operacao} onChange={e => setDadosNfe({...dadosNfe, natureza_operacao: e.target.value})} /><ContadorCaracteres valor={dadosNfe.natureza_operacao} max={60} /></F>
          <F label="Operação" required>
            {/* Devolução de venda própria é sempre Entrada — campo travado de
                propósito, com a explicação logo abaixo (e no painel do topo). */}
            <select style={sel} value={dadosNfe.tipo_operacao} disabled={devolucaoVendaAtiva}
                    onChange={e => setDadosNfe({...dadosNfe, tipo_operacao: Number(e.target.value)})}>
              <option value={1}>Saída</option><option value={0}>Entrada</option>
            </select>
            {devolucaoVendaAtiva && (
              <div style={{ fontSize: '11px', color: '#92400e', marginTop: '2px' }}>
                Fixo em Entrada: devolução de venda própria.
              </div>
            )}
          </F>
          <F label="Finalidade" required>
            <select style={sel} value={dadosNfe.finalidade} onChange={e => {
              // Devolução (F0h): o consumidor final vem da nota original, não do padrão do cliente.
              const fin = Number(e.target.value);
              setDadosNfe({ ...dadosNfe, finalidade: fin,
                consumidor_final: clienteInfo ? valorInicialConsumidorFinal(clienteInfo, fin) : dadosNfe.consumidor_final });
            }}>
              <option value={1}>Normal</option><option value={2}>Complementar</option><option value={3}>Ajuste</option><option value={4}>Devolução</option>
            </select>
          </F>
          <F label="Forma Emissão">
            <select style={sel} value={dadosNfe.forma_emissao} onChange={e => setDadosNfe({...dadosNfe, forma_emissao: Number(e.target.value)})}>
              <option value={1}>Normal</option><option value={2}>Contingência FS-IA</option><option value={5}>Contingência SVC-AN</option>
            </select>
          </F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="Tipo Atendimento">
            <select style={sel} value={dadosNfe.tipo_atendimento} onChange={e => setDadosNfe({...dadosNfe, tipo_atendimento: Number(e.target.value)})}>
              <option value={0}>0- Não se aplica</option><option value={1}>1- Presencial</option><option value={2}>2- Internet</option><option value={3}>3- Teleatendimento</option><option value={4}>4- Entrega domicílio</option><option value={9}>9- Outros</option>
            </select>
          </F>
          <F label="Intermediador">
            <select style={sel} value={dadosNfe.intermediador} onChange={e => setDadosNfe({...dadosNfe, intermediador: Number(e.target.value)})}>
              <option value={0}>0- Sem intermediador</option><option value={1}>1- Operação em site terceiro</option>
            </select>
          </F>
        </div>
      </Section>
      </>
      )}

      {/* ============ ETAPA 2: DADOS CLIENTE ============ */}
      {etapaAtual === 2 && (
      <Section title="Dados Cliente">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '10px' }}>
          <F label="Destinatário" required>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                {/* Combobox de busca inline — input SEMPRE montado (irmão fixo);
                    só a lista abaixo é condicional. NÃO transformar em subcomponente
                    (o input remontaria a cada tecla e perderia o foco). */}
                <input
                  type="text"
                  placeholder="Buscar cliente por nome ou CPF/CNPJ..."
                  value={clienteComboAberto
                    ? clienteBusca
                    : (clienteInfo
                        ? `${clienteInfo.razao_social} (${clienteInfo.cpf_cnpj?.length === 14 ? fmtCnpj(clienteInfo.cpf_cnpj) : clienteInfo.cpf_cnpj})`
                        : '')}
                  onFocus={() => { setClienteComboAberto(true); setClienteBusca(''); }}
                  onChange={e => { setClienteComboAberto(true); setClienteBusca(e.target.value); }}
                  onBlur={() => setClienteComboAberto(false)}
                  style={{ ...sel, width: '100%' }}
                />
                {clienteComboAberto && (() => {
                  const norm = s => (s == null ? '' : s.toString()).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                  const q = norm(clienteBusca);
                  const qDigitos = clienteBusca.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
                  const lista = clientes
                    .slice()
                    .sort((a, b) => (a.razao_social || '').localeCompare(b.razao_social || ''))
                    .filter(c => {
                      if (!q) return true;
                      const nomeOk = norm(c.razao_social).includes(q);
                      const docOk = qDigitos.length > 0 && (c.cpf_cnpj || '').includes(qDigitos);
                      return nomeOk || docOk;
                    });
                  return (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px', zIndex: 50, background: '#fff', color: '#1a202c', border: '1px solid #cbd5e0', borderRadius: '4px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      {lista.length === 0 && (
                        <div style={{ padding: '6px 10px', fontSize: '12px', color: '#718096' }}>Nenhum cliente encontrado</div>
                      )}
                      {lista.map(c => {
                        const incompleto = camposFaltantesDestinatario(c).length > 0;
                        const docFmt = c.cpf_cnpj?.length === 14 ? fmtCnpj(c.cpf_cnpj) : c.cpf_cnpj;
                        return (
                          <div
                            key={c.id}
                            onMouseDown={() => { setClienteId(c.id); setClienteComboAberto(false); setClienteBusca(''); }}
                            style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #edf2f7', whiteSpace: 'nowrap' }}
                          >
                            {incompleto ? '⚠️ ' : ''}{c.razao_social} ({docFmt})
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <Btn size="sm" variant="outline" onClick={abrirModalCliente} title="Cadastrar novo cliente">
                + Novo Cliente
              </Btn>
            </div>
          </F>
        </div>
        {clienteInfo && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', background: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
            <F label="CPF/CNPJ"><input style={inp} value={clienteInfo.cpf_cnpj?.length === 14 ? fmtCnpj(clienteInfo.cpf_cnpj) : clienteInfo.cpf_cnpj} disabled /></F>
            <F label="Inscrição Estadual"><input style={inp} value={clienteInfo.inscricao_estadual || ''} disabled /></F>
            <F label="E-mail" span={2}><input style={inp} value={clienteInfo.email || ''} disabled /></F>
            <F label="Logradouro" span={2}><input style={inp} value={clienteInfo.logradouro || ''} disabled /></F>
            <F label="Nº"><input style={inp} value={clienteInfo.numero || ''} disabled /></F>
            <F label="Bairro"><input style={inp} value={clienteInfo.bairro || ''} disabled /></F>
            <F label="Município"><input style={inp} value={clienteInfo.municipio || ''} disabled /></F>
            <F label="UF"><input style={inp} value={clienteInfo.uf || ''} disabled /></F>
            <F label="CEP"><input style={inp} value={clienteInfo.cep || ''} disabled /></F>
            <F label="Telefone"><input style={inp} value={clienteInfo.telefone || ''} disabled /></F>
          </div>
        )}
        {clienteInfo && modoCf === 'copiado' && (
          <div id="consumidor-final" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Devolução: consumidor final {dadosNfe.consumidor_final ? 'sim' : 'não'}, igual ao da nota original.
          </div>
        )}
        {clienteInfo && modoCf === 'aguardando' && (
          <div id="consumidor-final" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Devolução: o indicador de consumidor final vem da nota original (informe a chave nos itens).
            Se ela não estiver no sistema, a pergunta aparece aqui.
          </div>
        )}
        {clienteInfo && modoCf === 'perguntar' && (
          <div id="consumidor-final" style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '6px',
            background: typeof dadosNfe.consumidor_final === 'boolean' ? '#f8f9fa' : '#FEF3C7',
            border: typeof dadosNfe.consumidor_final === 'boolean' ? '1px solid #e2e8f0' : '1px solid #F59E0B' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
              {dadosNfe.finalidade === 4
                ? 'Devolução: a nota original não está no sistema. A operação original foi para consumidor final?'
                : 'Destinatário contribuinte do ICMS: esta compra é para consumidor final?'}
            </div>
            <label style={{ fontSize: '13px', marginRight: '18px', cursor: 'pointer' }}>
              <input type="radio" name="consumidor_final" checked={dadosNfe.consumidor_final === true}
                     onChange={() => setDadosNfe({ ...dadosNfe, consumidor_final: true })} />{' '}
              Sim — uso e consumo ou ativo
            </label>
            <label style={{ fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="consumidor_final" checked={dadosNfe.consumidor_final === false}
                     onChange={() => setDadosNfe({ ...dadosNfe, consumidor_final: false })} />{' '}
              Não — revenda ou industrialização
            </label>
            {typeof dadosNfe.consumidor_final !== 'boolean' && (
              <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                Escolha uma opção. Para não perguntar sempre, defina o padrão no cadastro do cliente.
              </div>
            )}
          </div>
        )}
        {clienteInfo && consumidorFinalObrigatorio(clienteInfo) && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Consumidor final: sim (destinatário com CPF ou não contribuinte do ICMS).
          </div>
        )}
      </Section>
      )}

      {/* ============ ETAPA 3: ITENS DA NOTA ============ */}
      {etapaAtual === 3 && (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h3 style={{ fontSize: '15px', margin: 0 }}>Itens da Nota ({itens.length})</h3>
        <Btn size="sm" onClick={addItem}>+ Adicionar Item</Btn>
      </div>

      {itens.map((item, idx) => (
        <div key={idx} id={`item-emissao-${idx}`} style={{ border: itemDestacado === idx ? '2px solid #F59E0B' : '1px solid #d0d5dd', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
          {/* Header do item */}
          <div style={{ background: '#5b7aa2', color: '#fff', padding: '6px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
            <span>Item {idx + 1} {item.descricao && `— ${item.descricao}`}</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {produtos.length > 0 && (
                <div style={{ position: 'relative' }}>
                  {/* Combobox de busca inline — input SEMPRE montado (irmão fixo);
                      só a lista abaixo é condicional. NÃO transformar em subcomponente. */}
                  <input
                    type="text"
                    placeholder="Buscar produto (código ou descrição)..."
                    value={comboAbertoIdx === idx ? comboBusca : ''}
                    onFocus={() => { setComboAbertoIdx(idx); setComboBusca(''); }}
                    onChange={e => { setComboAbertoIdx(idx); setComboBusca(e.target.value); }}
                    onBlur={() => setComboAbertoIdx(null)}
                    style={{ padding: '3px 6px', fontSize: '11px', borderRadius: '4px', border: 'none', width: '240px' }}
                  />
                  {comboAbertoIdx === idx && comboBusca && (() => {
                    const q = comboBusca.toLowerCase();
                    const lista = produtos
                      .slice()
                      .sort((a, b) => (a.descricao || '').localeCompare(b.descricao || ''))
                      .filter(p => (p.codigo || '').toLowerCase().includes(q) || (p.descricao || '').toLowerCase().includes(q))
                      .slice(0, 50);
                    return (
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '2px', zIndex: 50, background: '#fff', color: '#1a202c', border: '1px solid #cbd5e0', borderRadius: '4px', minWidth: '340px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                        {lista.length === 0 && (
                          <div style={{ padding: '6px 10px', fontSize: '12px', color: '#718096' }}>Nenhum produto encontrado</div>
                        )}
                        {lista.map(p => (
                          <div
                            key={p.id}
                            onMouseDown={() => { selectProduto(idx, p.id); setComboAbertoIdx(null); setComboBusca(''); }}
                            style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #edf2f7', whiteSpace: 'nowrap' }}
                          >
                            <b>{p.codigo}</b> — {p.descricao}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
              <button onClick={() => removeItem(idx)} style={{ background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', cursor: 'pointer', fontSize: '12px' }}>Remover</button>
            </div>
          </div>

          <div style={{ padding: '12px' }}>
            {/* Dados do produto */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 100px 80px 70px 80px 100px 100px', gap: '8px', marginBottom: '10px' }}>
              <F label="Código" required><input style={inp} value={item.codigo_produto} onChange={e => upd(idx,'codigo_produto',e.target.value)} /></F>
              <F label="Descrição" required><input style={inp} maxLength={120} value={item.descricao} onChange={e => upd(idx,'descricao',e.target.value)} /><ContadorCaracteres valor={item.descricao} max={120} /></F>
              <F label="NCM" required><input style={inp} value={item.ncm} onChange={e => upd(idx,'ncm',e.target.value)} maxLength={8} /></F>
              <F label="CEST"><input style={inp} value={item.cest} onChange={e => upd(idx,'cest',e.target.value)} maxLength={7} /></F>
              <F label="CFOP" required><input style={inp} value={item.cfop} onChange={e => upd(idx,'cfop',e.target.value)} maxLength={4} /></F>
              <F label="Unidade" required><input style={inp} value={item.unidade} onChange={e => upd(idx,'unidade',e.target.value.toUpperCase())} maxLength={6} /></F>
              <F label="Qtd" required><MoneyInput style={inp} value={item.quantidade} onChange={v => upd(idx,'quantidade',v)} /></F>
              <F label="V.Unitário" required><MoneyInput style={inp} value={item.valor_unitario} onChange={v => upd(idx,'valor_unitario',v)} /></F>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '10px' }}>
              <F label="EAN/GTIN"><input style={inp} value={item.ean || ''} onChange={e => upd(idx,'ean',e.target.value)} /></F>
              <F label="Desconto R$"><MoneyInput style={inp} value={item.valor_desconto} onChange={v => upd(idx,'valor_desconto',v)} /></F>
              <F label="Frete R$"><MoneyInput style={inp} value={item.valor_frete} onChange={v => upd(idx,'valor_frete',v)} /></F>
              <F label="Seguro R$"><MoneyInput style={inp} value={item.valor_seguro} onChange={v => upd(idx,'valor_seguro',v)} /></F>
              <F label="Outros R$"><MoneyInput style={inp} value={item.valor_outros} onChange={v => upd(idx,'valor_outros',v)} /></F>
            </div>

            {/* Tributos com abas */}
            <div style={{ border: '1px solid #d0d5dd', borderRadius: '6px' }}>
              <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #d0d5dd' }}>
                {['icms','ipi','pis','cofins'].map(t => (
                  <button key={t} onClick={() => setAbaTributo(t)} style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 600, border: 'none', borderBottom: abaTributo===t ? '2px solid #5b7aa2' : '2px solid transparent', background: abaTributo===t ? '#f0f4f8' : 'transparent', cursor: 'pointer', textTransform: 'uppercase', color: abaTributo===t ? '#5b7aa2' : '#888' }}>{t}</button>
                ))}
              </div>
              <div style={{ padding: '12px' }}>
                {abaTributo === 'icms' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    <F label="Origem" required>
                      <select style={sel} value={item.origem} onChange={e => upd(idx,'origem',Number(e.target.value))}>
                        {ORIGEM_OPTS.map(o => (
                          <option key={o.codigo} value={o.codigo}>{o.codigo} - {o.descricao}</option>
                        ))}
                      </select>
                    </F>
                    <F label={rotuloIcmsItem} required>
                      <select style={sel} value={item.cst_icms} onChange={e => upd(idx,'cst_icms',e.target.value)}>
                        <option value="">— Selecione —</option>
                        {opcoesIcmsSelect(regimeEmp, item.cst_icms)}
                      </select>
                    </F>
                    <F label="Alíquota ICMS %"><MoneyInput style={inp} value={item.aliquota_icms} onChange={v => upd(idx,'aliquota_icms',v)} /></F>
                    <F label="BC ICMS R$ (manual)"><MoneyInput style={inp} value={item.bc_icms_manual || 0} onChange={v => upd(idx,'bc_icms_manual',v)} /></F>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <CamposIcmsSt regime={regimeEmp} valores={item} editavel={false} onChange={() => {}}
                        valorDesonerado={
                          <F label="Valor do ICMS desonerado R$" required>
                            <MoneyInput style={inp} value={item.valor_icms_desonerado || 0} onChange={v => upd(idx,'valor_icms_desonerado',v)} />
                          </F>
                        } />
                      {precisaDadosFiscais(regimeEmp, item) && (() => {
                        const c = calcularIcmsItem(regimeEmp, item, transp.tipo_frete);
                        return (
                          <div style={{ fontSize: '12px', color: '#555' }}>
                            Prévia do cálculo: base do ICMS {fmtValor(c.vbc)} · ICMS {fmtValor(c.vicms)}
                            {c.vfcp > 0 && <> · FCP {fmtValor(c.vfcp)}</>}
                            {c.vbcst > 0 && <> · base da ST {fmtValor(c.vbcst)} · ICMS-ST {fmtValor(c.vst)}</>}
                            {c.vfcpst > 0 && <> · FCP-ST {fmtValor(c.vfcpst)}</>}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {abaTributo === 'icms' && String(clienteInfo?.uf || '').toUpperCase() === 'EX' && Number(dadosNfe.tipo_operacao) === 0 && (
                  <ImportacaoItem item={item} upd={upd} idx={idx} inp={inp} F={F} MoneyInput={MoneyInput} />
                )}
                {abaTributo === 'icms' && ehExportacao(clienteInfo, dadosNfe) && (
                  <ExportacaoItem item={item} upd={upd} idx={idx} inp={inp} F={F} />
                )}
                {abaTributo === 'ipi' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <F label="Situação Tributária" required>
                      <select style={sel} value={item.cst_ipi} onChange={e => upd(idx,'cst_ipi',e.target.value)}>
                        <option value="">Selecione</option>
                        {CST_IPI_SAIDA.map(o => { const [v,l]=o.split('-'); return <option key={v} value={v}>{v} - {l}</option>; })}
                      </select>
                    </F>
                    <F label="Alíquota IPI %"><MoneyInput style={inp} value={item.aliquota_ipi} onChange={v => upd(idx,'aliquota_ipi',v)} /></F>
                    {empresaAtual?.contribuinte_ipi && (
                      <F label="Enquadramento (cEnq)"><input style={{ ...inp, background: '#f5f5f5' }} value={item.enquadramento_ipi || ''} disabled title="Do cadastro do produto (escritório)" /></F>
                    )}
                    {dadosNfe.finalidade === 4 && (
                      <>
                        <F label="Devolução: % devolvido"><MoneyInput style={inp} value={item.percentual_devolucao || 0} onChange={v => upd(idx,'percentual_devolucao',v)} /></F>
                        <F label="IPI devolvido R$ (da nota original)"><MoneyInput style={inp} value={item.valor_ipi_devolvido || 0} onChange={v => upd(idx,'valor_ipi_devolvido',v)} /></F>
                      </>
                    )}
                  </div>
                )}
                {abaTributo === 'pis' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <F label="Situação Tributária" required>
                      <select style={sel} value={item.cst_pis} onChange={e => upd(idx,'cst_pis',e.target.value)}>
                        {CST_PIS_COFINS.map(o => { const [v,l]=o.split('-'); return <option key={v} value={v}>{v} - {l}</option>; })}
                      </select>
                    </F>
                    <F label="Alíquota PIS %"><MoneyInput style={inp} value={item.aliquota_pis} onChange={v => upd(idx,'aliquota_pis',v)} /></F>
                  </div>
                )}
                {abaTributo === 'cofins' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <F label="Situação Tributária" required>
                      <select style={sel} value={item.cst_cofins} onChange={e => upd(idx,'cst_cofins',e.target.value)}>
                        {CST_PIS_COFINS.map(o => { const [v,l]=o.split('-'); return <option key={v} value={v}>{v} - {l}</option>; })}
                      </select>
                    </F>
                    <F label="Alíquota COFINS %"><MoneyInput style={inp} value={item.aliquota_cofins} onChange={v => upd(idx,'aliquota_cofins',v)} /></F>
                  </div>
                )}
              </div>
            </div>

            {/* Info adicional do item */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '10px' }}>
              <F label="Nº Pedido Compra"><input style={inp} value={item.num_pedido} onChange={e => upd(idx,'num_pedido',e.target.value)} /></F>
              <F label="Item do Pedido"><input style={inp} value={item.item_pedido} onChange={e => upd(idx,'item_pedido',e.target.value)} /></F>
              <F label="Info Adicionais do Produto"><input style={inp} value={item.info_adicional} onChange={e => upd(idx,'info_adicional',e.target.value)} /></F>
            </div>

            {/* Devolução (finNFe=4): referência por ITEM à nota de origem —
                NT 2025.002-RTC / VC02-14 (obrigatório a partir de 01/09/2026).
                Renderização condicional inline; NÃO extrair em subcomponente. */}
            {dadosNfe.finalidade === 4 && (
              <div style={{ marginTop: '10px', border: '1px dashed #f59e0b', background: '#fffbeb', borderRadius: '6px', padding: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                  Devolução — nota de origem deste item
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '8px' }}>
                  <F label="Chave da NF-e de origem (44 dígitos)">
                    <input
                      style={inp}
                      value={item.chave_ref_devolucao || ''}
                      onChange={e => upd(idx,'chave_ref_devolucao', e.target.value.replace(/[^0-9A-Za-z]/g,'').toUpperCase().slice(0,44))}
                      placeholder="Somente números — 44 dígitos (digite manualmente se a nota não está no sistema)"
                      maxLength={44}
                    />
                  </F>
                  <F label="Nº item na origem">
                    <input
                      style={inp}
                      value={item.item_ref_devolucao ?? ''}
                      onChange={e => upd(idx,'item_ref_devolucao', e.target.value.replace(/\D/g,''))}
                      placeholder="ex.: 1"
                    />
                  </F>
                </div>
                {(() => {
                  const ch = limparChaveNfe(item.chave_ref_devolucao || '');
                  const nItem = String(item.item_ref_devolucao ?? '').trim();
                  if (!ch && !nItem) return <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>Deixe em branco se este item NÃO está sendo devolvido (devolução parcial).</div>;
                  const v = validarChaveNfe(ch);
                  if (!v.valida) return <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '4px' }}>Chave inválida: {v.erro}</div>;
                  if (!/^\d+$/.test(nItem) || Number(nItem) < 1) return <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '4px' }}>Informe o nº do item na nota de origem.</div>;
                  return <div style={{ fontSize: '11px', color: '#15803d', marginTop: '4px' }}>✓ Referência de origem válida.</div>;
                })()}
              </div>
            )}

            <div style={{ textAlign: 'right', marginTop: '8px', fontWeight: 600, fontSize: '13px', color: '#333' }}>
              V.Produto: R$ {fmtValor(Number(item.quantidade) * Number(item.valor_unitario))}
              {Number(item.valor_desconto) > 0 && <span style={{ color: '#e53e3e' }}> | Desc: -R$ {fmtValor(item.valor_desconto)}</span>}
              {' '}| <span style={{ color: cores.verde }}>Subtotal: R$ {fmtValor(Number(item.quantidade) * Number(item.valor_unitario) - Number(item.valor_desconto || 0) + Number(item.valor_frete || 0) + Number(item.valor_seguro || 0) + Number(item.valor_outros || 0))}</span>
            </div>
          </div>
        </div>
      ))}

      {/* TOTAIS */}
      {itens.length > 0 && (
        <Section title="TOTAIS">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <F label="Total Produtos"><input style={{...inp, background:'#f0f4f8', fontWeight:600}} value={`R$ ${fmtValor(totalProdutos)}`} disabled /></F>
            <F label="Total Desconto"><input style={inp} value={`R$ ${fmtValor(totalDesconto)}`} disabled /></F>
            <F label="Total Frete"><input style={inp} value={`R$ ${fmtValor(totalFrete)}`} disabled /></F>
            <F label="Total Seguro"><input style={inp} value={`R$ ${fmtValor(totalSeguro)}`} disabled /></F>
            <F label="Outras Despesas"><input style={inp} value={`R$ ${fmtValor(totalOutros)}`} disabled /></F>
            <F label="Total IPI"><input style={inp} value="R$ 0,00" disabled /></F>
            <F label="Total PIS"><input style={inp} value="R$ 0,00" disabled /></F>
            <F label="Total COFINS"><input style={inp} value="R$ 0,00" disabled /></F>
          </div>
          <div style={{ textAlign: 'right', marginTop: '12px', fontSize: '20px', fontWeight: 700, color: cores.verde }}>
            Total da Nota: R$ {fmtValor(totalNF)}
          </div>
        </Section>
      )}
      </>
      )}

      {/* ============ ETAPA 4: TRANSPORTADOR ============ */}
      {etapaAtual === 4 && (
      <Section title="TRANSPORTADOR">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <F label="Tipo Frete" required>
            <select style={sel} value={transp.tipo_frete} onChange={e => setTransp({...transp, tipo_frete: e.target.value})}>
              <option value="0">0- Emitente</option><option value="1">1- Destinatário</option><option value="2">2- Terceiros</option><option value="3">3- Próprio remetente</option><option value="4">4- Próprio destinatário</option><option value="9">9- Sem frete</option>
            </select>
          </F>
          <F label="CPF/CNPJ Transportador"><input style={inp} value={transp.cpf_cnpj} onChange={e => setTransp({...transp, cpf_cnpj: e.target.value.replace(/[^0-9A-Za-z]/g,'').toUpperCase()})} maxLength={14} /></F>
          <F label="Razão Social" span={2}><input style={inp} value={transp.razao_social} onChange={e => setTransp({...transp, razao_social: e.target.value})} maxLength={60} /></F>
        </div>
        {/* Veículo: ANTT, placa e UF da placa */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="ANTT"><input style={inp} value={transp.antt} onChange={e => setTransp({...transp, antt: e.target.value})} maxLength={20} /></F>
          <F label="Placa"><input style={inp} value={transp.placa} onChange={e => setTransp({...transp, placa: e.target.value.toUpperCase()})} maxLength={8} /></F>
          <F label="UF Placa">
            <select style={sel} value={transp.placa_uf} onChange={e => setTransp({...transp, placa_uf: e.target.value})}>
              <option value=""></option>
              {['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'].map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </F>
          <div />
        </div>
        {/* Volumes / pesos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="Quantidade"><input style={inp} value={transp.quantidade} onChange={e => setTransp({...transp, quantidade: e.target.value})} /></F>
          <F label="Espécie"><input style={inp} value={transp.especie} onChange={e => setTransp({...transp, especie: e.target.value})} maxLength={60} /></F>
          <F label="Marca"><input style={inp} value={transp.marca} onChange={e => setTransp({...transp, marca: e.target.value})} maxLength={60} /></F>
          <F label="Peso Líquido (kg)"><input style={inp} value={transp.peso_liquido} onChange={e => setTransp({...transp, peso_liquido: e.target.value})} /></F>
          <F label="Peso Bruto (kg)"><input style={inp} value={transp.peso_bruto} onChange={e => setTransp({...transp, peso_bruto: e.target.value})} /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: '10px', marginTop: '10px' }}>
          <F label="Numeração (opcional)"><input style={inp} value={transp.numeracao} onChange={e => setTransp({...transp, numeracao: e.target.value})} maxLength={60} /></F>
          <div />
        </div>
      </Section>
      )}

      {/* ============ ETAPA 5: COBRANÇA ============ */}
      {etapaAtual === 5 && (
      <Section title="Cobrança">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
          <F label="Forma Pagamento" required>
            <select style={sel} value={dadosNfe.ind_pag || '0'} onChange={e => setDadosNfe({...dadosNfe, ind_pag: e.target.value})}>
              <option value="0">À Vista</option><option value="1">A Prazo</option><option value="2">Outros</option>
            </select>
          </F>
        </div>
        {dadosNfe.finalidade === 4 ? (
          <>
            <div style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '10px',
              fontSize: '13px', lineHeight: 1.5,
            }}>
              ℹ️ NF-e de devolução: forma de pagamento fixada em "Sem Pagamento" por exigência da SEFAZ (tPag=90, vPag=0,00).
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '10px', marginBottom: '8px', alignItems: 'end' }}>
              <F label="Meio de pagamento" required>
                <input style={{ ...inp, background: '#f3f4f6', color: '#6b7280' }} value="90 - Sem Pagamento" readOnly disabled />
              </F>
              <F label="Valor">
                <input style={{ ...inp, background: '#f3f4f6', color: '#6b7280' }} value="R$ 0,00" readOnly disabled />
              </F>
            </div>
          </>
        ) : (
          <>
            {pagamentos.map((pag, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '200px 1fr 40px', gap: '10px', marginBottom: '8px', alignItems: 'end' }}>
                <F label="Meio de pagamento" required>
                  <select style={sel} value={pag.forma_pagamento} onChange={e => updPag(idx,'forma_pagamento',e.target.value)}>
                    {FORMAS_PAG.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
                  </select>
                </F>
                <F label="Valor"><MoneyInput style={inp} value={pag.valor} onChange={v => updPag(idx,'valor',v)} /></F>
                {pagamentos.length > 1 && <button onClick={() => setPagamentos(pagamentos.filter((_,i)=>i!==idx))} style={{ background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '7px', cursor: 'pointer', fontSize: '14px' }}>✕</button>}
              </div>
            ))}
            <Btn size="sm" variant="outline" onClick={() => setPagamentos([...pagamentos, { forma_pagamento: '01', valor: 0 }])}>+ Adicionar Pagamento</Btn>
          </>
        )}
      </Section>
      )}

      {/* ============ ETAPA 6: EMISSÃO ============ */}
      {etapaAtual === 6 && (
      <>
      <Section title="Informações Adicionais">
        <F label="Informações Complementares">
          <textarea style={{ ...inp, minHeight: '60px', resize: 'vertical' }} maxLength={5000} value={dadosNfe.info_complementar} onChange={e => setDadosNfe({...dadosNfe, info_complementar: e.target.value})} placeholder="Texto livre para o campo infCpl da NF-e" />
          <ContadorCaracteres valor={dadosNfe.info_complementar} max={5000} />
        </F>
      </Section>

      {/* NF-e REFERENCIADAS (referência à nota INTEIRA, grupo <NFref>) — só
          para Complementar (2) e Ajuste (3). Devolução (4) NÃO usa este bloco:
          a NT 2025.002-RTC (VC02-14) exige referência por ITEM, capturada na
          etapa de Itens (campos "nota de origem" em cada item). */}
      {(dadosNfe.finalidade === 2 || dadosNfe.finalidade === 3
        || (ehExportacao(clienteInfo, dadosNfe) && itens.some(i => String(i.cfop || '').replace(/\D/g, '') === '7501'))) && (
        <Section title="NF-e REFERENCIADAS">
          <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 10 }}>
            {dadosNfe.finalidade === 2 || dadosNfe.finalidade === 3
              ? <>Finalidade {dadosNfe.finalidade === 2 ? 'Complementar' : 'Ajuste'}{' '}geralmente exige referência à NF-e original (grupo &lt;NFref&gt; do XML).</>
              : <>Exportação de mercadoria recebida com fim específico (CFOP 7.501): informe a(s) nota(s) recebida(s).</>}
          </p>
          {dadosNfe.notas_referenciadas.length === 0 && (
            <div style={{
              background: '#FEF3C7', border: '1px solid #F59E0B',
              padding: '8px 12px', borderRadius: 6, fontSize: 13, color: '#92400E',
              marginBottom: 10,
            }}>
              ⚠ Nenhuma chave informada. A SEFAZ pode rejeitar a emissão com código 595 ou similar.
            </div>
          )}
          <NotasReferenciadas
            chaves={dadosNfe.notas_referenciadas}
            onChange={lista => setDadosNfe({ ...dadosNfe, notas_referenciadas: lista })}
          />
        </Section>
      )}

      {/* CE5: EXPORTAÇÃO (saída para destinatário do exterior) — grupo de embarque. */}
      {ehExportacao(clienteInfo, dadosNfe) && (
        <Section title="EXPORTAÇÃO">
          <ExportacaoNota dadosNfe={dadosNfe} setDadosNfe={setDadosNfe} inp={inp} F={F} />
        </Section>
      )}

      {/* AVISO DE CADASTRO INCOMPLETO DO DESTINATÁRIO */}
      {destinatarioIncompleto && (
        <div style={{
          background: '#FEF3C7', border: '1px solid #F59E0B',
          borderRadius: 8, padding: '12px 16px', marginTop: '16px',
          color: '#92400E', fontSize: 13, lineHeight: 1.5,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Cadastro de {clienteInfo?.razao_social || clienteInfo?.nome || 'destinatário'} está incompleto
            </div>
            <div>
              Falta preencher: <b>{camposFaltam.join(', ')}</b>. Atualize o
              cadastro do cliente para emitir. O rascunho pode ser salvo
              normalmente.
            </div>
          </div>
          <button
            onClick={() => navigate('clientes')}
            style={{
              background: '#92400E', color: '#fff', border: 'none',
              borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            Editar cliente
          </button>
        </div>
      )}

      {/* BOTÃO EMITIR */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
        <Btn size="lg" onClick={() => emitir()} disabled={enviando || conferindo || temPendencias || bloqueioEmissao || itens.length === 0 || destinatarioIncompleto}>
          {enviando ? (conferindo ? 'Conferindo pendências...' : 'Transmitindo à SEFAZ...')
            : bloqueioEmissao ? `Aguarde ${String(Math.floor(segundosBloqueio / 60)).padStart(2, '0')}:${String(segundosBloqueio % 60).padStart(2, '0')} para reenviar`
            : temPendencias ? 'Resolva as pendências para emitir'
            : 'Emitir NF-e'}
        </Btn>
        {conferencia?.lista?.length > 0 && (
          <Btn variant="outline" onClick={() => setModalPendencias(true)} style={{ borderColor: '#F59E0B', color: '#92400E' }}>
            ⚠️ Pendências ({conferencia.lista.length})
          </Btn>
        )}
        <Btn variant="outline" onClick={previewDanfe} disabled={itens.length === 0}>
          Pré-visualizar PDF
        </Btn>
        <Btn variant="outline" onClick={salvarRascunho} disabled={itens.length === 0}>
          Salvar Rascunho
        </Btn>
        {totalNF > 0 && <span style={{ fontSize: '18px', fontWeight: 700, color: cores.verde }}>Total: R$ {fmtValor(totalNF)}</span>}
      </div>

      {resultado && (
        <div style={{ marginTop: '1rem' }}>
          {resultado.sucesso ? (
            resultado.mensagem ? (
              <Alerta tipo="sucesso">
                <b>{resultado.mensagem}</b>
              </Alerta>
            ) : (
              <>
              <Alerta tipo="sucesso">
                <b>NF-e autorizada!</b><br/>
                Número: {resultado.numero} | Chave: {resultado.chave_acesso}<br/>
                Protocolo: {resultado.protocolo}
                {resultado.reforma && (<><br/>CBS: R$ {fmtValor(resultado.reforma.cbs)} | IBS: R$ {fmtValor(resultado.reforma.ibs)}</>)}
              </Alerta>
              {resultado.nota_id && (
                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Btn size="lg" onClick={() => baixarDoc('danfe')}>📄 Baixar DANFE</Btn>
                  <Btn size="lg" variant="outline" onClick={() => baixarDoc('xml')}>Baixar XML</Btn>
                </div>
              )}
              </>
            )
          ) : (
            <Alerta tipo="erro">
              {resultado.mensagemAmigavel
                ? <div><b>Falha de comunicação com a SEFAZ.</b> {resultado.mensagemAmigavel}</div>
                : resultado.rejeicaoSefaz
                ? <div><b>Rejeição SEFAZ: {resultado.codigo} - {resultado.erro}</b></div>
                : <div>{resultado.erro}</div>}
              <DetalheTecnico texto={resultado.detalheTecnico} />
              {resultado.camposFaltantes?.length > 0 && (
                <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: '13px', lineHeight: 1.5 }}>
                  {resultado.camposFaltantes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
              {(() => {
                // 656 (da SEFAZ ou bloqueio local) e reenvio idêntico: mostra a
                // última rejeição REAL da nota e o diagnóstico dela.
                const ro = resultado.rejeicaoOriginal;
                const eh656 = resultado.codigo === '656' || resultado.codigo === 'BLOQUEIO_656';
                const dica = resultado.rejeicaoSefaz || ro ? null
                  : 'Dica: salve como rascunho antes, para não perder os itens.';
                if (ro && eh656) {
                  const causa = ro.diagnostico?.mensagem || `[${ro.codigo}] ${ro.motivo || ''}`;
                  return (
                    <>
                      <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEE2E2', border: '1px solid #F87171', borderRadius: 6, color: '#7F1D1D', fontSize: 14 }}>
                        <b>Bloqueado pela SEFAZ por reenvios repetidos.</b> O erro original
                        {ro.numero ? ` (NF-e nº ${ro.numero})` : ''} era: <b>{causa}</b> Corrija isso antes do fim do bloqueio.
                      </div>
                      <CausaProvavel diag={ro.diagnostico} onAbrir={abrirCadastro} mostrarTexto={false} />
                    </>
                  );
                }
                if (ro) {
                  return (
                    <>
                      <div style={{ marginTop: 8, fontSize: 13 }}>
                        Última rejeição desta nota: <b>[{ro.codigo}] {ro.motivo}</b>
                      </div>
                      <CausaProvavel diag={ro.diagnostico} onAbrir={abrirCadastro} />
                    </>
                  );
                }
                // Campo fora do limite (validação local): a mensagem do topo já é
                // a causa exata — aqui só o atalho para o cadastro.
                return <CausaProvavel diag={resultado.diagnostico} onAbrir={abrirCadastro} dica={dica}
                                      mostrarTexto={resultado.codigo !== 'CAMPO_FORA_DO_LAYOUT'} />;
              })()}
              {resultado.acaoSugerida && (
                <div style={{ marginTop: 10, padding: '8px 12px', fontSize: 14, fontWeight: 600, background: '#FEF3C7', color: '#78350F', border: '1px solid #F59E0B', borderRadius: 6 }}>
                  ⚠️ O que fazer: {resultado.acaoSugerida}
                </div>
              )}
              {(resultado.mensagemAmigavel || resultado.rejeicaoSefaz) && (
                <RelatarErroBotao
                  notaId={resultado.notaId}
                  mensagemExibida={resultado.mensagemAmigavel || `Rejeição SEFAZ: ${resultado.codigo} - ${resultado.erro}`}
                  detalheTecnico={resultado.detalheTecnico || (resultado.rejeicaoSefaz ? `[${resultado.codigo}] ${resultado.erro}` : '')}
                  tela="Emitir NF-e" />
              )}
            </Alerta>
          )}
        </div>
      )}
      </>
      )}

      {/* ---- Navegação do wizard (Voltar / Avançar) ---- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
        {etapaAtual > 1 ? (
          <Btn variant="outline" onClick={voltar}>← Voltar</Btn>
        ) : <div />}
        <div style={{ flex: 1 }} />
        {etapaAtual < 6 && (
          <Btn onClick={avancar}>Avançar →</Btn>
        )}
      </div>

      {/* ============ MODAL: Pendências para emitir ============ */}
      {/* JSX inline (mesmo escopo de EmitirPage): nada de componente definido no render. */}
      <Modal titulo="Pendências para emitir" aberto={modalPendencias} onFechar={() => setModalPendencias(false)} largura="760px">
        <p style={{ marginTop: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
          A nota só é emitida completa. Resolva os pontos abaixo e clique em <b>Conferir de novo</b>.
        </p>
        {conferencia && conferencia.chave !== chaveConferencia && (
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
            Você alterou a nota depois desta conferência. Clique em <b>Conferir de novo</b> para atualizar a lista.
          </div>
        )}
        {[
          { chave: 'operador', titulo: 'Você pode resolver', cor: '#92400E', fundo: '#FFFBEB', borda: '#FCD34D' },
          { chave: 'escritorio', titulo: 'Solicite ao escritório', cor: '#7F1D1D', fundo: '#FEF2F2', borda: '#FCA5A5' },
        ].filter(g => gruposPendencias[g.chave].length > 0).map(g => (
          <div key={g.chave} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: g.cor, marginBottom: 6 }}>
              {g.titulo} ({gruposPendencias[g.chave].length})
            </div>
            {gruposPendencias[g.chave].map((p, i) => (
              <div key={`${g.chave}-${i}`} style={{ background: g.fundo, border: `1px solid ${g.borda}`, borderRadius: 6, padding: '8px 12px', marginBottom: 6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, fontSize: 13, lineHeight: 1.45, color: '#1f2937' }}>
                  <div style={{ fontWeight: 600 }}>
                    {p.item ? `Item ${p.item}` : 'Empresa'}
                    {p.item && p.codigo_produto ? ` — ${p.codigo_produto}` : ''}
                    {p.item && p.descricao ? ` — ${p.descricao}` : ''}
                  </div>
                  <div>{p.mensagem}</div>
                  {p.sugestao && (
                    <div style={{ marginTop: 2, color: '#475569' }}>Sugestão: <b>{p.sugestao}</b></div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
                  {sugestaoAplicavel(p) && (
                    <Btn size="sm" variant="outline" onClick={() => aplicarSugestao(p)}>Usar {p.sugestao}</Btn>
                  )}
                  {destinoCorrecao(p, { isAdmin, empresaId: empresaAtual?.id || user?.empresa_id || null }) && (
                    <Btn size="sm" onClick={() => corrigirPendencia(p)}>Corrigir</Btn>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
        {conferencia?.alertas?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1E40AF', marginBottom: 6 }}>
              Avisos — não impedem a emissão ({conferencia.alertas.length})
            </div>
            {conferencia.alertas.map((p, i) => (
              <div key={`alerta-${i}`} style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: '8px 12px', marginBottom: 6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, fontSize: 13, lineHeight: 1.45, color: '#1f2937' }}>
                  {p.item ? <div style={{ fontWeight: 600 }}>Item {p.item}</div> : null}
                  <div>{p.mensagem}</div>
                </div>
                {destinoCorrecao(p, { isAdmin, empresaId: empresaAtual?.id || user?.empresa_id || null }) && (
                  <Btn size="sm" onClick={() => corrigirPendencia(p)}>Corrigir</Btn>
                )}
              </div>
            ))}
          </div>
        )}
        {conferencia && conferencia.lista.length === 0 && (
          <Alerta tipo="sucesso">Nenhuma pendência: a nota pode ser emitida.</Alerta>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <Btn variant="outline" onClick={() => setModalPendencias(false)}>Fechar</Btn>
          {conferencia && conferencia.chave === chaveConferencia && conferencia.lista.length === 0
            && conferencia.alertas?.length > 0 && (
            <Btn variant="outline" disabled={enviando || conferindo} onClick={() => {
              setAlertasCientes(chaveConferencia); setModalPendencias(false); emitir({ cienteAlertas: true });
            }}>Estou ciente — emitir</Btn>
          )}
          <Btn onClick={() => conferirPendencias()} disabled={conferindo}>
            {conferindo ? 'Conferindo...' : 'Conferir de novo'}
          </Btn>
        </div>
      </Modal>

      {/* ============ MODAL: Cadastro rápido de cliente ============ */}
      {/* JSX inline (mesmo escopo de EmitirPage) — Modal é wrapper module-level */}
      {/* importado de components/UI; nenhum sub-componente é criado dentro do  */}
      {/* render, então o foco dos inputs do form é preservado entre keystrokes. */}
      <Modal titulo="Cadastrar Cliente" aberto={modalClienteAberto} onFechar={fecharModalCliente} largura="720px">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <F label="Tipo de Pessoa" required>
            <select style={sel} value={novoCliente.tipo_documento || 'J'} onChange={e => setNovoCliente({ ...novoCliente, tipo_documento: e.target.value })}>
              <option value="J">Pessoa Jurídica</option>
              <option value="F">Pessoa Física</option>
            </select>
          </F>
          <F label={(novoCliente.tipo_documento || 'J') === 'J' ? 'CNPJ' : 'CPF'} required span={2}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                style={{ ...inp, flex: 1 }}
                value={novoCliente.cpf_cnpj || ''}
                onChange={e => setNovoCliente({ ...novoCliente, cpf_cnpj: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase() })}
                maxLength={(novoCliente.tipo_documento || 'J') === 'J' ? 14 : 11}
                placeholder={(novoCliente.tipo_documento || 'J') === 'J' ? 'Só dígitos' : 'Só dígitos'}
              />
              {(novoCliente.tipo_documento || 'J') === 'J' && (
                <Btn size="sm" variant="outline" onClick={buscarCnpjNovoCliente} disabled={buscandoCnpjNC}>
                  {buscandoCnpjNC ? '…' : 'Buscar CNPJ'}
                </Btn>
              )}
            </div>
          </F>
          <F label="Indicador IE" required>
            <select style={sel} value={novoCliente.indicador_ie ?? 9} onChange={e => setNovoCliente({ ...novoCliente, indicador_ie: Number(e.target.value) })}>
              <option value={1}>1- Contribuinte ICMS</option>
              <option value={2}>2- Isento</option>
              <option value={9}>9- Não contribuinte</option>
            </select>
          </F>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label={(novoCliente.tipo_documento || 'J') === 'J' ? 'Razão Social' : 'Nome'} required span={Number(novoCliente.indicador_ie) === 1 ? 2 : 4}>
            <input style={inp} maxLength={60} value={novoCliente.razao_social || ''} onChange={e => setNovoCliente({ ...novoCliente, razao_social: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.razao_social} max={60} />
          </F>
          {Number(novoCliente.indicador_ie) === 1 && (
            <F label="Inscrição Estadual" required span={2}>
              <input style={inp} value={novoCliente.inscricao_estadual || ''} onChange={e => setNovoCliente({ ...novoCliente, inscricao_estadual: e.target.value })} />
            </F>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="CEP">
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                style={{ ...inp, flex: 1 }}
                value={novoCliente.cep || ''}
                onChange={e => setNovoCliente({ ...novoCliente, cep: e.target.value.replace(/\D/g, '') })}
                onBlur={() => { if ((novoCliente.cep || '').replace(/\D/g,'').length === 8) buscarCepNovoCliente(); }}
                maxLength={8}
                placeholder="Só dígitos"
              />
              <Btn size="sm" variant="outline" onClick={buscarCepNovoCliente} disabled={buscandoCepNC}>
                {buscandoCepNC ? '…' : 'Buscar CEP'}
              </Btn>
            </div>
          </F>
          <F label="Logradouro" span={2}>
            <input style={inp} maxLength={60} value={novoCliente.logradouro || ''} onChange={e => setNovoCliente({ ...novoCliente, logradouro: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.logradouro} max={60} />
          </F>
          <F label="Número">
            <input style={inp} maxLength={60} value={novoCliente.numero || ''} onChange={e => setNovoCliente({ ...novoCliente, numero: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.numero} max={60} />
          </F>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="Complemento">
            <input style={inp} maxLength={60} value={novoCliente.complemento || ''} onChange={e => setNovoCliente({ ...novoCliente, complemento: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.complemento} max={60} />
          </F>
          <F label="Bairro">
            <input style={inp} maxLength={60} value={novoCliente.bairro || ''} onChange={e => setNovoCliente({ ...novoCliente, bairro: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.bairro} max={60} />
          </F>
          <F label="Município" required>
            <input style={inp} maxLength={60} value={novoCliente.municipio || ''} onChange={e => setNovoCliente({ ...novoCliente, municipio: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.municipio} max={60} />
          </F>
          <F label="UF">
            <input style={inp} value={novoCliente.uf || ''} maxLength={2} onChange={e => setNovoCliente({ ...novoCliente, uf: e.target.value.toUpperCase() })} />
          </F>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px' }}>
          <F label="Código IBGE Município" required>
            <input style={inp} value={novoCliente.codigo_municipio || ''} maxLength={7} disabled
                   placeholder="Preenchido pelo CEP/CNPJ" />
          </F>
          <F label="E-mail" span={2}>
            <input style={inp} maxLength={60} value={novoCliente.email || ''} onChange={e => setNovoCliente({ ...novoCliente, email: e.target.value })} />
            <ContadorCaracteres valor={novoCliente.email} max={60} />
          </F>
          <F label="Telefone">
            <input style={inp} value={novoCliente.telefone || ''} onChange={e => setNovoCliente({ ...novoCliente, telefone: e.target.value.replace(/\D/g, '') })} />
          </F>
        </div>

        {erroModalCliente && (
          <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '10px 12px', marginTop: 12, color: '#92400E', fontSize: 13 }}>
            ⚠ {erroModalCliente}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <Btn variant="outline" onClick={fecharModalCliente} disabled={salvandoCliente}>Cancelar</Btn>
          <Btn onClick={salvarNovoCliente} disabled={salvandoCliente}>
            {salvandoCliente ? 'Salvando…' : 'Salvar Cliente'}
          </Btn>
        </div>
      </Modal>

    </div>
  );
}

const inputStyle = {
  padding: '6px 8px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const labelStyle = { fontSize: '10px', color: '#999', display: 'block', marginBottom: '2px' };

// ============================================================
// ASSISTENTE IA FISCAL — drawer lateral com chat streaming
// ============================================================
function AssistenteIA({ aberto, fechar, nota, mensagemInicial = null }) {
  const [mensagens, setMensagens] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [conversaId, setConversaId] = useState(null);
  const [erro, setErro] = useState(null);
  const fimRef = useRef(null);
  const jaIniciei = useRef(false);

  const enviar = useCallback(async (texto) => {
    if (!texto.trim() || enviando) return;
    setErro(null);
    const novasMsgs = [...mensagens, { role: 'user', content: texto }];
    setMensagens([...novasMsgs, { role: 'assistant', content: '' }]);
    setInput('');
    setEnviando(true);
    try {
      await api.assistenteStream(
        {
          conversa_id: conversaId,
          nota_id: nota?.id || null,
          mensagens: novasMsgs,
        },
        (evt) => {
          if (evt.tipo === 'conversa_id') {
            setConversaId(evt.id);
          } else if (evt.tipo === 'texto') {
            setMensagens(msgs => {
              const copia = [...msgs];
              copia[copia.length - 1] = {
                role: 'assistant',
                content: (copia[copia.length - 1].content || '') + evt.conteudo,
              };
              return copia;
            });
          } else if (evt.tipo === 'erro') {
            setErro(evt.mensagem || 'Erro no assistente');
          }
        }
      );
    } catch (e) {
      setErro(e.message || 'Erro de conexão.');
    } finally {
      setEnviando(false);
    }
  }, [mensagens, enviando, conversaId, nota]);

  // Reset ao trocar de nota / fechar
  useEffect(() => {
    if (!aberto) {
      jaIniciei.current = false;
      setMensagens([]);
      setConversaId(null);
      setErro(null);
      setInput('');
    }
  }, [aberto]);

  // Mensagem inicial automática quando abrir com uma nota
  useEffect(() => {
    if (aberto && nota && !jaIniciei.current) {
      jaIniciei.current = true;
      const perguntaInicial = nota.codigo_rejeicao
        ? `Me explique o que significa essa rejeição (${nota.codigo_rejeicao}) e o que devo fazer para corrigir.`
        : 'Me ajude a entender o status dessa nota e o que fazer agora.';
      enviar(perguntaInicial);
    }
  }, [aberto, nota, enviar]);

  // Pré-popula o textarea com uma pergunta sugerida (operadora revisa antes de enviar).
  useEffect(() => {
    if (aberto && mensagemInicial) {
      setInput(mensagemInicial);
    }
  }, [aberto, mensagemInicial]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  if (!aberto) return null;

  const limiteAtingido = mensagens.length >= 20;

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: 'min(420px, 100vw)', background: '#fff',
      borderLeft: '1px solid #E5E7EB',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
      display: 'flex', flexDirection: 'column', zIndex: 1000,
    }}>
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid #E5E7EB',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#F9FAFB',
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>🤖 Assistente Fiscal</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {nota
              ? `Nota #${nota.numero} · ${nota.codigo_rejeicao ? `Rejeição ${nota.codigo_rejeicao}` : nota.status}`
              : 'Tire dúvidas sobre preenchimento, tributos e fluxos fiscais'}
          </div>
        </div>
        <button onClick={fechar} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 20, color: '#6B7280', padding: 0, lineHeight: 1,
        }} aria-label="Fechar">✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {mensagens.map((m, i) => (
          <div key={i} style={{
            marginBottom: 10,
            display: 'flex',
            justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '85%', padding: '8px 12px', borderRadius: 12,
              background: m.role === 'user' ? '#DBEAFE' : '#F3F4F6',
              color: '#111827', fontSize: 13, lineHeight: 1.5,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {m.role === 'assistant' && m.content ? (
                <ReactMarkdown
                  components={{
                    p: ({children}) => <p style={{margin: '0 0 8px 0'}}>{children}</p>,
                    ul: ({children}) => <ul style={{margin: '4px 0', paddingLeft: 20}}>{children}</ul>,
                    ol: ({children}) => <ol style={{margin: '4px 0', paddingLeft: 20}}>{children}</ol>,
                    li: ({children}) => <li style={{marginBottom: 2}}>{children}</li>,
                    h1: ({children}) => <div style={{fontWeight: 700, fontSize: 14, marginTop: 6, marginBottom: 4}}>{children}</div>,
                    h2: ({children}) => <div style={{fontWeight: 700, fontSize: 13, marginTop: 6, marginBottom: 4}}>{children}</div>,
                    h3: ({children}) => <div style={{fontWeight: 600, fontSize: 13, marginTop: 4, marginBottom: 2}}>{children}</div>,
                    strong: ({children}) => <strong style={{fontWeight: 700}}>{children}</strong>,
                    code: ({children}) => <code style={{background: '#E5E7EB', padding: '1px 4px', borderRadius: 3, fontSize: 12}}>{children}</code>,
                    hr: () => <hr style={{border: 'none', borderTop: '1px solid #E5E7EB', margin: '8px 0'}} />,
                  }}
                >
                  {m.content}
                </ReactMarkdown>
              ) : (
                m.content || (enviando && i === mensagens.length - 1 ? '...' : '')
              )}
            </div>
          </div>
        ))}
        {erro && (
          <div style={{ color: '#B91C1C', fontSize: 12, padding: 8 }}>
            ⚠️ {erro}
          </div>
        )}
        <div ref={fimRef} />
      </div>

      <div style={{ padding: 10, borderTop: '1px solid #E5E7EB', display: 'flex', gap: 8 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar(input);
            }
          }}
          placeholder={
            enviando
              ? 'Aguarde...'
              : (nota
                  ? 'Pergunte algo sobre essa nota (Enter para enviar)'
                  : 'Ex: Qual CFOP usar para venda dentro do estado? (Enter para enviar)')
          }
          disabled={enviando || limiteAtingido}
          style={{
            flex: 1, resize: 'none', border: '1px solid #D1D5DB',
            borderRadius: 8, padding: 8, fontSize: 13, fontFamily: 'inherit',
            minHeight: 40, maxHeight: 120,
          }}
        />
        <button
          onClick={() => enviar(input)}
          disabled={enviando || !input.trim() || limiteAtingido}
          style={{
            background: '#2563EB', color: '#fff', border: 'none',
            borderRadius: 8, padding: '0 14px', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
          }}
        >
          {enviando ? '...' : 'Enviar'}
        </button>
      </div>
      {limiteAtingido && (
        <div style={{ padding: '6px 12px', fontSize: 11, color: '#92400E', background: '#FEF3C7' }}>
          Limite de 20 mensagens atingido. Feche e reabra para nova conversa.
        </div>
      )}
    </div>
  );
}

function AssistenteFlutuante({ aberto, setAberto }) {
  return (
    <>
      <button
        onClick={() => setAberto(true)}
        title="Assistente Fiscal — tire dúvidas sobre NF-e, tributos e operação do sistema"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#2563EB',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: 26,
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(37, 99, 235, 0.5)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
        }}
      >
        🤖
      </button>

      <AssistenteIA
        aberto={aberto}
        fechar={() => setAberto(false)}
        nota={null}
      />
    </>
  );
}


function NotasPage() {
  const { navigate } = useNav();
  const [notas, setNotas] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [cancelModal, setCancelModal] = useState(null);
  const [cceModal, setCceModal] = useState(null);
  const [cceListModal, setCceListModal] = useState(null);
  const [cceList, setCceList] = useState([]);
  const [cceListLoading, setCceListLoading] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [correcao, setCorrecao] = useState('');
  const [msg, setMsg] = useState(null);
  const [dtInicio, setDtInicio] = useState('');
  const [dtFim, setDtFim] = useState('');
  const [reemitindo, setReemitindo] = useState(null);
  const [editandoRascunho, setEditandoRascunho] = useState(null);
  // Regularização: modal de confirmação
  const [regulConfirm, setRegulConfirm] = useState(null); // { id, numero }
  const [regulando, setRegulando] = useState(false);
  const [verificando, setVerificando] = useState(null); // id da nota "Transmitida" em verificação
  // Inutilização: modal + form + lista das já feitas
  const [inutModal, setInutModal] = useState(false);
  const [inutForm, setInutForm] = useState({ serie: '', numero_inicial: '', numero_final: '', justificativa: '' });
  const [inutEnviando, setInutEnviando] = useState(false);
  const [inutResultado, setInutResultado] = useState(null);
  const [inutLista, setInutLista] = useState([]);
  // AC/BA/SP desde 01/11/2026 (Ajuste SINIEF 40/2026): sem pedido à SEFAZ; o número
  // não utilizado é só registrado no emissor. A regra vem do backend.
  const [inutRegra, setInutRegra] = useState(null);
  const [naoUtilLista, setNaoUtilLista] = useState([]);
  // excluirNotaConfirm: { id, status, numero } da nota a excluir; null = fechado
  const [excluirNotaConfirm, setExcluirNotaConfirm] = useState(null);
  const [excluindoNota, setExcluindoNota] = useState(false);
  // Assistente IA
  const [iaAberta, setIaAberta] = useState(false);
  const [iaNota, setIaNota] = useState(null);
  const abrirIA = (nota) => { setIaNota(nota); setIaAberta(true); };

  const reemitir = async (notaId) => {
    setReemitindo(notaId);
    try {
      const dados = await api.dadosReemissao(notaId);
      navigate('emitir', { reemissao: dados });
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message });
      setReemitindo(null);
    }
  };

  const editarRascunho = async (notaId) => {
    setEditandoRascunho(notaId);
    try {
      const dados = await api.dadosRascunho(notaId);
      navigate('emitir', { rascunho: dados });
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message });
      setEditandoRascunho(null);
    }
  };

  const confirmarExcluirNota = async () => {
    if (!excluirNotaConfirm) return;
    setExcluindoNota(true);
    try {
      await api.excluirNota(excluirNotaConfirm.id);
      setExcluirNotaConfirm(null);
      setMsg({ tipo: 'sucesso', texto: 'Nota excluída!' });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message });
    }
    setExcluindoNota(false);
  };

  const notasFiltradas = (() => {
    const q = busca.trim();
    if (!q) return notas.items || [];
    const qLower = q.toLowerCase();
    const qDigits = q.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    return (notas.items || []).filter(n => {
      const numeroMatch = String(n.numero || '').includes(q);
      const nomeMatch = (n.cliente_nome || '').toLowerCase().includes(qLower);
      const cnpjDigits = (n.cliente_cpf_cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      const cnpjMatch = qDigits.length > 0 && cnpjDigits.includes(qDigits);
      return numeroMatch || nomeMatch || cnpjMatch;
    });
  })();

  const gerarRelatorio = async () => {
    if (!dtInicio || !dtFim) { setMsg({tipo:'erro',texto:'Informe o periodo'}); return; }
    try {
      const blob = await api.relatorioNotas(dtInicio, dtFim);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) { setMsg({tipo:'erro',texto:err.message}); }
  };

  const carregarInutilizacoes = useCallback(() => {
    api.listarInutilizacoes().then(setInutLista).catch(() => setInutLista([]));
    api.listarNumeracaoNaoUtilizada().then(setNaoUtilLista).catch(() => setNaoUtilLista([]));
    api.regraInutilizacao().then(setInutRegra).catch(() => setInutRegra(null));
  }, []);
  const inutDispensada = !!inutRegra?.dispensada;

  const abrirModalInut = () => {
    setInutForm({ serie: '', numero_inicial: '', numero_final: '', justificativa: '' });
    setInutResultado(null);
    setInutModal(true);
    carregarInutilizacoes();
  };

  const inutTravada = useRef(false);
  const enviarInutilizacao = async () => {
    if (inutTravada.current) return;  // clique duplo: um único envio
    inutTravada.current = true;
    setInutEnviando(true); setInutResultado(null);
    try {
      const payload = {
        serie: Number(inutForm.serie),
        numero_inicial: Number(inutForm.numero_inicial),
        numero_final: Number(inutForm.numero_final),
        justificativa: inutForm.justificativa.trim(),
      };
      if (inutDispensada) {
        const { justificativa, ...resto } = payload;
        const res = await api.registrarNumeracaoNaoUtilizada({ ...resto, motivo: justificativa });
        setInutResultado({ ...res, registro: true });
        if (res.sucesso) {
          carregarInutilizacoes();
          setMsg({ tipo: 'sucesso', texto: 'Número não utilizado registrado.' });
        }
      } else {
        const res = await api.inutilizar(payload);
        setInutResultado(res);
        if (res.sucesso) {
          carregar();
          carregarInutilizacoes();
          setMsg({ tipo: 'sucesso', texto: `Inutilização homologada — protocolo ${res.protocolo}` });
        }
      }
    } catch (err) {
      setInutResultado({ sucesso: false, erro: err.message, codigo: err.codigo, motivo: err.motivo, camposFaltantes: err.camposFaltantes });
    }
    inutTravada.current = false;
    setInutEnviando(false);
  };

  const confirmarRegularizar = async () => {
    if (!regulConfirm) return;
    setRegulando(true);
    try {
      const res = await api.regularizarNota(regulConfirm.id);
      if (res.alterou) {
        setMsg({
          tipo: 'sucesso',
          texto: `NF-e nº ${String(regulConfirm.numero).padStart(9, '0')} regularizada para ${res.status_novo.toUpperCase()}. Protocolo: ${res.protocolo || '—'}`,
        });
      } else {
        setMsg({
          tipo: 'aviso',
          texto: res.mensagem || 'SEFAZ não confirmou autorização. Status mantido.',
        });
      }
      carregar();
    } catch (err) {
      setMsg({ tipo: 'erro', texto: `Falha ao consultar SEFAZ: ${err.message}` });
    }
    setRegulando(false);
    setRegulConfirm(null);
  };

  // Nota "Transmitida": o envio à SEFAZ não teve resposta processada. Consulta a
  // situação; a mensagem do servidor diz o resultado ou quando tentar de novo.
  const verificarTransmissao = async (row) => {
    setVerificando(row.id);
    try {
      const res = await api.verificarTransmissao(row.id);
      setMsg({ tipo: res.conclusivo ? 'sucesso' : 'aviso', texto: res.mensagem });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message });
    }
    setVerificando(null);
  };

  const baixarXmlInut = async (id, tipo) => {
    try {
      const blob = await api.baixarXmlInutilizacao(id, tipo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `inutilizacao_${id.slice(0,8)}_${tipo}.xml`; a.click();
    } catch (err) { setMsg({tipo:'erro',texto:err.message}); }
  };

  const baixarXmls = async () => {
    if (!dtInicio || !dtFim) { setMsg({tipo:'erro',texto:'Informe o periodo'}); return; }
    try {
      const blob = await api.downloadXmlsPeriodo(dtInicio, dtFim);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'xmls_' + dtInicio + '_' + dtFim + '.zip'; a.click();
    } catch (err) { setMsg({tipo:'erro',texto:err.message}); }
  };

  const carregar = useCallback(() => {
    api.listarNotas(page, filtro, '', dtInicio, dtFim)
       .then(setNotas)
       .catch(console.error);
  }, [page, filtro, dtInicio, dtFim]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarInutilizacoes(); }, [carregarInutilizacoes]);

  const download = async (id, tipo) => {
    try {
      const blob = tipo === 'danfe' ? await api.downloadDanfe(id) : await api.downloadXml(id);
      const url = URL.createObjectURL(blob);
      if (tipo === 'danfe') window.open(url, '_blank');
      else { const a = document.createElement('a'); a.href = url; a.download = `nfe_${tipo}.xml`; a.click(); }
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const cancelar = async () => {
    try {
      await api.cancelarNFe(cancelModal, justificativa);
      setCancelModal(null); setJustificativa(''); carregar();
      setMsg({ tipo: 'sucesso', texto: 'NF-e cancelada!' });
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const emitirCCe = async () => {
    try {
      const res = await api.cartaCorrecao(cceModal, correcao);
      setCceModal(null); setCorrecao(''); carregar();
      setMsg({ tipo: 'sucesso', texto: res?.duplicado ? res.mensagem : 'CC-e emitida!' });
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const baixarCCePDF = async (notaId, sequencia) => {
    try {
      const blob = await api.downloadCCePdf(notaId, sequencia);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        setMsg({ tipo: 'aviso', texto: 'Pop-up bloqueado pelo navegador. Permita pop-ups deste site para abrir o PDF da CC-e.' });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setMsg({ tipo: 'erro', texto: `Erro ao baixar CC-e: ${err.message}` });
    }
  };

  useEffect(() => {
    if (!cceListModal) { setCceList([]); return; }
    setCceListLoading(true);
    api.listarCCes(cceListModal)
      .then(data => setCceList(Array.isArray(data) ? data : []))
      .catch(err => setMsg({ tipo: 'erro', texto: `Erro ao listar CC-es: ${err.message}` }))
      .finally(() => setCceListLoading(false));
  }, [cceListModal]);

  return (
    <div>
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '20px', margin: 0 }}>Notas Fiscais</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d0d5dd', borderRadius: '6px', fontSize: '12px' }} />
          <span style={{ fontSize: '12px' }}>a</span>
          <input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d0d5dd', borderRadius: '6px', fontSize: '12px' }} />
          <button onClick={gerarRelatorio} style={{padding:"5px 10px",border:"1px solid #d0d5dd",borderRadius:"6px",fontSize:"12px",cursor:"pointer",background:"#fff"}}>Relatorio PDF</button>
          <button onClick={baixarXmls} style={{padding:"5px 10px",border:"1px solid #d0d5dd",borderRadius:"6px",fontSize:"12px",cursor:"pointer",background:"#fff"}}>XMLs ZIP</button>
          <button onClick={abrirModalInut} style={{padding:"5px 10px",border:`1px solid ${cores.vermelho}`,borderRadius:"6px",fontSize:"12px",cursor:"pointer",background:"#fff",color:cores.vermelhoTxt}} title="Inutilizar (ou, em SP/AC/BA, registrar) números não utilizados">Inutilizar Numeração</button>
        </div>
        <select value={filtro} onChange={e => { setFiltro(e.target.value); setPage(1); }}
          style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${cores.cinzaBorda}`, fontSize: '13px' }}>
          <option value="">Todos</option>
          <option value="autorizada">Autorizadas</option>
          <option value="cancelada">Canceladas</option>
          <option value="rejeitada">Rejeitadas</option>
          <option value="reemitida">Reemitidas</option>
          <option value="transmitida">Transmitidas (aguardando confirmação)</option>
          <option value="rascunho">Rascunhos</option>
          <option value="inutilizada">Inutilizadas</option>
        </select>
      </div>

      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
        <input
          type="text"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por número da NF-e, nome ou CNPJ do cliente..."
          style={{
            width: '100%',
            padding: '9px 36px 9px 36px',
            border: `1px solid ${cores.cinzaBorda}`,
            borderRadius: '8px',
            fontSize: '13px',
            boxSizing: 'border-box',
            background: '#fff',
          }}
        />
        {busca && (
          <button
            onClick={() => setBusca('')}
            title="Limpar busca"
            style={{
              position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: '16px', color: '#888', padding: '4px 8px', lineHeight: 1,
            }}
          >×</button>
        )}
      </div>

      <Tabela
        colunas={[
          { key: 'numero', label: 'Número', bold: true, render: r => String(r.numero).padStart(9, '0') },
          { key: 'cliente_nome', label: 'Cliente', render: r => r.cliente_nome || '—' },
          { key: 'data_emissao', label: 'Data', render: r => r.data_emissao ? new Date(r.data_emissao).toLocaleDateString('pt-BR') : '' },
          { key: 'valor_total', label: 'Valor', align: 'right', render: r => `R$ ${fmtValor(r.valor_total)}` },
          { key: 'status', label: 'Status', align: 'center', render: r => (
            <>
            {r.status === 'reemitida'
              ? <span style={{ background: '#e5e7eb', color: '#374151', padding: '3px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>Reemitida</span>
              : <StatusBadge status={r.status} />}
            {r.status === 'rejeitada' && r.diagnostico_rejeicao?.mensagem && (
              <div title={r.diagnostico_rejeicao.mensagem} style={{ fontSize: 11, color: '#B91C1C', marginTop: 4, maxWidth: 260, textAlign: 'left', lineHeight: 1.35 }}>
                Provável causa: {r.diagnostico_rejeicao.mensagem}
              </div>
            )}
            </>
          ) },
        ]}
        dados={notasFiltradas}
        acoes={(row) => (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {row.status === 'autorizada' && <>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'danfe')}>DANFE</Btn>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setCceModal(row.id)} style={{ color: cores.amarelo }}>CC-e</Btn>
              {(row.sequencia_cce || 0) > 0 && (
                <Btn
                  variant="ghost" size="sm"
                  onClick={() => {
                    if (row.sequencia_cce === 1) baixarCCePDF(row.id, 1);
                    else setCceListModal(row.id);
                  }}
                  style={{ color: cores.amarelo }}
                  title={`Baixar PDF da Carta de Correção (${row.sequencia_cce} emitida${row.sequencia_cce > 1 ? 's' : ''})`}
                >CC-e PDF</Btn>
              )}
              <Btn variant="ghost" size="sm" onClick={() => setCancelModal(row.id)} style={{ color: cores.vermelho }}>Cancelar</Btn>
            </>}
            {row.status === 'cancelada' && <>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'danfe')}>DANFE</Btn>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML</Btn>
            </>}
            {row.status === 'rejeitada' && <>
              <Btn variant="ghost" size="sm" onClick={() => abrirIA(row)} style={{ color: cores.azul }} title="Pergunte ao assistente fiscal o que significa a rejeição">
                🤖 Entender erro
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => reemitir(row.id)} disabled={reemitindo === row.id} style={{ color: cores.azul }}>
                {reemitindo === row.id ? 'Carregando...' : 'Reemitir'}
              </Btn>
              <Btn
                variant="ghost" size="sm"
                onClick={() => setRegulConfirm({ id: row.id, numero: row.numero })}
                style={{ color: cores.roxo }}
                title="Consultar SEFAZ e sincronizar status caso a nota tenha sido autorizada"
              >Regularizar</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setExcluirNotaConfirm({ id: row.id, status: row.status, numero: row.numero })} style={{ color: cores.vermelho }}>Excluir</Btn>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML Rejeitado</Btn>
            </>}
            {row.status === 'transmitida' && (
              <Btn
                variant="ghost" size="sm"
                onClick={() => verificarTransmissao(row)}
                disabled={verificando === row.id}
                style={{ color: cores.roxo }}
                title="O envio à SEFAZ ficou sem resposta. Consulta se a nota foi autorizada e atualiza a lista."
              >{verificando === row.id ? 'Consultando...' : 'Verificar na SEFAZ'}</Btn>
            )}
            {row.status === 'denegada' && <>
              <Btn variant="ghost" size="sm" onClick={() => abrirIA(row)} style={{ color: cores.azul }} title="Pergunte ao assistente fiscal o que significa a denegação">
                🤖 Entender erro
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML</Btn>
            </>}
            {row.status === 'reemitida' && <>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML Rejeitado</Btn>
              {row.reemitida_por_id && (
                <Btn variant="ghost" size="sm" onClick={() => navigate('notas', { focar: row.reemitida_por_id })} style={{ color: cores.azul }}>Ver nova NF-e</Btn>
              )}
            </>}
            {row.status === 'rascunho' && <>
              <Btn variant="ghost" size="sm" onClick={() => editarRascunho(row.id)} disabled={editandoRascunho === row.id} style={{ color: cores.azul }}>
                {editandoRascunho === row.id ? 'Carregando...' : 'Editar'}
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => setExcluirNotaConfirm({ id: row.id, status: row.status, numero: row.numero })} style={{ color: cores.vermelho }}>Excluir</Btn>
            </>}
            {row.status === 'inutilizada' && (() => {
              // Acha a inutilização que cobre esse número/série
              const i = inutLista.find(x => x.serie === row.serie && row.numero >= x.numero_inicial && row.numero <= x.numero_final);
              return i ? (
                <>
                  <Btn variant="ghost" size="sm" onClick={() => baixarXmlInut(i.id, 'envio')}>XML Envio</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => baixarXmlInut(i.id, 'retorno')}>XML Retorno</Btn>
                </>
              ) : null;
            })()}
          </div>
        )}
      />

      {busca && notasFiltradas.length === 0 && (notas.items || []).length > 0 && (
        <div style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px', background: '#fafafa', border: '1px dashed #e0e0e0', borderRadius: '8px', marginTop: '8px' }}>
          Nenhuma nota encontrada para essa busca.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1rem' }}>
        <Btn variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Btn>
        <span style={{ padding: '6px 8px', fontSize: '13px', color: '#888' }}>Pág. {page}/{notas.pages || 1}</span>
        <Btn variant="outline" size="sm" disabled={page >= (notas.pages || 1)} onClick={() => setPage(p => p + 1)}>Próxima</Btn>
      </div>

      {/* Cancelamento */}
      <ModalTextoEvento
        titulo="Cancelar NF-e" aberto={!!cancelModal} onFechar={() => setCancelModal(null)}
        tipoAlerta="aviso" alerta="O cancelamento é irreversível e deve ser feito em até 24h após a autorização."
        rotulo="Justificativa" limite={LIMITE_JUSTIFICATIVA}
        valor={justificativa} onChange={e => setJustificativa(e.target.value)}
        onEnviar={cancelar} rotuloBotao="Confirmar Cancelamento" variante="danger"
      />

      {/* CC-e */}
      <ModalTextoEvento
        titulo="Carta de Correção" aberto={!!cceModal} onFechar={() => setCceModal(null)}
        alerta="A CC-e não pode corrigir valores, alíquotas, dados cadastrais do remetente/destinatário ou data de emissão."
        rotulo="Texto da correção" limite={LIMITE_CORRECAO}
        valor={correcao} onChange={e => setCorrecao(e.target.value)}
        onEnviar={emitirCCe} rotuloBotao="Emitir CC-e"
      />

      {/* Seleção de CC-e para download (quando há ≥2) */}
      <Modal titulo="Cartas de Correção emitidas" aberto={!!cceListModal} onFechar={() => setCceListModal(null)} largura="720px">
        {cceListLoading ? (
          <p style={{ padding: '8px 0', color: '#666' }}>Carregando...</p>
        ) : cceList.length === 0 ? (
          <p style={{ padding: '8px 0', color: '#666' }}>Nenhuma CC-e encontrada.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Seq.</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Data</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Protocolo</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Texto da Correção</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {cceList.map(cce => (
                <tr key={cce.sequencia} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px' }}>{cce.sequencia}</td>
                  <td style={{ padding: '8px' }}>{cce.data_evento ? new Date(cce.data_evento).toLocaleString('pt-BR') : '—'}</td>
                  <td style={{ padding: '8px', fontFamily: 'monospace' }}>{cce.protocolo || '—'}</td>
                  <td style={{ padding: '8px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cce.descricao || ''}>
                    {cce.descricao || '—'}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <Btn variant="ghost" size="sm" onClick={() => baixarCCePDF(cceListModal, cce.sequencia)} style={{ color: cores.amarelo }}>
                      Baixar PDF
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <Btn variant="outline" onClick={() => setCceListModal(null)}>Fechar</Btn>
        </div>
      </Modal>

      {/* Confirmação de exclusão de nota — aviso varia conforme status */}
      <Modal
        titulo={excluirNotaConfirm?.status === 'rascunho' ? 'Excluir Rascunho' : 'Excluir Nota Fiscal'}
        aberto={!!excluirNotaConfirm}
        onFechar={() => { if (!excluindoNota) setExcluirNotaConfirm(null); }}
        largura="540px"
      >
        {excluirNotaConfirm?.status === 'rascunho' ? (
          <div style={{ fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
            Tem certeza que deseja excluir este rascunho?
            <div style={{ marginTop: '6px', color: '#666' }}>
              Rascunhos não foram transmitidos à SEFAZ — a exclusão é local e não tem efeito fiscal.
            </div>
          </div>
        ) : (
          <div style={{
            background: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: '8px', padding: '14px 16px', marginBottom: '12px',
            color: '#991B1B', fontSize: '13px', lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 700, marginBottom: '6px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span> ATENÇÃO — EXCLUSÃO COM IMPLICAÇÕES FISCAIS
            </div>
            <div style={{ marginBottom: '8px' }}>
              Você está prestes a excluir permanentemente a nota nº <b>{String(excluirNotaConfirm?.numero || 0).padStart(9, '0')}</b> (status: <b>{excluirNotaConfirm?.status}</b>) do sistema.
            </div>
            <div style={{ marginBottom: '8px' }}>
              Esta ação <b>não pode ser desfeita</b> e pode ter consequências fiscais e contábeis graves:
            </div>
            <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
              <li>Perda do histórico de transmissão e do XML de retorno da SEFAZ.</li>
              <li>Possível inconsistência com registros já enviados ao contador, ao fisco ou a sistemas integrados (ERP, escrita fiscal, SPED).</li>
              <li>Em caso de fiscalização, a ausência da nota no sistema <b>não a remove dos registros da SEFAZ</b> — a numeração permanece consumida e a nota permanece consultável publicamente pela chave de acesso.</li>
              <li>Para notas rejeitadas, considere usar <b>Reemitir</b> em vez de excluir, mantendo o rastreio da rejeição original.</li>
            </ul>
            <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
              Recomendação: confirme com o seu contador antes de prosseguir.
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setExcluirNotaConfirm(null)} disabled={excluindoNota}>Cancelar</Btn>
          <Btn variant="danger" onClick={confirmarExcluirNota} disabled={excluindoNota}>
            {excluindoNota ? 'Excluindo...' : (excluirNotaConfirm?.status === 'rascunho' ? 'Excluir' : 'Excluir mesmo assim')}
          </Btn>
        </div>
      </Modal>

      {/* Regularização: consulta SEFAZ */}
      <Modal
        titulo="Regularizar NF-e"
        aberto={!!regulConfirm}
        onFechar={() => { if (!regulando) setRegulConfirm(null); }}
        largura="500px"
      >
        <div style={{ fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
          Consultar a SEFAZ para verificar se a NF-e nº <b>{String(regulConfirm?.numero || 0).padStart(9, '0')}</b> foi
          de fato autorizada e atualizar o status local?
          <div style={{ marginTop: '8px', color: '#666' }}>
            Esta operação chama o webservice oficial NfeConsultaProtocolo e pode levar alguns segundos.
            Se a SEFAZ confirmar autorização (cStat 100 ou 150), a nota será marcada como <b>autorizada</b> com o protocolo
            recebido. Se não houver autorização, o status fica como está.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setRegulConfirm(null)} disabled={regulando}>Cancelar</Btn>
          <Btn onClick={confirmarRegularizar} disabled={regulando} style={{ background: cores.roxo, color: '#fff', border: 'none' }}>
            {regulando ? 'Consultando SEFAZ...' : 'Consultar SEFAZ'}
          </Btn>
        </div>
      </Modal>

      {/* Inutilização de Numeração */}
      <Modal
        titulo={inutDispensada ? 'Registrar número não utilizado' : 'Inutilizar Numeração de NF-e'}
        aberto={inutModal}
        onFechar={() => { if (!inutEnviando) { setInutModal(false); setInutResultado(null); } }}
        largura="640px"
      >
        {inutDispensada ? (
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: '8px', padding: '12px 14px', marginBottom: '14px',
          color: '#1E3A8A', fontSize: '13px', lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Não há mais inutilização na SEFAZ</div>
          {inutRegra.mensagem}
          <div style={{ marginTop: '6px' }}>
            Nada é enviado à SEFAZ. Só é possível registrar números anteriores ao próximo número
            da série, e o registro não pode ser desfeito.
          </div>
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#475569' }}>{inutRegra.fundamento}</div>
        </div>
        ) : (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: '8px', padding: '12px 14px', marginBottom: '14px',
          color: '#991B1B', fontSize: '13px', lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '18px' }}>⚠️</span> Ação fiscal definitiva
          </div>
          A inutilização registra na SEFAZ que a faixa de numeração <b>não foi usada</b>. Use APENAS para
          números efetivamente sem autorização (rejeitados, em estado inconsistente, ou nunca transmitidos).
          <b> Não inutilize NF-e autorizadas</b> — para essas, use Cancelamento (até 24h) ou regularização via contador.
          Após autorizada pela SEFAZ, a inutilização é <b>irreversível</b>.
        </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <Input label="Série *" type="number" value={inutForm.serie} onChange={e => setInutForm({ ...inutForm, serie: e.target.value })} />
          <Input label="Número Inicial *" type="number" value={inutForm.numero_inicial} onChange={e => setInutForm({ ...inutForm, numero_inicial: e.target.value })} />
          <Input label="Número Final *" type="number" value={inutForm.numero_final} onChange={e => setInutForm({ ...inutForm, numero_final: e.target.value })} />
        </div>
        <Textarea
          label={`${inutDispensada ? 'Motivo' : 'Justificativa'} * (${inutForm.justificativa.length}/255 — mínimo 15)`}
          value={inutForm.justificativa}
          onChange={e => setInutForm({ ...inutForm, justificativa: e.target.value.slice(0, 255) })}
          placeholder="Descreva o motivo (sem acentos para evitar problemas com a SEFAZ)"
        />
        {inutResultado && (
          inutResultado.sucesso && inutResultado.registro ? (
            <Alerta tipo="sucesso">
              <b>Número não utilizado registrado.</b> A emissão segue normalmente pelo próximo número.
            </Alerta>
          ) : inutResultado.sucesso ? (
            <Alerta tipo="sucesso">
              <b>Inutilização homologada pela SEFAZ.</b><br/>
              Protocolo: {inutResultado.protocolo} | cStat: {inutResultado.codigo}<br/>
              {inutResultado.motivo}
            </Alerta>
          ) : (
            <Alerta tipo="erro">
              <div>{inutResultado.erro || inutResultado.mensagem || inutResultado.motivo || 'Falha desconhecida'}</div>
              {inutResultado.acao_sugerida && <div style={{ marginTop: '4px' }}>{inutResultado.acao_sugerida}</div>}
              {inutResultado.codigo && <div style={{ marginTop: '4px', fontSize: '12px' }}>cStat SEFAZ: {inutResultado.codigo}</div>}
              {inutResultado.camposFaltantes?.length > 0 && (
                <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: '13px' }}>
                  {inutResultado.camposFaltantes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </Alerta>
          )
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="outline" onClick={() => { setInutModal(false); setInutResultado(null); }} disabled={inutEnviando}>
            {inutResultado?.sucesso ? 'Fechar' : 'Cancelar'}
          </Btn>
          {!inutResultado?.sucesso && (
            <Btn
              variant="danger"
              onClick={enviarInutilizacao}
              disabled={
                inutEnviando ||
                !inutForm.serie || !inutForm.numero_inicial || !inutForm.numero_final ||
                inutForm.justificativa.trim().length < 15
              }
            >
              {inutDispensada
                ? (inutEnviando ? 'Registrando...' : 'Registrar')
                : (inutEnviando ? 'Transmitindo à SEFAZ...' : 'Enviar à SEFAZ')}
            </Btn>
          )}
        </div>

        {naoUtilLista.length > 0 && (
          <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Números não utilizados registrados</div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee', color: '#666' }}>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Faixa</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Série</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Motivo</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Registrado por</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {naoUtilLista.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f3' }}>
                      <td style={{ padding: '6px 4px' }}>{r.numero_inicial}{r.numero_final !== r.numero_inicial ? ` – ${r.numero_final}` : ''}</td>
                      <td style={{ padding: '6px 4px' }}>{r.serie}</td>
                      <td style={{ padding: '6px 4px' }}>{r.motivo}</td>
                      <td style={{ padding: '6px 4px' }}>{r.usuario_email || '—'}</td>
                      <td style={{ padding: '6px 4px' }}>{r.data ? new Date(r.data + 'Z').toLocaleString('pt-BR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {inutLista.length > 0 && (
          <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Inutilizações desta empresa</div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee', color: '#666' }}>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Faixa</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Série</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Protocolo</th>
                    <th style={{ textAlign: 'right', padding: '6px 4px' }}>XMLs</th>
                  </tr>
                </thead>
                <tbody>
                  {inutLista.map(i => (
                    <tr key={i.id} style={{ borderBottom: '1px solid #f5f5f3' }}>
                      <td style={{ padding: '6px 4px' }}>{i.numero_inicial}{i.numero_final !== i.numero_inicial ? ` – ${i.numero_final}` : ''}</td>
                      <td style={{ padding: '6px 4px' }}>{i.serie}</td>
                      <td style={{ padding: '6px 4px' }}>{i.status}</td>
                      <td style={{ padding: '6px 4px', fontFamily: 'ui-monospace, monospace' }}>{i.protocolo || '—'}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        <Btn variant="ghost" size="sm" onClick={() => baixarXmlInut(i.id, 'envio')}>Envio</Btn>
                        <Btn variant="ghost" size="sm" onClick={() => baixarXmlInut(i.id, 'retorno')}>Retorno</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      <AssistenteIA
        aberto={iaAberta}
        fechar={() => setIaAberta(false)}
        nota={iaNota}
      />
    </div>
  );
}


// ============================================================
// MINHA EMPRESA (operador) — edita o próprio cadastro + certificado
// ============================================================
const CAMPOS_CRITICOS_EMPRESA = new Set([
  'cnpj', 'razao_social', 'inscricao_estadual', 'regime_tributario', 'ambiente',
]);

function MinhaEmpresaPage() {
  const { user } = useAuth();
  const { payload, navigate } = useNav();
  // ambiente, série e próximo número: só o escritório altera (F0a).
  const soEscritorio = user?.perfil !== 'admin';
  const [emp, setEmp] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmCriticos, setConfirmCriticos] = useState(null); // lista de campos críticos pendentes
  const [confirmProximo, setConfirmProximo] = useState(false);
  const [certInfo, setCertInfo] = useState(null);
  const [certModalAberto, setCertModalAberto] = useState(!!payload?.abrirCertificado);
  const [certFile, setCertFile] = useState(null);
  const [certSenha, setCertSenha] = useState('');
  const [certEnviando, setCertEnviando] = useState(false);
  const [certResultado, setCertResultado] = useState(null);
  // Logotipo (DANFE)
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoInfo, setLogoInfo] = useState(null);   // { nome, data } ou null
  const [logoFile, setLogoFile] = useState(null);
  const [logoEnviando, setLogoEnviando] = useState(false);
  const [logoErro, setLogoErro] = useState(null);
  const [logoOk, setLogoOk] = useState(null);
  const [logoRemovendo, setLogoRemovendo] = useState(false);
  const [confirmRemoverLogo, setConfirmRemoverLogo] = useState(false);

  // Busca o logo como blob (o <img> não envia o JWT) e vira uma object URL.
  // A revogação da URL anterior fica por conta do useEffect de limpeza abaixo.
  const carregarLogo = useCallback(() => {
    if (!user?.empresa_id) return;
    api.obterLogo(user.empresa_id)
      .then(blob => setLogoUrl(URL.createObjectURL(blob)))
      .catch(() => setLogoUrl(null));
  }, [user?.empresa_id]);

  const carregar = useCallback(() => {
    if (!user?.empresa_id) return;
    api.obterEmpresa(user.empresa_id)
      .then(e => {
        setEmp(e); setForm(e);
        setLogoInfo(e.tem_logo ? { nome: e.logo_nome_arquivo, data: e.logo_data_upload } : null);
        if (e.tem_logo) carregarLogo();
      })
      .catch(err => setMsg({ tipo: 'erro', texto: err.message }));
    api.infoCertificado(user.empresa_id)
      .then(setCertInfo)
      .catch(() => setCertInfo(null));
  }, [user?.empresa_id, carregarLogo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Revoga a object URL anterior sempre que ela troca — e na saída da tela.
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);


  const enviarLogo = async () => {
    if (!logoFile) { setLogoErro('Selecione um arquivo PNG ou JPG.'); return; }
    if (!['image/png', 'image/jpeg'].includes(logoFile.type)) {
      setLogoErro('Formato não aceito. Envie uma imagem PNG ou JPG.'); return;
    }
    if (logoFile.size > 5 * 1024 * 1024) {
      setLogoErro('O arquivo excede 5 MB. Reduza a imagem e envie novamente.'); return;
    }
    setLogoEnviando(true); setLogoErro(null); setLogoOk(null);
    try {
      const res = await api.uploadLogo(user.empresa_id, logoFile);
      setLogoInfo({ nome: res.logo_nome_arquivo, data: res.logo_data_upload });
      setLogoOk('Logotipo enviado. Ele já aparece no DANFE das próximas notas.');
      setLogoFile(null);
      carregarLogo();
    } catch (err) {
      setLogoErro(err.message);
    }
    setLogoEnviando(false);
  };

  const removerLogo = async () => {
    setLogoRemovendo(true); setLogoErro(null); setLogoOk(null);
    try {
      await api.removerLogo(user.empresa_id);
      setLogoInfo(null);
      setLogoUrl(null);
      setLogoOk('Logotipo removido. O DANFE volta a ser gerado sem logotipo.');
      setConfirmRemoverLogo(false);
    } catch (err) {
      setLogoErro(err.message);
      setConfirmRemoverLogo(false);
    }
    setLogoRemovendo(false);
  };

  const camposAlterados = () => {
    if (!emp) return [];
    return Object.keys(form).filter(k =>
      Object.prototype.hasOwnProperty.call(emp, k) && form[k] !== emp[k]
    );
  };

  const salvar = async () => {
    const alterados = camposAlterados();
    if (!alterados.length) {
      setMsg({ tipo: 'aviso', texto: 'Nenhuma alteração para salvar.' });
      return;
    }
    const erroTam = excessoLayoutNfe(form, CAMPOS_LIMITE_EMPRESA);
    if (erroTam) { setMsg({ tipo: 'erro', texto: erroTam }); return; }
    // Validação IBGE (mesmo padrão dos outros formulários)
    const ibge = (form.codigo_municipio || '').toString().trim();
    if (!ibge || ibge.replace(/\D/g, '').length !== 7) {
      setMsg({ tipo: 'erro', texto: 'Selecione o município pelo autocomplete — Cód. IBGE (7 dígitos) obrigatório.' });
      return;
    }
    // Aviso pre-save: críticos
    const criticos = alterados.filter(k => CAMPOS_CRITICOS_EMPRESA.has(k));
    if (criticos.length && !confirmCriticos) {
      setConfirmCriticos(criticos);
      return;
    }
    // Aviso pre-save: próximo número
    if (alterados.includes('proximo_numero_nfe') && !confirmProximo) {
      setConfirmProximo(true);
      return;
    }
    setSalvando(true);
    try {
      const atualizada = await api.atualizarEmpresa(user.empresa_id, form);
      setEmp(atualizada); setForm(atualizada);
      setMsg({ tipo: 'sucesso', texto: 'Cadastro atualizado com sucesso.' });
      setConfirmCriticos(null); setConfirmProximo(false);
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message });
    }
    setSalvando(false);
  };

  const enviarCert = async () => {
    if (!certFile) { setCertResultado({ erro: 'Selecione o arquivo .pfx' }); return; }
    if (!certSenha) { setCertResultado({ erro: 'Informe a senha do certificado' }); return; }
    if (!certFile.name.toLowerCase().endsWith('.pfx')) {
      setCertResultado({ erro: 'O arquivo deve ter extensão .pfx' }); return;
    }
    if (certFile.size > 5 * 1024 * 1024) {
      setCertResultado({ erro: 'Arquivo excede 5 MB' }); return;
    }
    setCertEnviando(true); setCertResultado(null);
    try {
      const res = await api.uploadCertificado(user.empresa_id, certFile, certSenha);
      setCertResultado({ sucesso: true, ...res });
      setCertFile(null); setCertSenha('');
      api.infoCertificado(user.empresa_id).then(setCertInfo).catch(() => {});
    } catch (err) {
      setCertResultado({ erro: err.message });
    }
    setCertEnviando(false);
  };

  if (!emp) return <div style={{ padding: '24px' }}>Carregando...</div>;

  return (
    <div>
      <BannerVoltarEmissao retorno={payload?.retornoEmissao}
        onVoltar={() => navigate('emitir', { retomada: payload.retornoEmissao })} />
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}
      <h2 style={{ fontSize: '20px', marginBottom: '12px' }}>Minha Empresa</h2>

      {/* Banner de vencimento */}
      {certInfo?.cadastrado && certInfo.vencido && (
        <Alerta tipo="erro">
          <b>❌ Certificado vencido em {certInfo.validade}.</b> A emissão de NF-e está suspensa até a substituição do certificado.
        </Alerta>
      )}
      {certInfo?.cadastrado && certInfo.vence_em_breve && (
        <Alerta tipo="aviso">
          <b>⚠️ Seu certificado digital vence em {certInfo.dias_restantes} dia{certInfo.dias_restantes === 1 ? '' : 's'}</b> ({certInfo.validade}). Programe a substituição.
        </Alerta>
      )}

      <div style={{ background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Input label="Razão Social *" value={form.razao_social || ''} onChange={e => setForm({ ...form, razao_social: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={60} contador />
          <Input label="Nome Fantasia" value={form.nome_fantasia || ''} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} maxLength={60} contador />
          <Input label="CNPJ *" value={form.cnpj || ''} onChange={e => setForm({ ...form, cnpj: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase() })} maxLength={14} />
          <Input label="Inscrição Estadual *" value={form.inscricao_estadual || ''} onChange={e => setForm({ ...form, inscricao_estadual: e.target.value })} />
          <Input label="Inscrição Municipal" value={form.inscricao_municipal || ''} onChange={e => setForm({ ...form, inscricao_municipal: e.target.value })} />
          <Input label="CNAE" value={form.cnae || ''} onChange={e => setForm({ ...form, cnae: e.target.value })} />
          <Select label="Regime Tributário *" value={form.regime_tributario || 3} onChange={e => setForm({ ...form, regime_tributario: Number(e.target.value) })}>
            <option value={1}>Simples Nacional</option>
            <option value={2}>Excesso Sublimite</option>
            <option value={3}>Regime Normal (LP/LR)</option>
            <option value={4}>Simples Nacional - MEI</option>
          </Select>
          <Select label="Ambiente *" value={form.ambiente || 2} disabled={soEscritorio} title={soEscritorio ? 'Só o escritório contábil altera' : undefined} onChange={e => setForm({ ...form, ambiente: Number(e.target.value) })}>
            <option value={2}>Homologação (Testes)</option>
            <option value={1}>Produção</option>
          </Select>
          <Input label="Logradouro *" value={form.logradouro || ''} onChange={e => setForm({ ...form, logradouro: e.target.value })} style={{ gridColumn: '1/3' }} maxLength={60} contador />
          <Input label="Número *" value={form.numero || ''} onChange={e => setForm({ ...form, numero: e.target.value })} maxLength={60} contador />
          <Input label="Complemento" value={form.complemento || ''} onChange={e => setForm({ ...form, complemento: e.target.value })} maxLength={60} contador />
          <Input label="Bairro *" value={form.bairro || ''} onChange={e => setForm({ ...form, bairro: e.target.value })} maxLength={60} contador />
          <Input label="UF *" value={form.uf || ''} onChange={e => setForm({ ...form, uf: e.target.value.toUpperCase() })} maxLength={2} />
          <Input label="CEP *" value={form.cep || ''} onChange={e => setForm({ ...form, cep: e.target.value.replace(/\D/g, '') })} maxLength={8} />
          <MunicipioAutocomplete
            uf={form.uf}
            municipio={form.municipio || ''}
            codigoIbge={form.codigo_municipio || ''}
            onChange={(nome, codigo) => setForm({ ...form, municipio: nome, codigo_municipio: codigo })}
            required
          />
          <Input label="Telefone" value={form.telefone || ''} onChange={e => setForm({ ...form, telefone: e.target.value })} />
          <Input label="E-mail" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Input label="Série NF-e" type="number" value={form.serie_nfe ?? 1} disabled={soEscritorio} title={soEscritorio ? 'Só o escritório contábil altera' : undefined} onChange={e => setForm({ ...form, serie_nfe: Number(e.target.value) })} />
          <Input label="Próximo Número NF-e" type="number" value={form.proximo_numero_nfe ?? 1} disabled={soEscritorio} title={soEscritorio ? 'Só o escritório contábil altera' : undefined} onChange={e => setForm({ ...form, proximo_numero_nfe: Number(e.target.value) })} />
        </div>
        {soEscritorio && (
          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px' }}>
            Ambiente, série e próximo número da NF-e só podem ser alterados pelo escritório contábil.
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="outline" onClick={() => setForm(emp)} disabled={salvando}>Reverter</Btn>
          <Btn onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar Alterações'}</Btn>
        </div>
      </div>

      {/* Certificado Digital */}
      <div style={{ background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '20px' }}>
        <h3 style={{ fontSize: '16px', margin: '0 0 12px' }}>Certificado Digital A1</h3>
        {certInfo?.cadastrado ? (
          <div style={{ fontSize: '13px', lineHeight: 1.6 }}>
            <div><b>Validade:</b> {certInfo.validade} {certInfo.vencido && <span style={{ color: cores.vermelho }}> (VENCIDO)</span>}</div>
            <div><b>Dias restantes:</b> {certInfo.dias_restantes}</div>
            {certInfo.serial && <div style={{ color: '#666' }}><b>Serial:</b> {certInfo.serial}</div>}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#666' }}>Nenhum certificado cadastrado. Faça upload do arquivo .pfx para emitir NF-e.</div>
        )}
        <div style={{ marginTop: '12px' }}>
          <Btn onClick={() => { setCertModalAberto(true); setCertResultado(null); setCertFile(null); setCertSenha(''); }}>
            {certInfo?.cadastrado ? 'Substituir Certificado' : 'Cadastrar Certificado'}
          </Btn>
        </div>
      </div>

      {/* Logotipo da Empresa — impresso no DANFE */}
      <div style={{ background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '20px', marginTop: '16px' }}>
        <h3 style={{ fontSize: '16px', margin: '0 0 12px' }}>Logotipo da Empresa</h3>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Preview */}
          <div style={{
            width: '220px', minHeight: '90px', border: `1px solid ${cores.cinzaBorda}`,
            borderRadius: '6px', background: logoUrl ? '#fff' : '#F3F4F6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '10px', boxSizing: 'border-box', flexShrink: 0,
          }}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logotipo da empresa" style={{ maxWidth: '100%', maxHeight: '80px', display: 'block' }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Nenhum logotipo enviado</span>
            )}
          </div>

          {/* Controles */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            {logoInfo && (
              <div style={{ fontSize: '13px', lineHeight: 1.6, marginBottom: '10px' }}>
                <div><b>Arquivo:</b> {logoInfo.nome}</div>
                {logoInfo.data && (
                  <div style={{ color: '#666' }}>
                    <b>Enviado em:</b> {new Date(logoInfo.data).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={e => { setLogoFile(e.target.files?.[0] || null); setLogoErro(null); setLogoOk(null); }}
                style={{ fontSize: '13px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Btn onClick={enviarLogo} disabled={logoEnviando || !logoFile}>
                {logoEnviando ? 'Enviando...' : 'Enviar logotipo'}
              </Btn>
              {logoInfo && (
                <Btn variant="outline" onClick={() => { setConfirmRemoverLogo(true); setLogoErro(null); setLogoOk(null); }} disabled={logoEnviando || logoRemovendo}>
                  Remover logotipo
                </Btn>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '10px', lineHeight: 1.5 }}>
              PNG ou JPG, até 5 MB. A imagem é redimensionada automaticamente para o DANFE.
              Recomendado: fundo transparente ou branco, formato horizontal.
            </div>
            {logoErro && <div style={{ color: cores.vermelho, fontSize: '13px', marginTop: '10px', fontWeight: 500 }}>{logoErro}</div>}
            {logoOk && <div style={{ color: cores.verde, fontSize: '13px', marginTop: '10px', fontWeight: 500 }}>{logoOk}</div>}
          </div>
        </div>
      </div>

      {/* Confirmação de remoção do logotipo */}
      <Modal
        titulo="Remover logotipo"
        aberto={confirmRemoverLogo}
        onFechar={() => { if (!logoRemovendo) setConfirmRemoverLogo(false); }}
        largura="440px"
      >
        <div style={{ fontSize: '13px', lineHeight: 1.6, marginBottom: '12px' }}>
          O logotipo será apagado e o DANFE das próximas notas voltará a ser gerado
          sem logotipo. As notas já emitidas não são alteradas.
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setConfirmRemoverLogo(false)} disabled={logoRemovendo}>Cancelar</Btn>
          <Btn variant="danger" onClick={removerLogo} disabled={logoRemovendo}>
            {logoRemovendo ? 'Removendo...' : 'Sim, remover'}
          </Btn>
        </div>
      </Modal>

      {/* Confirmação alterando campos críticos */}
      <Modal
        titulo="⚠️ Alterações fiscais críticas"
        aberto={!!confirmCriticos}
        onFechar={() => { if (!salvando) setConfirmCriticos(null); }}
        largura="560px"
      >
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', padding: '14px', borderRadius: '8px', color: '#991B1B', fontSize: '13px', lineHeight: 1.6, marginBottom: '12px' }}>
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>Você está alterando dados FISCAIS críticos.</div>
          Essas alterações afetam diretamente a emissão de NF-e e podem causar:
          <ul style={{ margin: '6px 0 6px 18px' }}>
            <li>Rejeições da SEFAZ se inconsistente com os dados oficiais.</li>
            <li>Inconsistências fiscais nas notas já emitidas (a chave de acesso contém o CNPJ).</li>
            <li>Necessidade de reemissão do certificado digital se o CNPJ mudou.</li>
          </ul>
          <div style={{ marginTop: '6px' }}><b>Recomenda-se confirmar com seu contador antes de salvar.</b></div>
          <div style={{ marginTop: '10px', fontSize: '12px' }}>
            Campos alterados: <b>{(confirmCriticos || []).join(', ')}</b>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setConfirmCriticos(null)} disabled={salvando}>Cancelar</Btn>
          <Btn variant="danger" onClick={() => salvar()} disabled={salvando}>{salvando ? 'Salvando...' : 'Sim, prosseguir'}</Btn>
        </div>
      </Modal>

      {/* Confirmação ao alterar próximo número */}
      <Modal
        titulo="⚠️ Alterar próximo número de NF-e"
        aberto={confirmProximo && !confirmCriticos}
        onFechar={() => { if (!salvando) setConfirmProximo(false); }}
        largura="500px"
      >
        <Alerta tipo="aviso">
          Alterar o próximo número de NF-e pode causar duplicidade ou pular numeração.
          Use apenas se houver inconsistência conhecida (ex.: importação de notas legadas).
          Recomendado consultar o contador.
        </Alerta>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="outline" onClick={() => setConfirmProximo(false)} disabled={salvando}>Cancelar</Btn>
          <Btn onClick={() => salvar()} disabled={salvando}>{salvando ? 'Salvando...' : 'Confirmar'}</Btn>
        </div>
      </Modal>

      {/* Modal upload certificado */}
      <Modal titulo="Upload de Certificado Digital A1" aberto={certModalAberto} onFechar={() => { if (!certEnviando) setCertModalAberto(false); }} largura="500px">
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
          O sistema valida a senha, extrai os dados do certificado e confere se o CNPJ
          do certificado bate com o CNPJ desta empresa antes de salvar.
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Arquivo .pfx (até 5 MB) *</label>
          <input type="file" accept=".pfx" onChange={e => setCertFile(e.target.files?.[0] || null)} style={{ fontSize: '13px' }} />
        </div>
        <Input label="Senha do certificado *" type="password" value={certSenha} onChange={e => setCertSenha(e.target.value)} />
        {certResultado && (
          certResultado.sucesso ? (
            <Alerta tipo="sucesso">
              Certificado cadastrado. Validade: <b>{certResultado.validade}</b> ({certResultado.dias_restantes} dia{certResultado.dias_restantes === 1 ? '' : 's'} restantes).
            </Alerta>
          ) : (
            <Alerta tipo="erro">{certResultado.erro}</Alerta>
          )
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="outline" onClick={() => setCertModalAberto(false)} disabled={certEnviando}>Fechar</Btn>
          <Btn onClick={enviarCert} disabled={certEnviando || !certFile || !certSenha}>
            {certEnviando ? 'Validando...' : 'Validar e Salvar'}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// AUDITORIA (admin) — histórico de alterações em empresas
// ============================================================
function AuditoriaPage() {
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({ campo: '', inicio: '', fim: '' });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true); setErro(null);
    const params = { page, per_page: 50 };
    if (filtros.campo) params.campo = filtros.campo;
    if (filtros.inicio) params.inicio = filtros.inicio;
    if (filtros.fim) params.fim = filtros.fim;
    api.listarAuditoria(params)
      .then(setData)
      .catch(err => setErro(err.message))
      .finally(() => setCarregando(false));
  }, [page, filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '12px' }}>Histórico de Alterações</h2>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Campo</label>
          <input value={filtros.campo} onChange={e => { setPage(1); setFiltros({ ...filtros, campo: e.target.value }); }} placeholder="ex: cnpj" style={{ padding: '6px 10px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>De</label>
          <input type="date" value={filtros.inicio} onChange={e => { setPage(1); setFiltros({ ...filtros, inicio: e.target.value }); }} style={{ padding: '6px 10px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Até</label>
          <input type="date" value={filtros.fim} onChange={e => { setPage(1); setFiltros({ ...filtros, fim: e.target.value }); }} style={{ padding: '6px 10px', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <Btn variant="outline" onClick={() => { setFiltros({ campo: '', inicio: '', fim: '' }); setPage(1); }}>Limpar</Btn>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>{data.total} registro{data.total === 1 ? '' : 's'}</span>
      </div>

      <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Data</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Empresa</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Usuário</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Campo</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Antes</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>Depois</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#666' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f3' }}>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{l.data ? new Date(l.data).toLocaleString('pt-BR') : ''}</td>
                <td style={{ padding: '8px 10px' }}>{l.empresa_razao_social || l.empresa_id?.slice(0, 8)}</td>
                <td style={{ padding: '8px 10px' }}>{l.usuario_nome || '—'}<br /><span style={{ fontSize: '11px', color: '#888' }}>{l.perfil}</span></td>
                <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace' }}>{l.campo_alterado}</td>
                <td style={{ padding: '8px 10px', maxWidth: '180px', wordBreak: 'break-word', color: '#888' }}>{l.valor_anterior || '—'}</td>
                <td style={{ padding: '8px 10px', maxWidth: '180px', wordBreak: 'break-word', fontWeight: 500 }}>{l.valor_novo || '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: '11px', color: '#888' }}>{l.ip_origem || '—'}</td>
              </tr>
            ))}
            {!carregando && data.items.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhum registro</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px', alignItems: 'center' }}>
          <Btn size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Btn>
          <span style={{ fontSize: '12px', color: '#888' }}>Pág. {data.page}/{data.pages}</span>
          <Btn size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Próxima</Btn>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TABELAS DO IBPT — Lei 12.741/2012 (só admin)
// ============================================================
const UFS_IBPT = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

// ============================================================
// RELATÓRIOS DE CONFERÊNCIA (F0h, só admin) — mesmos critérios das planilhas
// do F0a; o CSV é gerado no navegador (nada é gravado no servidor).
// ============================================================
const RELATORIOS_CONFERENCIA = [
  { chave: 'notas-102', titulo: 'Notas com CSOSN 102 não escolhido', arquivo: 'notas_102_nao_escolhido',
    ajuda: 'Itens de notas autorizadas ou canceladas que saíram com CSOSN 102 sem que esse código estivesse no item (antes da correção de 23/10).' },
  { chave: 'produtos-codigo-invalido', titulo: 'Produtos com código inválido para o regime', arquivo: 'produtos_codigo_invalido',
    ajuda: 'Produtos ativos com CST/CSOSN que a emissão recusa para o regime da empresa, e CST de PIS/COFINS fora da tabela oficial.' },
];

function ConferenciaPage() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [rel, setRel] = useState(RELATORIOS_CONFERENCIA[0].chave);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const atual = RELATORIOS_CONFERENCIA.find(r => r.chave === rel);

  useEffect(() => { api.listarEmpresas().then(setEmpresas).catch(() => setEmpresas([])); }, []);
  useEffect(() => {
    let cancelado = false;
    setCarregando(true); setErro(null); setDados(null);
    api.relatorioConferencia(rel, empresaId)
      .then(d => { if (!cancelado) setDados(d); })
      .catch(e => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [rel, empresaId]);

  const exportar = () => {
    if (!dados) return;
    const blob = new Blob([paraCsv(dados.colunas, dados.linhas)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${atual.arquivo}${empresaId ? '_' + empresaId.slice(0, 8) : ''}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const th = { padding: '6px 8px', textAlign: 'left', color: '#666', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', borderTop: '1px solid #eee', verticalAlign: 'top' };
  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Relatórios de conferência</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>{atual.ajuda}</p>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ minWidth: '260px' }}>
          <Select label="Relatório" value={rel} onChange={e => setRel(e.target.value)}>
            {RELATORIOS_CONFERENCIA.map(r => <option key={r.chave} value={r.chave}>{r.titulo}</option>)}
          </Select>
        </div>
        <div style={{ minWidth: '260px' }}>
          <Select label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
            <option value="">Todas</option>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
          </Select>
        </div>
        <Btn onClick={exportar} disabled={!dados || dados.total === 0}>Exportar CSV</Btn>
      </div>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {carregando && <div style={{ fontSize: '13px', color: '#666' }}>Carregando...</div>}
      {dados && (
        <>
          <div style={{ fontSize: '13px', marginBottom: '8px' }}>{dados.total} linha(s).</div>
          {dados.total > 0 && (
            <div style={{ overflowX: 'auto', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead><tr>{dados.colunas.map(c => <th key={c} style={th}>{c}</th>)}</tr></thead>
                <tbody>
                  {dados.linhas.slice(0, 500).map((l, i) => (
                    <tr key={i}>{dados.colunas.map(c => <td key={c} style={td}>{l[c]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dados.total > 500 && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
              Mostrando 500 de {dados.total}; o CSV traz todas.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IbptPage() {
  const [dados, setDados] = useState({ ufs: [], versoes: [], chave_ligada: true });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [uf, setUf] = useState('SP');
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const inputRef = useRef(null);

  const carregar = useCallback(() => {
    setCarregando(true); setErro(null);
    api.listarTabelasIbpt()
      .then(setDados)
      .catch(err => setErro(err.message))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const limparEnvio = () => {
    setArquivo(null); setPrevia(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const gerarPrevia = async () => {
    setErro(null); setOk(null); setPrevia(null);
    if (!arquivo) { setErro('Escolha o arquivo da tabela do IBPT.'); return; }
    setEnviando(true);
    try {
      const { status, dados: d } = await api.enviarTabelaIbpt('previa', uf, arquivo);
      if (status === 200 || status === 422) setPrevia(d);
      else setErro(d.erro || `Erro ${status} ao conferir a tabela.`);
    } catch (e) { setErro(e.message); } finally { setEnviando(false); }
  };

  const confirmar = async () => {
    setErro(null); setOk(null);
    setEnviando(true);
    try {
      const { status, dados: d } = await api.enviarTabelaIbpt('importar', uf, arquivo, previa.sha256);
      if (status === 201) {
        setOk(`Tabela de ${d.uf} versão ${d.versao} carregada (${d.linhas} linhas).`);
        limparEnvio(); carregar();
      } else if (status === 422) {
        setPrevia(d);
      } else {
        setErro(d.erro || `Erro ${status} ao carregar a tabela.`);
      }
    } catch (e) { setErro(e.message); } finally { setEnviando(false); }
  };

  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarTabelaIbpt(desativando.id, motivo);
      setOk(`Tabela de ${desativando.uf} versão ${desativando.versao} desativada.`);
      setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px' };

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Tabelas do IBPT — Lei da Transparência</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        Valor aproximado dos tributos nas notas a consumidor final (Lei 12.741/2012). Baixe o arquivo
        de cada UF no site do IBPT ("De Olho no Imposto") e carregue aqui. Assim que houver tabela vigente
        da UF da empresa, as notas a consumidor final passam a levar o valor; com NCM fora da tabela, a
        nota não sai. Sem tabela vigente (ou vencida), as notas saem sem o valor.
      </p>
      {!dados.chave_ligada && (
        <Alerta tipo="info">
          A Lei da Transparência está desligada no emissor (desligamento de emergência): nenhuma nota
          recebe o valor aproximado dos tributos, mesmo com tabela carregada.
        </Alerta>
      )}
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}

      {dados.ufs.filter(u => u.alerta).map(u => (
        <Alerta key={u.uf} tipo="aviso">{u.alerta} ({u.empresas} empresa{u.empresas === 1 ? '' : 's'} desta UF)</Alerta>
      ))}

      <div style={{ border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 12px' }}>Carregar tabela</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: '90px' }}>
            <Select label="UF" value={uf} onChange={e => { setUf(e.target.value); setPrevia(null); }}>
              {UFS_IBPT.map(u => <option key={u} value={u}>{u}</option>)}
            </Select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Arquivo (.csv do IBPT)</label>
            <input ref={inputRef} type="file" accept=".csv,text/csv"
              onChange={e => { setArquivo(e.target.files?.[0] || null); setPrevia(null); }} />
          </div>
          <Btn onClick={gerarPrevia} disabled={!arquivo || enviando}>{enviando && !previa ? 'Conferindo...' : 'Conferir (prévia)'}</Btn>
        </div>

        {previa && (
          <div style={{ marginTop: '16px' }}>
            {previa.erros?.length > 0 && (
              <Alerta tipo="erro">
                <strong>A tabela não pode ser carregada:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  {previa.erros.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </Alerta>
            )}
            {previa.avisos?.length > 0 && (
              <Alerta tipo="aviso">
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  {previa.avisos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </Alerta>
            )}
            {previa.ok && (
              <div style={{ fontSize: '13px' }}>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>{previa.uf}</strong> · versão <strong>{previa.versao}</strong> · chave {previa.chave} · fonte {previa.fonte}<br />
                  Vigência: <strong>{fmtData(previa.vigencia_inicio)} a {fmtData(previa.vigencia_fim)}</strong><br />
                  {previa.linhas} linhas: {previa.linhas_ncm} NCM, {previa.linhas_nbs} NBS, {previa.linhas_lc116} LC 116
                </p>
                <table style={{ borderCollapse: 'collapse', fontSize: '12px', marginBottom: '12px' }}>
                  <thead>
                    <tr style={{ background: '#fafaf8' }}>
                      <th style={th}>NCM</th><th style={th}>Descrição</th><th style={th}>Federal nac.</th>
                      <th style={th}>Federal imp.</th><th style={th}>Estadual</th><th style={th}>Municipal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previa.amostra.map(a => (
                      <tr key={a.ncm + a.ex} style={{ borderBottom: '1px solid #f5f5f3' }}>
                        <td style={td}>{a.ncm}{a.ex ? ` ex ${a.ex}` : ''}</td>
                        <td style={{ ...td, maxWidth: '260px' }}>{a.descricao}</td>
                        <td style={td}>{a.nacional}%</td><td style={td}>{a.importada}%</td>
                        <td style={td}>{a.estadual}%</td><td style={td}>{a.municipal}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Btn onClick={confirmar} disabled={!podeConfirmar(previa, uf, arquivo) || enviando}>
                    {enviando ? 'Carregando...' : 'Confirmar e carregar'}
                  </Btn>
                  <Btn variant="outline" onClick={limparEnvio} disabled={enviando}>Cancelar</Btn>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '15px', margin: '0 0 8px' }}>Versões carregadas</h3>
      <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
              <th style={th}>UF</th><th style={th}>Versão</th><th style={th}>Vigência</th><th style={th}>Situação</th>
              <th style={th}>Linhas</th><th style={th}>Carregada</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {dados.versoes.map(v => (
              <tr key={v.id} style={{ borderBottom: '1px solid #f5f5f3' }}>
                <td style={td}>{v.uf}</td>
                <td style={td}>{v.versao}<br /><span style={{ fontSize: '11px', color: '#888' }}>chave {v.chave}</span></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(v.vigencia_inicio)} a {fmtData(v.vigencia_fim)}</td>
                <td style={{ ...td, color: corSituacao(v), fontWeight: 600 }}>
                  {ROTULO_SITUACAO[v.situacao] || v.situacao}
                  {v.em_uso && <span style={{ fontWeight: 400, color: '#666' }}> · em uso hoje</span>}
                  {v.vence_em_breve && <div style={{ fontWeight: 400 }}>vence em {v.dias_para_vencer} dia(s)</div>}
                  {v.situacao === 'desativada' && (
                    <div style={{ fontWeight: 400, color: '#888' }}>
                      {v.desativado_por_email || '—'} em {fmtData(v.desativado_em)}: {v.motivo_desativacao}
                    </div>
                  )}
                </td>
                <td style={td}>{v.linhas_ncm} NCM</td>
                <td style={td}>{fmtData(v.data_importacao)}<br /><span style={{ fontSize: '11px', color: '#888' }}>{v.importado_por_email || '—'}</span></td>
                <td style={td}>
                  {v.ativo && <Btn size="sm" variant="outline" onClick={() => { setDesativando(v); setMotivo(''); }}>Desativar</Btn>}
                </td>
              </tr>
            ))}
            {!carregando && dados.versoes.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhuma tabela carregada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal titulo="Desativar tabela do IBPT" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}>
              Tabela de <strong>{desativando.uf}</strong>, versão <strong>{desativando.versao}</strong>. A tabela
              fica no histórico, mas deixa de ser usada nas próximas notas.
              {desativando.em_uso && ' Ela está em uso hoje: sem outra tabela vigente, as notas a consumidor final desta UF voltam a sair sem o valor aproximado dos tributos.'}
            </p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={!motivo.trim()}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ============================================================
// CRÉDITO DO SIMPLES — percentual por mês (F0e, só admin)
// ============================================================
function CreditoSnPage() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [linhas, setLinhas] = useState([]);
  const [form, setForm] = useState({ competencia: '', aliquota: '', memoria: '' });
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    api.listarEmpresas().then(l => setEmpresas((l || []).filter(e => Number(e.regime_tributario) === 1)))
      .catch(() => setEmpresas([]));
  }, []);
  const carregar = useCallback(() => {
    if (!empresaId) { setLinhas([]); return; }
    api.listarCreditoSn(empresaId).then(d => setLinhas(d.linhas || [])).catch(e => setErro(e.message));
  }, [empresaId]);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro(null); setOk(null);
    try {
      const d = await api.criarCreditoSn({ empresa_id: empresaId, ...form });
      setOk(`Percentual de ${d.aliquota}% cadastrado para ${d.competencia}.`);
      setForm({ competencia: '', aliquota: '', memoria: '' }); carregar();
    } catch (e) { setErro(e.message); }
  };
  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarCreditoSn(desativando.id, motivo);
      setOk(`Percentual de ${desativando.competencia} desativado.`);
      setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px' };

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Crédito do ICMS do Simples Nacional</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        Nas notas com CSOSN 101 ou 201, a empresa do Simples informa ao cliente o crédito de ICMS que ele
        pode aproveitar. O percentual muda todo mês, conforme a faixa de receita do mês anterior (LC 123/2006,
        art. 23; Resolução CGSN 140/2018, art. 60). Cadastre aqui o percentual de cada mês, com a memória de
        cálculo. Sem percentual para o mês, a emissão com 101/201 mostra a pendência "procure o escritório".
        O MEI não dá crédito.
      </p>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}
      <Select label="Empresa (Simples Nacional)" value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
        <option value="">— Selecione —</option>
        {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
      </Select>
      {empresaId && (
        <>
          <div style={{ border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', margin: '12px 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <Input label="Mês da operação" type="month" value={form.competencia} onChange={e => setForm({ ...form, competencia: e.target.value })} />
              <Input label="Percentual de crédito (%)" type="number" step="0.01" value={form.aliquota} onChange={e => setForm({ ...form, aliquota: e.target.value })} />
            </div>
            <Textarea label="Memória de cálculo (RBT12, faixa, alíquota nominal, parcela a deduzir, percentual do ICMS)" rows={2}
              value={form.memoria} onChange={e => setForm({ ...form, memoria: e.target.value })} />
            <Btn onClick={salvar}>Cadastrar percentual</Btn>
          </div>
          <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
                  <th style={th}>Mês</th><th style={th}>Percentual</th><th style={th}>Memória</th><th style={th}>Situação</th><th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f3', color: l.ativo ? undefined : '#999' }}>
                    <td style={td}>{l.competencia}</td>
                    <td style={td}>{l.aliquota}%</td>
                    <td style={{ ...td, maxWidth: '320px' }}>{l.memoria}<br /><span style={{ fontSize: '11px', color: '#888' }}>{l.criado_por_email || '—'}</span></td>
                    <td style={td}>{l.ativo ? 'ativo' : `desativado: ${l.motivo_desativacao}`}</td>
                    <td style={td}>{l.ativo && <Btn size="sm" variant="outline" onClick={() => { setDesativando(l); setMotivo(''); }}>Desativar</Btn>}</td>
                  </tr>
                ))}
                {linhas.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhum percentual cadastrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <Modal titulo="Desativar percentual" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}>Percentual de <strong>{desativando.competencia}</strong>. Fica no histórico, mas deixa de valer nas próximas notas.</p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={motivo.trim().length < 5}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ============================================================
// ALÍQUOTAS OFICIAIS do IBS e da CBS (C2, só admin)
// ============================================================
const TRIBUTOS_OFICIAIS = [
  { valor: 'CBS', rotulo: 'CBS (federal)' },
  { valor: 'IBS_UF', rotulo: 'IBS da UF' },
  { valor: 'IBS_MUN', rotulo: 'IBS do município' },
];
const ALIQ_OFICIAL_VAZIA = { tributo: 'CBS', uf: '', codigo_municipio: '', p_aliquota: '', origem_valor: 'texto_de_lei',
  d_ini_vig: '', d_fim_vig: '', fonte: '', link_fonte: '', observacao: '' };
const TRIBUTO_DA_LINHA = { federal: 'CBS', estadual: 'IBS_UF', municipal: 'IBS_MUN' };

function AliquotasOficiaisPage() {
  const [dados, setDados] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [form, setForm] = useState(ALIQ_OFICIAL_VAZIA);
  const [substituindo, setSubstituindo] = useState(null);   // linha que será substituída
  const [motivoSubst, setMotivoSubst] = useState('');
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);

  const carregar = useCallback(() => {
    api.listarAliquotasOficiais().then(setDados).catch(e => setErro(e.message));
    api.historicoAliquotasOficiais().then(d => setHistorico(d.historico || [])).catch(() => setHistorico([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro(null); setOk(null);
    try {
      const corpo = { ...form, p_aliquota: form.origem_valor === 'sem_fixacao' ? '' : form.p_aliquota };
      const d = substituindo
        ? await api.substituirAliquotaOficial(substituindo.id, { ...corpo, motivo: motivoSubst })
        : await api.criarAliquotaOficial(corpo);
      setOk(`${d.rotulo}: ${d.p_aliquota === null ? 'aguardando publicação' : `${d.p_aliquota}%`} a partir de ${fmtData(d.d_ini_vig)} ${substituindo ? '(substituiu a anterior)' : 'cadastrada'}.`);
      setForm(ALIQ_OFICIAL_VAZIA); setSubstituindo(null); setMotivoSubst(''); carregar();
    } catch (e) { setErro(e.message); }
  };
  const iniciarSubstituicao = (l) => {
    setErro(null); setOk(null); setSubstituindo(l); setMotivoSubst('');
    setForm({ tributo: TRIBUTO_DA_LINHA[l.esfera] || 'CBS', uf: l.uf || '', codigo_municipio: l.codigo_municipio || '',
      p_aliquota: l.p_aliquota || '', origem_valor: l.origem_valor, d_ini_vig: l.d_ini_vig || '', d_fim_vig: l.d_fim_vig || '',
      fonte: l.fonte || '', link_fonte: l.link_fonte || '', observacao: l.observacao || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarAliquotaOficial(desativando.id, motivo);
      setOk(`${desativando.rotulo} de ${fmtData(desativando.d_ini_vig)} desativada.`);
      setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px', verticalAlign: 'top' };
  const linhas = dados?.linhas || [];
  const semPercentual = form.origem_valor === 'sem_fixacao';

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Alíquotas oficiais do IBS e da CBS</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        As notas das empresas obrigadas calculam o IBS e a CBS com a alíquota vigente na <strong>data de emissão</strong>.
        Cadastre aqui cada alíquota oficial com a norma de origem e a vigência. Nada é apagado: para corrigir, use
        <strong> Substituir</strong> (a linha antiga fica no histórico, desativada). Sem alíquota oficial para a data,
        a nota da empresa obrigada mostra a pendência "procure o escritório" — o sistema nunca calcula com zero.
      </p>
      {dados?.aviso_2027 && <Alerta tipo="aviso"><strong>CBS 2027 pendente de publicação.</strong> {dados.aviso_2027}</Alerta>}
      {(dados?.cobertura || []).map(a => (
        <Alerta key={a.componente} tipo="erro">{a.mensagem}</Alerta>
      ))}
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}

      <div style={{ border: `1px solid ${substituindo ? '#F59E0B' : cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', margin: '12px 0' }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
          {substituindo ? `Substituir: ${substituindo.rotulo} de ${fmtData(substituindo.d_ini_vig)}` : 'Nova alíquota oficial'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
          <Select label="Tributo" value={form.tributo} onChange={e => setForm({ ...form, tributo: e.target.value })}>
            {TRIBUTOS_OFICIAIS.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </Select>
          <Input label="UF (vazio = todas)" value={form.uf} maxLength={2} disabled={form.tributo === 'CBS'}
            onChange={e => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
          <Input label="Município IBGE (vazio = todos)" value={form.codigo_municipio} maxLength={7} disabled={form.tributo !== 'IBS_MUN'}
            onChange={e => setForm({ ...form, codigo_municipio: e.target.value.replace(/\D/g, '') })} />
          <Select label="Origem do valor" value={form.origem_valor} onChange={e => setForm({ ...form, origem_valor: e.target.value })}>
            {Object.entries(dados?.origens || { texto_de_lei: 'Texto de lei, resolução ou NT' }).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Input label="Percentual (%), até 4 casas" type="number" step="0.0001" min="0" max="100" value={semPercentual ? '' : form.p_aliquota}
            disabled={semPercentual} onChange={e => setForm({ ...form, p_aliquota: e.target.value })} />
          <div />
          <Input label="Vigência: início" type="date" value={form.d_ini_vig} onChange={e => setForm({ ...form, d_ini_vig: e.target.value })} />
          <Input label="Vigência: fim (opcional)" type="date" value={form.d_fim_vig} onChange={e => setForm({ ...form, d_fim_vig: e.target.value })} />
        </div>
        <Input label="Norma de origem (obrigatória — ex.: LC 214/2025, art. 343)" value={form.fonte} onChange={e => setForm({ ...form, fonte: e.target.value })} />
        <Input label="Link da fonte (opcional)" value={form.link_fonte} onChange={e => setForm({ ...form, link_fonte: e.target.value })} />
        <Textarea label="Observação (opcional)" rows={2} value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })} />
        {substituindo && (
          <Textarea label="Motivo da substituição (obrigatório)" rows={2} value={motivoSubst} onChange={e => setMotivoSubst(e.target.value)} />
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={salvar} disabled={substituindo && motivoSubst.trim().length < 5}>{substituindo ? 'Substituir alíquota' : 'Cadastrar alíquota'}</Btn>
          {substituindo && <Btn variant="outline" onClick={() => { setSubstituindo(null); setForm(ALIQ_OFICIAL_VAZIA); }}>Cancelar</Btn>}
        </div>
      </div>

      <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
              <th style={th}>Tributo</th><th style={th}>Local</th><th style={th}>Percentual</th><th style={th}>Vigência</th>
              <th style={th}>Norma de origem</th><th style={th}>Situação</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f3', color: l.ativo ? undefined : '#999' }}>
                <td style={td}>{l.rotulo}</td>
                <td style={td}>{l.codigo_municipio ? `${l.uf} / ${l.codigo_municipio}` : (l.uf || 'Todos')}</td>
                <td style={td}>{l.p_aliquota === null ? <em>aguardando publicação</em> : `${l.p_aliquota}%`}</td>
                <td style={td}>{fmtData(l.d_ini_vig)} a {l.d_fim_vig ? fmtData(l.d_fim_vig) : 'sem fim'}</td>
                <td style={{ ...td, maxWidth: '340px' }}>
                  {l.fonte}{l.link_fonte && <> — <a href={l.link_fonte} target="_blank" rel="noreferrer">link</a></>}
                  <br /><span style={{ fontSize: '11px', color: '#888' }}>{l.criado_por_email || 'carga inicial'} · {fmtData(l.data_importacao)}</span>
                </td>
                <td style={td}>{l.ativo ? 'ativa' : `desativada: ${l.motivo_desativacao || ''}`}</td>
                <td style={td}>
                  {l.ativo && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Btn size="sm" variant="outline" onClick={() => iniciarSubstituicao(l)}>Substituir</Btn>
                      <Btn size="sm" variant="outline" onClick={() => { setDesativando(l); setMotivo(''); }}>Desativar</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {linhas.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhuma alíquota cadastrada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {historico.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Histórico de alterações</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: '#fff', border: `1px solid ${cores.cinzaBorda}` }}>
            <thead><tr style={{ background: '#fafaf8' }}><th style={th}>Quando</th><th style={th}>Quem</th><th style={th}>Ação</th><th style={th}>Antes</th><th style={th}>Depois</th><th style={th}>Motivo</th></tr></thead>
            <tbody>
              {historico.map(h => (
                <tr key={h.id} style={{ borderTop: '1px solid #f5f5f3' }}>
                  <td style={td}>{fmtData(h.quando)}</td>
                  <td style={td}>{h.usuario_email || '—'}</td>
                  <td style={td}>{{ criacao: 'Cadastro', desativacao: 'Desativação', substituicao: 'Substituição' }[h.acao] || h.acao}</td>
                  <td style={td}>{h.anterior ? `${h.anterior.rotulo} ${h.anterior.p_aliquota ?? 'aguardando'}% desde ${fmtData(h.anterior.d_ini_vig)}` : '—'}</td>
                  <td style={td}>{h.novo ? `${h.novo.rotulo} ${h.novo.p_aliquota ?? 'aguardando'}% desde ${fmtData(h.novo.d_ini_vig)}${h.novo.ativo ? '' : ' (desativada)'}` : '—'}</td>
                  <td style={td}>{h.motivo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal titulo="Desativar alíquota" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}><strong>{desativando.rotulo}</strong> de {fmtData(desativando.d_ini_vig)}. Fica no histórico, mas deixa de valer
              nas próximas notas. Sem outra alíquota para o período, as notas das empresas obrigadas ficam com pendência.</p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={motivo.trim().length < 5}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// SIMPLES 2027 — alíquota efetiva do IBS e da CBS por mês (NT 2026.005, só admin)
// ============================================================
// Opção do Simples pelo regime regular do IBS/CBS (C1b): LC 214/2025, art. 41, § 3º; LC 123/2006,
// art. 13, §§ 9º a 11 (semestres de janeiro e julho, irretratável); efeitos a partir de 01/01/2027.
function OpcaoRegimeRegular({ empresaId }) {
  const [linhas, setLinhas] = useState([]);
  const [form, setForm] = useState({ vigencia_inicio: '', vigencia_fim: '', inicio_atividade: false, comprovante: '' });
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const carregar = useCallback(() => {
    api.listarOpcaoRegimeRegular(empresaId).then(d => setLinhas(d.linhas || [])).catch(e => setErro(e.message));
  }, [empresaId]);
  useEffect(() => { carregar(); }, [carregar]);
  const salvar = async () => {
    setErro(null); setOk(null);
    try {
      const d = await api.criarOpcaoRegimeRegular({ empresa_id: empresaId, ...form });
      setOk(`Opção pelo regime regular cadastrada a partir de ${fmtData(d.vigencia_inicio)}.`);
      setForm({ vigencia_inicio: '', vigencia_fim: '', inicio_atividade: false, comprovante: '' }); carregar();
    } catch (e) { setErro(e.message); }
  };
  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarOpcaoRegimeRegular(desativando.id, motivo);
      setOk('Opção desativada.'); setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px', verticalAlign: 'top' };
  return (
    <div style={{ border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', margin: '16px 0' }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Opção pelo regime regular do IBS/CBS</div>
      <p style={{ fontSize: '12px', color: '#666', marginTop: 0 }}>
        O optante pelo Simples pode apurar o IBS e a CBS pelo regime regular (LC 214/2025, art. 41, § 3º). A opção é feita
        no Portal do Simples Nacional, por semestre (janeiro a junho ou julho a dezembro), é irretratável no semestre e só
        produz efeitos a partir de 01/01/2027 (LC 123/2006, art. 13, §§ 9º e 10; LC 214, art. 544, III). Informe aqui a opção
        feita, com o comprovante. Com a opção, a nota passa a levar o IBS e a CBS como no regime normal, com as alíquotas
        oficiais e a classificação de cada produto — quando o schema oficial dos campos novos sair; até lá as notas
        continuam como hoje. ICMS, CSOSN e as expressões do Simples não mudam.
      </p>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
        <Input label="Início (1º de janeiro ou de julho)" type="date" value={form.vigencia_inicio} onChange={e => setForm({ ...form, vigencia_inicio: e.target.value })} />
        <Input label="Fim (30/06 ou 31/12; vazio = até nova decisão)" type="date" value={form.vigencia_fim} onChange={e => setForm({ ...form, vigencia_fim: e.target.value })} />
        <label style={{ fontSize: 12, color: '#555', display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
          <input type="checkbox" checked={form.inicio_atividade} onChange={e => setForm({ ...form, inicio_atividade: e.target.checked })} />
          Início de atividade (começa na data de início)
        </label>
      </div>
      <Input label="Comprovante (recibo/protocolo da opção no Portal do Simples Nacional)" value={form.comprovante} onChange={e => setForm({ ...form, comprovante: e.target.value })} />
      <Btn onClick={salvar}>Cadastrar opção</Btn>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginTop: 12 }}>
        <thead><tr style={{ background: '#fafaf8' }}><th style={th}>Vigência</th><th style={th}>Comprovante</th><th style={th}>Situação</th><th style={th}></th></tr></thead>
        <tbody>
          {linhas.map(l => (
            <tr key={l.id} style={{ borderTop: '1px solid #f5f5f3', color: l.ativo ? undefined : '#999' }}>
              <td style={td}>{fmtData(l.vigencia_inicio)} a {l.vigencia_fim ? fmtData(l.vigencia_fim) : 'sem fim'}{l.inicio_atividade ? ' (início de atividade)' : ''}</td>
              <td style={td}>{l.comprovante}<br /><span style={{ fontSize: '11px', color: '#888' }}>{l.criado_por_email || '—'} · {fmtData(l.created_at)}</span></td>
              <td style={td}>{l.ativo ? 'ativa' : `desativada por ${l.desativado_por_email || '—'}: ${l.motivo_desativacao}`}</td>
              <td style={td}>{l.ativo && <Btn size="sm" variant="outline" onClick={() => { setDesativando(l); setMotivo(''); }}>Desativar</Btn>}</td>
            </tr>
          ))}
          {linhas.length === 0 && <tr><td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#aaa' }}>Nenhuma opção cadastrada</td></tr>}
        </tbody>
      </table>
      <Modal titulo="Desativar opção pelo regime regular" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}>Fica no histórico, mas deixa de valer. Use só se a opção não foi feita ou foi cancelada no Portal.</p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={motivo.trim().length < 5}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Simples2027Page() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [linhas, setLinhas] = useState([]);
  const [form, setForm] = useState({ competencia: '', p_ibs_sn: '', p_cbs_sn: '', memoria: '' });
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    // gTribSN (alíquota efetiva): CRT 1 e 2 sem regime regular, e CRT 3 com opção pendente
    // tipo 2 ou 3 (NT p. 6). O servidor confere de novo ao gravar.
    api.listarEmpresas().then(l => setEmpresas((l || []).filter(e => {
      const crt = Number(e.regime_tributario);
      return ((crt === 1 || crt === 2) && Number(e.regime_apuracao_ibscbs) !== 2)
        || (crt === 3 && [2, 3].includes(Number(e.tp_regime_ibscbs)));
    }))).catch(() => setEmpresas([]));
  }, []);
  const carregar = useCallback(() => {
    if (!empresaId) { setLinhas([]); return; }
    api.listarAliquotaEfetivaSn(empresaId).then(d => setLinhas(d.linhas || [])).catch(e => setErro(e.message));
  }, [empresaId]);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro(null); setOk(null);
    try {
      const d = await api.criarAliquotaEfetivaSn({ empresa_id: empresaId, ...form });
      setOk(`Alíquota efetiva de ${d.competencia} cadastrada (IBS ${d.p_ibs_sn}% · CBS ${d.p_cbs_sn}%).`);
      setForm({ competencia: '', p_ibs_sn: '', p_cbs_sn: '', memoria: '' }); carregar();
    } catch (e) { setErro(e.message); }
  };
  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarAliquotaEfetivaSn(desativando.id, motivo);
      setOk(`Alíquota de ${desativando.competencia} desativada.`);
      setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px', verticalAlign: 'top' };

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Simples Nacional em 2027 — alíquota efetiva do IBS e da CBS</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        A partir de 2027, a nota da empresa do Simples informa a alíquota efetiva do IBS e da CBS (NT 2026.005, p. 9).
        O escritório informa aqui o valor apurado de cada mês, com a memória de cálculo. <strong>O sistema não calcula
        a fórmula</strong>: a NT não a traz. Nada é apagado: para corrigir, desative e cadastre de novo.
      </p>
      <Alerta tipo="info">
        Para o operador nada muda em 2026. Em 2027, enquanto o schema oficial dos campos novos não for publicado, as notas
        do Simples saem sem o grupo do IBS/CBS; estes dados (e a atividade do Simples no cadastro de cada produto) já ficam
        prontos para quando ele sair. A atividade do Simples (cAtivSN) fica no cadastro do produto; a opção pendente pelo
        Simples das empresas do Regime Normal, no cadastro da empresa.
      </Alerta>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}
      <Select label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
        <option value="">— Selecione —</option>
        {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
      </Select>
      {empresaId && [1, 2].includes(Number((empresas.find(e => e.id === empresaId) || {}).regime_tributario)) && (
        <OpcaoRegimeRegular empresaId={empresaId} />
      )}
      {empresaId && (
        <>
          <div style={{ border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', margin: '12px 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
              <Input label="Mês da operação" type="month" value={form.competencia} onChange={e => setForm({ ...form, competencia: e.target.value })} />
              <Input label="Alíquota efetiva do IBS (%)" type="number" step="0.0001" value={form.p_ibs_sn} onChange={e => setForm({ ...form, p_ibs_sn: e.target.value })} />
              <Input label="Alíquota efetiva da CBS (%)" type="number" step="0.0001" value={form.p_cbs_sn} onChange={e => setForm({ ...form, p_cbs_sn: e.target.value })} />
            </div>
            <Textarea label="Memória de cálculo (como o valor foi apurado)" rows={2}
              value={form.memoria} onChange={e => setForm({ ...form, memoria: e.target.value })} />
            <Btn onClick={salvar}>Cadastrar alíquota do mês</Btn>
          </div>
          <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
                  <th style={th}>Mês</th><th style={th}>IBS</th><th style={th}>CBS</th><th style={th}>Memória</th><th style={th}>Situação</th><th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f3', color: l.ativo ? undefined : '#999' }}>
                    <td style={td}>{l.competencia}</td>
                    <td style={td}>{l.p_ibs_sn}%</td>
                    <td style={td}>{l.p_cbs_sn}%</td>
                    <td style={{ ...td, maxWidth: '320px' }}>{l.memoria}<br /><span style={{ fontSize: '11px', color: '#888' }}>{l.criado_por_email || '—'} · {fmtData(l.created_at)}</span></td>
                    <td style={td}>{l.ativo ? 'ativa' : `desativada por ${l.desativado_por_email || '—'}: ${l.motivo_desativacao}`}</td>
                    <td style={td}>{l.ativo && <Btn size="sm" variant="outline" onClick={() => { setDesativando(l); setMotivo(''); }}>Desativar</Btn>}</td>
                  </tr>
                ))}
                {linhas.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhuma alíquota cadastrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <Modal titulo="Desativar alíquota do mês" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}>Alíquota de <strong>{desativando.competencia}</strong>. Fica no histórico, mas deixa de valer.</p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={motivo.trim().length < 5}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// TABELA DO DIFAL — venda a consumidor final de outro estado (F0c, só admin)
// ============================================================
const LINHA_DIFAL_VAZIA = { uf: 'RJ', ncm_prefixo: '', aliquota_interna: '', aliquota_fcp: '',
  metodo: 'unica', vigencia_inicio: '', vigencia_fim: '', fonte: '' };

function DifalPage() {
  const [linhas, setLinhas] = useState([]);
  const [form, setForm] = useState(LINHA_DIFAL_VAZIA);
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [desativando, setDesativando] = useState(null);
  const [motivo, setMotivo] = useState('');

  const carregar = useCallback(() => {
    api.listarDifal().then(d => setLinhas(d.linhas || [])).catch(e => setErro(e.message));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro(null); setOk(null); setSalvando(true);
    try {
      const d = await api.criarLinhaDifal(form);
      setOk(`Linha de ${d.uf}${d.ncm_prefixo ? ` (NCM ${d.ncm_prefixo}...)` : ''} cadastrada.`);
      setForm(LINHA_DIFAL_VAZIA); carregar();
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  };

  const desativar = async () => {
    setErro(null); setOk(null);
    try {
      await api.desativarLinhaDifal(desativando.id, motivo);
      setOk(`Linha de ${desativando.uf} desativada.`);
      setDesativando(null); setMotivo(''); carregar();
    } catch (e) { setErro(e.message); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', color: '#666' };
  const td = { padding: '8px 10px' };
  const campo = (k) => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '4px' }}>Tabela do DIFAL — venda a consumidor final de outro estado</h2>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        Nas empresas do Regime Normal, a venda para outro estado a quem não tem Inscrição Estadual leva o
        ICMS da UF de destino (DIFAL). O emissor não decide a alíquota: cadastre aqui a alíquota interna e o
        FCP de cada UF de destino, com a fonte (lei ou página da SEFAZ da UF) e a vigência. Sem linha vigente
        para a UF, a emissão mostra a pendência "procure o escritório". Uma linha com prefixo de NCM vale só
        para os produtos que começam com ele e tem prioridade sobre a regra geral da UF.
      </p>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {ok && <Alerta tipo="sucesso">{ok}</Alerta>}

      <div style={{ border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 12px' }}>Nova linha</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px' }}>
          <Select label="UF de destino" {...campo('uf')}>
            {UFS_IBPT.map(u => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Input label="Prefixo de NCM (vazio = geral)" maxLength={8} {...campo('ncm_prefixo')} />
          <Input label="Alíquota interna (%)" type="number" step="0.01" {...campo('aliquota_interna')} />
          <Input label="FCP da UF (%, até 2)" type="number" step="0.01" {...campo('aliquota_fcp')} />
          <Select label="Base de cálculo" {...campo('metodo')}>
            <option value="unica">Única (valor da operação)</option>
            <option value="dupla">Dupla ("por dentro") — ainda não gerada</option>
          </Select>
          <Input label="Vigência: início" type="date" {...campo('vigencia_inicio')} />
          <Input label="Vigência: fim (opcional)" type="date" {...campo('vigencia_fim')} />
        </div>
        <Textarea label="Fonte (obrigatória: norma da UF ou página da SEFAZ, com a data da consulta)" rows={2} {...campo('fonte')} />
        <Btn onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Cadastrar linha'}</Btn>
      </div>

      <div style={{ overflowX: 'auto', background: '#fff', border: `1px solid ${cores.cinzaBorda}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#fafaf8', borderBottom: `1px solid ${cores.cinzaBorda}` }}>
              <th style={th}>UF</th><th style={th}>NCM</th><th style={th}>Alíquota</th><th style={th}>FCP</th>
              <th style={th}>Base</th><th style={th}>Vigência</th><th style={th}>Fonte</th><th style={th}>Situação</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f3', color: l.ativo ? undefined : '#999' }}>
                <td style={td}>{l.uf}</td>
                <td style={td}>{l.ncm_prefixo ? `${l.ncm_prefixo}...` : 'geral'}</td>
                <td style={td}>{l.aliquota_interna}%</td>
                <td style={td}>{l.aliquota_fcp != null ? `${l.aliquota_fcp}%` : '—'}</td>
                <td style={td}>{l.metodo === 'unica' ? 'única' : 'dupla'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(l.vigencia_inicio)} a {l.vigencia_fim ? fmtData(l.vigencia_fim) : '—'}</td>
                <td style={{ ...td, maxWidth: '260px' }}>{l.fonte}<br /><span style={{ fontSize: '11px', color: '#888' }}>{l.criado_por_email || '—'}</span></td>
                <td style={td}>
                  {l.ativo ? 'ativa' : (
                    <span>desativada<br /><span style={{ fontSize: '11px' }}>{l.desativado_por_email || '—'} em {fmtData(l.desativado_em)}: {l.motivo_desativacao}</span></span>
                  )}
                </td>
                <td style={td}>
                  {l.ativo && <Btn size="sm" variant="outline" onClick={() => { setDesativando(l); setMotivo(''); }}>Desativar</Btn>}
                </td>
              </tr>
            ))}
            {linhas.length === 0 && (
              <tr><td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Nenhuma linha cadastrada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal titulo="Desativar linha do DIFAL" aberto={!!desativando} onFechar={() => setDesativando(null)}>
        {desativando && (
          <div>
            <p style={{ fontSize: '13px' }}>
              Linha de <strong>{desativando.uf}</strong>{desativando.ncm_prefixo ? ` (NCM ${desativando.ncm_prefixo}...)` : ''}. Ela fica no
              histórico, mas deixa de ser usada nas próximas notas.
            </p>
            <Textarea label="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn variant="danger" onClick={desativar} disabled={motivo.trim().length < 5}>Desativar</Btn>
              <Btn variant="outline" onClick={() => setDesativando(null)}>Cancelar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ============================================================
// AJUDA — Guia do Operador
// ============================================================
function AjudaPage() {
  const [aberta, setAberta] = useState(null);
  const [busca, setBusca] = useState('');
  const toggle = (i) => setAberta(aberta === i ? null : i);

  // Normaliza: lowercase + remove acentos. Garante que "cedula" == "cédula".
  const normalize = (s) => (s == null ? '' : s.toString())
    .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // 8 accordions principais + 6 FAQ (separados por uma label visual antes do FAQ).
  const FAQ_OFFSET = 8;
  const secoes = [
    {
      titulo: '📊 Dashboard',
      conteudo: [
        'Tela inicial com visão geral das operações da empresa ativa:',
        '',
        '• Total de NF-e Autorizadas no mês',
        '• Total de NF-e Canceladas no mês',
        '• Faturamento do mês (soma dos valores das notas autorizadas)',
        '• Total de Clientes cadastrados (e quantidade de produtos)',
        '• Lista das últimas 5-10 notas emitidas para acesso rápido',
        '• Cabeçalho mostra o nome da empresa ativa em destaque',
        '',
        '💡 Use o Dashboard como ponto de partida do dia: confira o que foi emitido, abra uma nota para corrigir, ou vá ao menu "NF-e" para começar uma emissão nova.',
      ]
    },
    {
      titulo: '🏢 Minha Empresa',
      conteudo: [
        'Tela onde cada operador edita os dados da própria empresa emitente.',
        '',
        '📌 CAMPOS DO CADASTRO:',
        '• Razão Social, Nome Fantasia, CNPJ, Inscrição Estadual, CNAE',
        '• Regime Tributário (Simples Nacional, Lucro Presumido, Lucro Real, MEI)',
        '• Ambiente (Produção ou Homologação)',
        '• Endereço completo: Logradouro, Número, Complemento, Bairro',
        '• Município (autocompleta via API IBGE) + UF + Código IBGE preenchido automaticamente',
        '• CEP, Telefone, E-mail',
        '• Próximo número de NF-e e série padrão',
        '',
        '🔐 CERTIFICADO DIGITAL A1:',
        '• Upload do arquivo .pfx com a senha',
        '• Validação automática: o CNPJ do certificado precisa bater com o CNPJ da empresa (proteção contra subir certificado errado)',
        '• Validação automática: a senha precisa estar correta',
        '• Senha armazenada criptografada (Fernet) — nunca em texto puro',
        '• A tela mostra a validade do certificado e quantos dias faltam para vencer',
        '• 🟡 Banner amarelo aparece 30 dias antes do vencimento — programe a renovação',
        '• 🔴 Banner vermelho e bloqueio de emissão quando o certificado vence — não emite até subir um novo',
        '',
        '⚠️ ATENÇÃO: alterações em campos críticos (CNPJ, IE, Razão Social, Regime, Ambiente) exibem dialog de confirmação. Recomenda-se confirmar com o contador antes de alterar.',
        '',
        '💡 EXEMPLO PRÁTICO: seu certificado vence em 20 dias. Aparece o banner amarelo em Minha Empresa. Vá em Certificado Digital → "Substituir certificado" → upload do novo .pfx + senha. Pronto: você continua emitindo sem interrupção.',
      ]
    },
    {
      titulo: '👥 Cadastrar Cliente',
      conteudo: [
        'Menu Clientes → Novo Cliente. Campos obrigatórios:',
        '',
        '• CNPJ ou CPF (formatação automática)',
        '• Razão Social ou Nome',
        '• Nome Fantasia (opcional)',
        '• Inscrição Estadual: digite somente números, sem pontos ou traços. Para isento deixe em branco ou use "ISENTO"',
        '• Endereço completo',
        '• Município (autocomplete via API IBGE) + UF + Código IBGE preenchido automaticamente',
        '• CEP, Telefone (opcional), E-mail (recomendado)',
        '',
        '💡 ATALHO NA EMISSÃO: você também pode cadastrar um cliente direto na emissão, na Etapa 2 (Dados Cliente), pelo botão "+ Novo Cliente" — sem perder o que já preencheu. O modal tem "Buscar CNPJ" (preenche razão social e endereço a partir da Receita) e "Buscar CEP" (preenche logradouro, bairro, município, UF e o Código IBGE pelo ViaCEP). Ao salvar, o cliente já fica selecionado na nota.',
        '',
        '⚠️ Atenção: IE com formatação e Código IBGE de município ausente são as principais causas de rejeição SEFAZ.',
      ]
    },
    {
      titulo: '📦 Cadastrar Produto',
      conteudo: [
        'Menu Produtos → Novo Produto. Campos essenciais:',
        '',
        '• Código interno (uso da sua empresa, ex.: "PROD-001")',
        '• Descrição',
        '• NCM (autocomplete: digite código ou descrição, sistema sugere baseado na tabela oficial SISCOMEX)',
        '• CFOP padrão (5102 venda interna, 6102 interestadual)',
        '• Unidade (UN, KG, M, PC, etc.)',
        '• Valor unitário',
        '• Origem (0 — Nacional, 1 — Estrangeira Importação Direta, etc.)',
        '• CEST (quando aplicável)',
        '• Alíquotas de ICMS, PIS, COFINS, IPI',
        '',
        '💡 CST/CSOSN: o sistema aplica automaticamente conforme o regime tributário da empresa emitente. Se sua empresa é Simples Nacional, sai CSOSN (ex.: 102, 900); se é Lucro Real/Presumido, sai CST (ex.: 00, 40).',
        '',
        '📌 IMPORTAÇÃO EM LOTE (acelera muito o cadastro):',
        '• Botão "📄 Importar de NF-e" ao lado do "Novo Produto"',
        '• Aceita arquivo .xml ou .pdf de NF-e recebida de fornecedor',
        '• XML: extração 100% confiável (estrutura padronizada SEFAZ)',
        '• PDF: extração via pdfplumber (DANFEs digitais), com preview EDITÁVEL para ajustes',
        '• Detecção automática de duplicados (código + descrição ou NCM + descrição similar)',
        '• Para cada item: marcar/desmarcar + decidir Pular ou Atualizar se duplicado',
        '',
        '🗑️ EXCLUSÃO DE PRODUTO:',
        '• Botão "Excluir" com confirmação',
        '• Exclusão LÓGICA (soft delete) — produto fica oculto mas o histórico de NF-e que o usaram fica preservado',
        '• Necessário para integridade fiscal e auditoria',
      ]
    },
    {
      titulo: '📝 Emitir NF-e',
      conteudo: [
        'A emissão é um ASSISTENTE EM 6 ETAPAS. Use os botões "Avançar →" e "← Voltar" no rodapé para navegar entre elas. O topo da tela mostra um indicador "Passo X de 6 — <nome da etapa>" para você não se perder.',
        '',
        '🔹 RECURSOS PERSISTENTES (disponíveis em TODAS as etapas, no topo da tela):',
        '• "Salvar Rascunho" — pausa e retoma depois. Disponível a partir do 1º item adicionado. Reabrir um rascunho recarrega tudo na Etapa 1.',
        '• "Visualizar Prévia" — abre o PDF da DANFE com marca d\'água "PRÉVIA - SEM VALOR FISCAL", a partir do 1º item. Use para conferir como a nota está ficando antes de emitir.',
        '',
        '📌 AS 6 ETAPAS:',
        '',
        '1) Dados NF-e',
        '   Natureza da operação (ex.: "Venda de mercadoria"), Operação (Saída/Entrada), Finalidade (Normal/Complementar/Ajuste/Devolução), Tipo de Atendimento e Intermediador. Modelo, Série e Número da nota já vêm preenchidos automaticamente.',
        '',
        '2) Dados Cliente',
        '   Selecione o destinatário na lista. O sistema identifica se é operação interna ou interestadual e preenche o campo idDest do XML automaticamente.',
        '   💡 ATALHO: pelo botão "+ Novo Cliente" ao lado da lista, você cadastra um cliente novo sem sair da emissão. O modal tem "Buscar CNPJ" (preenche razão social/endereço pela Receita) e "Buscar CEP" (preenche logradouro, bairro, município, UF e o Código IBGE pelo ViaCEP). Ao salvar, o cliente já fica selecionado.',
        '',
        '3) Itens da Nota',
        '   Adicione os produtos: código, descrição, NCM, CFOP, unidade, quantidade, valor unitário, desconto/frete/seguro/outras. Cada item tem abas ICMS / IPI / PIS / COFINS para as alíquotas. Você pode selecionar um produto já cadastrado (autocompleta tudo) ou digitar os dados direto.',
        '',
        '4) Transportador',
        '   Tipo de frete (padrão "9 - Sem frete" — adequado para retiradas e venda no balcão). Se houver frete, informe razão social, CNPJ/CPF, ANTT, placa do veículo e dados de volume (quantidade, espécie, peso). Tudo opcional, exceto o Tipo de Frete.',
        '',
        '5) Cobrança',
        '   Forma de pagamento (À Vista / A Prazo / Outros) e os pagamentos da nota (Dinheiro, PIX, Cartão, Boleto, etc.) com os valores.',
        '',
        '6) Emissão',
        '   Informações Complementares (texto livre que vai no infCpl do XML, ex.: "Pedido nº 123"). Aqui aparecem os botões finais:',
        '   • "Emitir NF-e" — transmite à SEFAZ',
        '   • "Pré-visualizar PDF" — abre o DANFE de prévia (mesma função do "Visualizar Prévia" do topo)',
        '   • "Salvar Rascunho"',
        '',
        '✅ VALIDAÇÃO PRÉVIA:',
        '• Ao clicar "Avançar →" entre etapas, o sistema valida os campos obrigatórios DAQUELA etapa e mostra mensagem clara em PT-BR se faltar algo (ex.: "Selecione o destinatário", "Item 1: informe o NCM").',
        '• Antes de chamar a SEFAZ no botão "Emitir NF-e", o sistema também valida o cadastro da empresa emitente e do destinatário. Se faltar algo (ex.: Código IBGE), retorna erro com a lista de campos faltantes — não desperdiça chamada à SEFAZ.',
        '',
        '💡 APÓS AUTORIZADA: o DANFE final é gerado automaticamente com protocolo, chave de acesso, canhoto, código de barras e número da nota oficial.',
        '',
        '💡 REJEIÇÃO AUTOMÁTICA: em caso de rejeição da SEFAZ, o número da NF-e é incrementado automaticamente na próxima tentativa (cálculo MAX+1 com lock de concorrência, evitando duplicidade em emissões simultâneas). Use o botão "Reemitir" na lista de Notas Emitidas — todos os dados vêm preenchidos.',
      ]
    },
    {
      titulo: '📋 Notas Emitidas — Ações Disponíveis',
      conteudo: [
        'Menu NF-e → Notas Emitidas. Filtre por período, status, cliente ou número.',
        'Busca rápida por número, nome ou CNPJ do cliente.',
        '',
        '📌 AÇÕES POR STATUS:',
        '',
        '📘 Autorizadas:',
        '• Download XML (assinado e autorizado pela SEFAZ)',
        '• Download DANFE (PDF com canhoto, código de barras, chave, protocolo)',
        '• Carta de Correção Eletrônica (CC-e): corrige erros formais, não altera valores/partes/dados fiscais. Até 20 CC-e por NF-e',
        '• Cancelar NF-e: prazo legal de 24 horas, justificativa obrigatória mínimo 15 caracteres',
        '',
        '🔴 Canceladas:',
        '• Download XML (preservado)',
        '• Download DANFE: PDF gerado com marca d\'água diagonal vermelha "NOTA CANCELADA" + tarja de cancelamento com protocolo, data e justificativa',
        '',
        '❌ Rejeitadas:',
        '• Download XML rejeitado (preservado)',
        '• Botão Reemitir: abre tela de emissão com TODOS os dados da nota original pré-preenchidos (já na Etapa 1 do wizard). Banner âmbar mostra código e mensagem da rejeição + ação sugerida, e informa se o mesmo número será reutilizado. Como uma nota rejeitada não existe na SEFAZ, a reemissão reaproveita o MESMO número — evitando pulos na numeração. Exceção: rejeição por duplicidade (204/539), em que um novo número é gerado. Após sucesso, a original muda para status "Reemitida"',
        '• Botão Regularizar: consulta SEFAZ para verificar se a nota foi de fato autorizada (caso de dessincronização local x SEFAZ). Se confirmar autorização, atualiza status local e importa protocolo',
        '• Excluir (com aviso fiscal forte)',
        '',
        '📝 Rascunhos:',
        '• Botão Editar: retoma o rascunho na tela de emissão (abre na Etapa 1 do wizard com tudo preenchido). Salvar atualiza o mesmo registro (não cria duplicata)',
        '• Excluir (sem aviso forte — esboço pode ser apagado)',
        '',
        '♻️ Reemitidas:',
        '• Badge cinza',
        '• Acesso ao XML rejeitado original',
        '• Link "Ver nova NF-e" para a reemissão',
        '',
        '⛔ Inutilizadas:',
        '• Badge cinza-azulado',
        '• Download do XML de inutilização',
        '',
        '🆚 QUANDO USAR CADA AÇÃO (referência rápida):',
        '• 🔴 Cancelar — a NF-e foi autorizada mas a venda não vai acontecer (devolução, erro grave). Prazo: 24h.',
        '• ✏️ CC-e — a NF-e foi autorizada e contém erro pequeno que NÃO afeta valores/CNPJ/datas/CFOP/NCM (ex.: endereço de entrega digitado errado). Até 20 CC-e por nota.',
        '• ♻️ Reemitir — a SEFAZ REJEITOU. Você corrige o que causou a rejeição e emite de novo com novo número automático.',
        '• ⛔ Inutilizar — você "queima" números de NF-e que não foram usados (rejeições antigas, faixas pendentes). Definitivo, sem volta.',
        '• 🛟 Regularizar — está autorizada na SEFAZ mas o sistema marca como rejeitada (dessincronização). O sistema consulta a SEFAZ e corrige o status local.',
        '',
        '📌 INUTILIZAR NUMERAÇÃO:',
        '• Procedimento oficial SEFAZ para "queimar" números não utilizados (ex.: rejeições antigas, inconsistências)',
        '• Permite faixa contínua de números (ex.: 8 a 10)',
        '• Justificativa obrigatória entre 15 e 255 caracteres',
        '⚠️ DEFINITIVO — não há "des-inutilizar"',
        '⚠️ Não usar para notas autorizadas (use cancelamento)',
        '• São Paulo, Acre e Bahia: desde 01/11/2026 não existe mais inutilização na SEFAZ (Ajuste SINIEF 40/2026). O número que ficou sem uso é só registrado no sistema, com o motivo; a emissão segue pelo número seguinte.',
        '',
        '📊 RELATÓRIOS EM MASSA:',
        '• Relatório PDF por período (separa autorizadas e canceladas)',
        '• Download de XMLs em ZIP (envio à contabilidade)',
      ]
    },
    {
      titulo: '❓ Erros Comuns SEFAZ e Soluções',
      conteudo: [
        'Códigos de rejeição mais frequentes e o que fazer na tela:',
        '',
        '🔴 104, 215, 225 — Falha no Schema XML do lote',
        'Algum campo obrigatório está vazio ou mal formatado. Ação prática:',
        '• Abra Minha Empresa e confira: CNPJ, IE, Razão Social, endereço completo, Código IBGE do município preenchido.',
        '• Abra o cadastro do destinatário (Clientes ou direto na Etapa 2 da emissão) e confira: CNPJ/CPF, Razão Social, endereço, Código IBGE.',
        '• Confira nos itens: NCM (8 dígitos), CFOP, valores corretos.',
        '',
        '🔴 233 — Inscrição Estadual inválida',
        'A IE do destinatário deve estar sem formatação (apenas números) e ativa na SEFAZ. Ação prática:',
        '• Abra o cadastro do cliente, remova pontos/traços/letras da IE, deixe só números. Se for isento, use "ISENTO" e Indicador IE = 2 (Isento).',
        '',
        '🔴 272 — Código de município inválido',
        'O Código IBGE de 7 dígitos é obrigatório no cadastro do destinatário (e do emitente). Ação prática:',
        '• Abra Minha Empresa ou o cadastro do cliente e use o autocomplete de Município — o Código IBGE é preenchido automaticamente. No modal "+ Novo Cliente" da emissão, o "Buscar CEP" também preenche o Código IBGE pelo ViaCEP.',
        '',
        '🔴 539 — Duplicidade de NF-e',
        'O número já foi utilizado nesta série. Ação prática:',
        '• O sistema incrementa o próximo número automaticamente — basta tentar reemitir (botão Reemitir na lista de Notas Emitidas).',
        '',
        '🔴 778 — NCM inexistente',
        'O NCM informado não existe na tabela oficial. Ação prática:',
        '• Abra o cadastro do produto e use o autocomplete de NCM (digite código ou descrição — o sistema consulta a tabela oficial SISCOMEX).',
        '',
        '🟡 SEFAZ fora do ar',
        'Aguarde alguns minutos. Consulte status em nfe.fazenda.sp.gov.br. O sistema não emite em contingência nesta versão — tentar novamente após restabelecimento.',
        '',
        '🟡 Certificado digital vencido',
        'Banner vermelho aparece em Minha Empresa e a emissão é bloqueada. Ação prática:',
        '• Acesse Minha Empresa → Certificado Digital → "Substituir certificado" → upload do novo .pfx + senha.',
        '',
        '💡 DICA GERAL: ao clicar "Reemitir" em qualquer nota rejeitada, a tela mostra o código e a mensagem da rejeição num banner âmbar no topo, com a ação sugerida — facilita identificar o que corrigir.',
      ]
    },
    {
      titulo: '📞 Suporte',
      conteudo: [
        'Canais de atendimento Contabilizza KT Prime:',
        '',
        '• WhatsApp: (11) 4781-2596 (botão flutuante verde no canto inferior direito)',
        '• E-mail: atendimento@ktprime.com.br',
        '• Horário: Segunda a sexta, 9h às 18h',
        '• Site: contabilizza.com',
        '',
        'Responsável técnica: Contadora Karina Campos',
        'Sistema: Emissor NF-e Contabilizza — Versão em produção',
      ]
    },
    // ===================== FAQ =====================
    {
      titulo: '❓ FAQ — Cadastros',
      conteudo: [
        'P: Posso cadastrar um cliente sem Inscrição Estadual?',
        'R: Sim, se for pessoa física (CPF) ou pessoa jurídica isenta. Deixe o campo em branco ou preencha "ISENTO".',
        '',
        'P: O sistema valida CNPJ automaticamente?',
        'R: Sim, valida formato e dígitos verificadores. Para verificar se o CNPJ está ativo na Receita Federal, use o botão "Buscar CNPJ" no cadastro.',
        '',
        'P: Como atualizar dados de um cliente que já tem NF-e emitidas?',
        'R: Pode atualizar livremente. As NF-e antigas continuam mostrando os dados que estavam no momento da emissão (preservados no XML autorizado). Apenas novas emissões usarão os dados atualizados.',
        '',
        'P: Tenho clientes duplicados, como consolidar?',
        'R: Hoje a consolidação é feita manualmente pelo admin via banco de dados. Entre em contato com o suporte caso precise consolidar duplicados (mantém histórico fiscal preservado).',
        '',
        'P: O Código IBGE é obrigatório?',
        'R: Sim, é obrigatório para emissão de NF-e. O sistema preenche automaticamente quando você seleciona o município no autocomplete. Se digitar errado, a SEFAZ rejeita com código 272.',
      ]
    },
    {
      titulo: '❓ FAQ — Emissão de NF-e',
      conteudo: [
        'P: Posso editar uma NF-e depois de emitida?',
        'R: Não. Após autorizada pela SEFAZ, a NF-e fica imutável. Para correções formais menores (sem alterar valores, partes ou dados fiscais), use a CC-e (Carta de Correção Eletrônica). Para correções maiores, cancele e emita nova.',
        '',
        'P: Qual o prazo para cancelar uma NF-e?',
        'R: 24 horas após a autorização. Após esse prazo, somente regularização contábil pelo seu contador.',
        '',
        'P: Posso pular um número de NF-e?',
        'R: Em São Paulo, Acre e Bahia, desde 01/11/2026 não há mais inutilização na SEFAZ (Ajuste SINIEF 40/2026): continue pelo número seguinte e registre o número sem uso em "Inutilizar Numeração", com o motivo. Nas demais UFs, o número não usado deve ser INUTILIZADO via SEFAZ.',
        '',
        'P: O que fazer se a SEFAZ estiver fora do ar?',
        'R: Aguarde. O sistema não emite em contingência (ainda). Consulte status em nfe.fazenda.sp.gov.br. Tentar novamente após restabelecimento.',
        '',
        'P: Posso emitir NF-e em fim de semana ou feriado?',
        'R: Sim. A SEFAZ funciona 24/7 (com janelas de manutenção esporádicas).',
        '',
        'P: O que é a "data de saída" no DANFE?',
        'R: É a data em que a mercadoria efetivamente saiu do estabelecimento. O sistema preenche automaticamente com a data de emissão (quando emissão e saída coincidem).',
      ]
    },
    {
      titulo: '❓ FAQ — Cancelamento e Correção',
      conteudo: [
        'P: Quando usar Cancelamento vs CC-e?',
        'R: Cancelamento: quando os dados estão completamente errados, ou a venda não vai acontecer. CC-e: para correções menores que NÃO alterem valores, partes envolvidas (CNPJ/CPF), datas ou dados fiscais (NCM, CFOP, alíquotas).',
        '',
        'P: Quantas CC-e posso emitir por NF-e?',
        'R: Até 20 CC-e por NF-e, conforme regra da SEFAZ.',
        '',
        'P: Cancelei mas o cliente já recebeu o produto, e agora?',
        'R: O cancelamento da NF-e dentro de 24h é técnico (na SEFAZ). A regularização operacional (devolução, reemissão para outro destinatário) deve ser tratada com seu contador.',
        '',
        'P: O DANFE de uma nota cancelada continua válido?',
        'R: NÃO. Quando você baixa o DANFE de uma nota cancelada, o sistema gera o PDF com marca d\'água vermelha "NOTA CANCELADA" e tarja com protocolo de cancelamento — para evitar uso indevido.',
      ]
    },
    {
      titulo: '❓ FAQ — Rejeições e Inconsistências',
      conteudo: [
        'P: Minha NF-e foi rejeitada, perdi o trabalho?',
        'R: Não. Use o botão "Reemitir" na lista de Notas Emitidas. Todos os dados da nota original são pré-preenchidos. Apenas corrija o que causou a rejeição e emita novamente.',
        '',
        'P: Como saber o que causou a rejeição?',
        'R: Cada rejeição vem com código e mensagem da SEFAZ. Veja a seção "Erros Comuns SEFAZ" desta Central. Ao reemitir, o banner âmbar mostra o código e a ação sugerida.',
        '',
        'P: A SEFAZ diz que minha nota está autorizada, mas no sistema aparece rejeitada. O que fazer?',
        'R: Use o botão "Regularizar" na lista de Notas Emitidas. O sistema consulta a SEFAZ via webservice oficial e sincroniza o status local. Útil em casos de dessincronização (queda de rede durante autorização, bug de sistema antigo, etc.).',
        '',
        'P: O número da NF-e ficou "queimado" depois de várias rejeições. Como liberar?',
        'R: Use "Inutilizar Numeração" (na seção Notas Emitidas). Em São Paulo, Acre e Bahia, desde 01/11/2026, o número é só registrado no sistema como não utilizado (Ajuste SINIEF 40/2026); nas demais UFs, a inutilização é feita na SEFAZ. Nos dois casos é definitivo — não tem volta.',
      ]
    },
    {
      titulo: '❓ FAQ — Certificado Digital',
      conteudo: [
        'P: Como cadastro meu certificado A1?',
        'R: Acesse Minha Empresa → seção "Certificado Digital" → upload do arquivo .pfx + senha. O sistema valida automaticamente.',
        '',
        'P: O sistema verifica se o certificado é da minha empresa?',
        'R: Sim. O CNPJ extraído do certificado deve bater com o CNPJ cadastrado na empresa. Se não bater, o upload é bloqueado (proteção contra erro).',
        '',
        'P: Minha senha do certificado fica armazenada no sistema?',
        'R: Sim, mas de forma CRIPTOGRAFADA com Fernet (padrão da indústria). Nunca é armazenada em texto puro. Apenas o sistema descriptografa internamente para usar na assinatura dos XMLs.',
        '',
        'P: O sistema avisa quando o certificado vai vencer?',
        'R: Sim. 30 dias antes do vencimento aparece banner amarelo. Após vencer, banner vermelho e a emissão é bloqueada até renovação.',
        '',
        'P: Como renovar o certificado?',
        'R: Acesse Minha Empresa → Certificado Digital → "Substituir certificado". Faça upload do novo .pfx + senha. O antigo é substituído.',
      ]
    },
    {
      titulo: '❓ FAQ — Importação e Cadastros em Lote',
      conteudo: [
        'P: Posso importar produtos a partir de uma NF-e que recebi do fornecedor?',
        'R: Sim. Em Produtos, clique "📄 Importar de NF-e" e envie o XML ou o PDF da NF-e. O sistema extrai todos os itens e mostra preview para você selecionar quais cadastrar.',
        '',
        'P: É melhor importar XML ou PDF?',
        'R: XML é muito mais confiável (estrutura padronizada nacional). PDF funciona mas pode ter pequenas imprecisões — por isso o preview do PDF é editável. Sempre prefira o XML quando disponível.',
        '',
        'P: O que acontece se o produto importado já existe no meu cadastro?',
        'R: O sistema detecta como duplicado e marca em amarelo. Você decide: Pular (não cadastrar de novo) ou Atualizar (substituir dados do existente pelos novos).',
        '',
        'P: Posso importar produtos de várias notas de uma vez?',
        'R: Não nesta versão. Cada importação processa um arquivo. Funcionalidade de importação em massa pode ser implementada futuramente conforme demanda.',
      ]
    },
  ];

  const cardStyle = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' };
  const headerStyle = (expanded) => ({
    padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: expanded ? '#f0f4ff' : '#fafafa', fontSize: '15px', fontWeight: 600,
    borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
  });

  // Filtragem por busca (normalizada — ignora acento/caixa). Preserva índice
  // original (i) para que `aberta` e FAQ_OFFSET continuem coerentes.
  const termo = normalize(busca.trim());
  const buscando = termo.length > 0;
  const matchesTermo = (sec) =>
    !buscando ||
    normalize(sec.titulo).includes(termo) ||
    sec.conteudo.some(l => normalize(l).includes(termo));
  const secoesFiltradas = secoes
    .map((sec, i) => ({ sec, i }))
    .filter(({ sec }) => matchesTermo(sec));
  const primeiroFaqVisivel = secoesFiltradas.findIndex(({ i }) => i >= FAQ_OFFSET);

  return (
    <div>
      <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>Central de Ajuda</h2>
      <p style={{ color: '#666', marginBottom: '12px', fontSize: '14px' }}>
        Guia completo de utilização do Emissor NF-e Contabilizza. Clique em cada seção para expandir, ou use a busca para encontrar um tópico específico.
      </p>

      <input
        type="search"
        value={busca}
        onChange={e => setBusca(e.target.value)}
        placeholder="🔍 Buscar na ajuda... (ex: cancelar nota, certificado, rejeição 225)"
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 14px', marginBottom: '20px',
          border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '14px',
          background: '#fff', outline: 'none',
        }}
      />

      {buscando && secoesFiltradas.length === 0 && (
        <div style={{
          padding: '20px', background: '#FEF3C7', border: '1px solid #F59E0B',
          borderRadius: '8px', color: '#92400E', fontSize: '14px', textAlign: 'center',
        }}>
          Nenhum resultado para "<b>{busca}</b>". Tente outras palavras ou fale com o Suporte (WhatsApp 11 4781-2596).
        </div>
      )}

      {secoesFiltradas.map(({ sec, i }, idx) => {
        const expanded = buscando ? true : (aberta === i);
        const showFaqSeparator = idx === primeiroFaqVisivel;
        return (
          <React.Fragment key={i}>
            {showFaqSeparator && (
              <div style={{
                marginTop: '24px', marginBottom: '12px',
                padding: '10px 16px',
                background: 'linear-gradient(90deg, #F3E8FF 0%, #FCE7F3 100%)',
                borderRadius: '8px',
                fontSize: '14px', fontWeight: 700, color: '#6B21A8',
                letterSpacing: '0.3px',
              }}>
                ❓ PERGUNTAS FREQUENTES (FAQ)
              </div>
            )}
            <div style={cardStyle}>
              <div style={headerStyle(expanded)} onClick={() => !buscando && toggle(i)}>
                <span>{sec.titulo}</span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
              </div>
              {expanded && (
                <div style={{ padding: '16px', fontSize: '13px', lineHeight: '1.8', color: '#333', background: '#fff' }}>
                  {sec.conteudo.map((linha, j) => (
                    <div key={j} style={{ marginBottom: linha === '' ? '8px' : '2px', paddingLeft: linha.startsWith('   ') ? '16px' : '0' }}>
                      {linha || '\u00A0'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================================
// SIDEBAR — cards arredondados com ícones lucide e cores pastel
// ============================================================
function Sidebar({ menu, currentPage, onNavigate, user, isAdmin, onLogout, onAbrirAssistente }) {
  return (
    <aside style={{
      width: '270px',
      background: '#F9FAFB',
      borderRight: '1px solid #E5E7EB',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
    }}>
      {/* Cabeçalho — logo + identificação do usuário */}
      <div style={{ padding: '20px 16px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div
            style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <FileCheck size={28} color="#FFFFFF" strokeWidth={2.5} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontWeight: 600, fontSize: 13.5, margin: 0, color: '#111827' }}>
              Emissor NF-e Contabilizza
            </p>
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.nome}
            </p>
          </div>
        </div>
        <span style={{
          display: 'inline-block', fontSize: 10, fontWeight: 700,
          padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.5,
          background: isAdmin ? '#F3E8FF' : '#DBEAFE',
          color: isAdmin ? '#7C3AED' : '#1D4ED8',
        }}>{isAdmin ? 'Contabilidade' : 'Operador'}</span>
      </div>

      {/* Lista de itens — cards individuais com cor pastel por seção */}
      <nav style={{ flex: 1, padding: '6px 12px', display: 'flex',
                    flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
        {menu.map(item => {
          const ativo = currentPage === item.key;
          return <SidebarItem key={item.key} item={item} ativo={ativo}
                              onClick={() => onNavigate(item.key, null)} />;
        })}
      </nav>

      {/* Rodapé — Suporte IA + WhatsApp + Sair */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={onAbrirAssistente}
          title="Abrir o assistente fiscal de IA"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '8px 12px', background: '#2563EB', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            marginBottom: 10,
          }}
        ><Bot size={16} /> Suporte IA</button>
        <a
          href="https://wa.me/5511930337000?text=Preciso%20de%20suporte%20na%20emiss%C3%A3o%20de%20NF-e"
          target="_blank" rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: '#25D366', color: '#fff',
            borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600,
            marginBottom: 10,
          }}
        >💬 Suporte WhatsApp</a>
        <button onClick={onLogout} style={{
          background: 'none', border: 'none', color: '#6B7280',
          cursor: 'pointer', fontSize: 13, padding: 0,
        }}>Sair</button>
      </div>
    </aside>
  );
}

function SidebarItem({ item, ativo, onClick }) {
  const [hover, setHover] = useState(false);
  const { Icon, label, bg, fg } = item;
  const ROXO_ATIVO = '#7C3AED';

  // Card de fora muda no estado ativo (roxo) ou no hover (cinza claro).
  const cardBg = ativo ? ROXO_ATIVO : (hover ? '#F3F4F6' : '#FFFFFF');
  const cardBorder = ativo ? ROXO_ATIVO : '#E5E7EB';
  const textColor = ativo ? '#FFFFFF' : '#1F2937';
  const arrowColor = ativo ? '#FFFFFF' : '#9CA3AF';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: 10,
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        cursor: 'pointer',
        transition: 'background 0.12s, border-color 0.12s',
        userSelect: 'none',
      }}
    >
      {/* Tile do ícone — mantém pastel mesmo quando o card está ativo,
          para preservar identidade visual da seção. */}
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} color={fg} strokeWidth={2.2} />
      </div>
      <span style={{
        flex: 1, fontSize: 13.5, fontWeight: ativo ? 600 : 500,
        color: textColor, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>{label}</span>
      <ChevronRight size={16} color={arrowColor} strokeWidth={2.2} />
    </div>
  );
}


// ============================================================
// LAYOUT PRINCIPAL
// ============================================================
// ============================================================
// POP-UP DE NOVIDADES (changelog) — controlado por versão
// ------------------------------------------------------------
// Modal NÃO-dispensável: sem ESC, sem clique-fora, sem botão X.
// Só o botão "Lido" fecha. Enquanto não confirmar, reaparece a cada
// carregamento. A versão atual vem de changelog.js; o backend só persiste a
// string confirmada (coluna usuarios.changelog_visto_versao).
// ============================================================
function ChangelogModal() {
  const { user, setUser } = useAuth();
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Só exibe se houver itens publicados E a versão confirmada pelo usuário for
  // diferente da atual (inclui o caso "nunca confirmou" → null !== versao).
  // Itens com apenasAdmin: true só aparecem para o escritório (F0h).
  const itensVisiveis = (Array.isArray(CHANGELOG_ITENS) ? CHANGELOG_ITENS : [])
    .filter(item => !item.apenasAdmin || user?.perfil === 'admin');
  const temItens = itensVisiveis.length > 0;
  const precisaVer = temItens && user?.changelog_visto_versao !== CHANGELOG_VERSAO;
  if (!precisaVer) return null;

  const marcarLido = async () => {
    setSalvando(true); setErro('');
    try {
      await api.marcarChangelogLido(CHANGELOG_VERSAO);
      // Atualiza o usuário em memória para o modal não reaparecer nesta sessão.
      const atualizado = { ...user, changelog_visto_versao: CHANGELOG_VERSAO };
      setUser(atualizado);
      api.setUser(atualizado);
    } catch (err) {
      setErro('Não foi possível registrar a confirmação. Tente novamente.');
      setSalvando(false);
    }
  };

  return (
    // Overlay SEM onClick — clique fora não fecha. Sem listener de ESC.
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1100,
    }}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '1.75rem',
        width: '520px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
      }}>
        {/* Sem botão X — fechar só pelo botão "Lido". */}
        <h3 style={{ margin: '0 0 4px', fontSize: '18px' }}>Novidades</h3>
        <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#888' }}>
          Atualização {CHANGELOG_VERSAO}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {itensVisiveis.map((item, i) => (
            <div key={i} style={{
              borderLeft: `3px solid ${cores.verde}`, paddingLeft: '12px',
            }}>
              <p style={{ margin: '0 0 2px', fontSize: '14px', fontWeight: 600 }}>{item.titulo}</p>
              {item.descricao && (
                <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: 1.5 }}>{item.descricao}</p>
              )}
            </div>
          ))}
        </div>

        {erro && <div style={{ marginTop: '16px' }}><Alerta tipo="erro">{erro}</Alerta></div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <Btn onClick={marcarLido} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Lido'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// NOTAS ANTIGAS (CONSULTA) — arquivo morto do sistema anterior
// ------------------------------------------------------------
// Tela SOMENTE LEITURA. Estas notas não foram emitidas aqui: não existe
// cancelar, corrigir, reemitir nem excluir. Só consulta e reimpressão.
// ============================================================
function NotasLegadoPage() {
  const [notas, setNotas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busca, setBusca] = useState('');
  // 'todas' | 'validas' | 'canceladas' — filtrado no servidor.
  const [filtro, setFiltro] = useState('todas');

  useEffect(() => {
    setCarregando(true);
    const arg = filtro === 'todas' ? undefined : filtro === 'canceladas';
    api.listarNotasLegado(arg)
       .then(d => setNotas(d.notas || []))
       .catch(err => setMsg({ tipo: 'erro', texto: err.message }))
       .finally(() => setCarregando(false));
  }, [filtro]);

  const download = async (id, tipo) => {
    try {
      const blob = tipo === 'danfe'
        ? await api.downloadDanfeLegado(id)
        : await api.downloadXmlLegado(id);
      const url = URL.createObjectURL(blob);
      if (tipo === 'danfe') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = `nfe_legado_${id}.xml`; a.click();
      }
    } catch (err) { setMsg({ tipo: 'erro', texto: err.message }); }
  };

  const termo = busca.trim().toLowerCase();
  const notasFiltradas = !termo ? notas : notas.filter(n =>
    String(n.numero || '').includes(termo)
    || (n.dest_nome || '').toLowerCase().includes(termo)
    || (n.dest_documento || '').includes(termo)
    || (n.chave_acesso || '').includes(termo)
  );

  return (
    <div>
      {msg && <Alerta tipo={msg.tipo}>{msg.texto}</Alerta>}

      <div style={{ marginBottom: '1rem' }}>
        <Alerta tipo="info">
          Notas importadas do sistema anterior. Disponíveis apenas para consulta
          e impressão — não é possível cancelar, corrigir ou reemitir. As notas
          que já haviam sido canceladas no sistema antigo aparecem marcadas como
          <strong> Cancelada</strong> e não têm efeito fiscal.
        </Alerta>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '20px', margin: 0 }}>Notas Antigas (consulta)</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${cores.cinzaBorda}`, fontSize: '13px' }}
          >
            <option value="todas">Todas</option>
            <option value="validas">Válidas</option>
            <option value="canceladas">Canceladas</option>
          </select>
          <span style={{ fontSize: '12px', color: '#888' }}>
            {notas.length} nota{notas.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
        <input
          type="text"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por número, destinatário, CNPJ/CPF ou chave de acesso..."
          style={{
            width: '100%',
            padding: '9px 36px 9px 36px',
            border: `1px solid ${cores.cinzaBorda}`,
            borderRadius: '8px',
            fontSize: '13px',
            boxSizing: 'border-box',
            background: '#fff',
          }}
        />
        {busca && (
          <button
            onClick={() => setBusca('')}
            title="Limpar busca"
            style={{
              position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: '16px', color: '#888', padding: '4px 8px', lineHeight: 1,
            }}
          >×</button>
        )}
      </div>

      {carregando ? (
        <p style={{ fontSize: '13px', color: '#888' }}>Carregando...</p>
      ) : (
        <Tabela
          colunas={[
            { key: 'numero', label: 'Número', bold: true, render: r => String(r.numero).padStart(9, '0') },
            { key: 'serie', label: 'Série', align: 'center', render: r => r.serie ?? '—' },
            { key: 'data_emissao', label: 'Data', render: r => r.data_emissao ? new Date(r.data_emissao).toLocaleDateString('pt-BR') : '—' },
            { key: 'dest_nome', label: 'Destinatário', render: r => r.dest_nome || '—' },
            { key: 'valor_total', label: 'Valor', align: 'right', render: r => r.valor_total != null ? `R$ ${fmtValor(r.valor_total)}` : '—' },
            { key: 'cancelada', label: 'Situação', align: 'center', render: r => (
              r.cancelada
                ? <span
                    title={r.motivo_status || 'Cancelada no sistema anterior'}
                    style={{ background: '#fee2e2', color: '#b91c1c', padding: '3px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}
                  >Cancelada</span>
                : <span style={{ color: '#888', fontSize: '12px' }}>Válida</span>
            ) },
          ]}
          dados={notasFiltradas}
          estiloLinha={r => (r.cancelada ? { background: '#fef6f6' } : null)}
          acoes={(row) => (
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'danfe')}>DANFE</Btn>
              <Btn variant="ghost" size="sm" onClick={() => download(row.id, 'xml')}>XML</Btn>
            </div>
          )}
        />
      )}
    </div>
  );
}

function Layout() {
  const { user, logout } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [navPayload, setNavPayload] = useState(null);
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  const navigate = (next, payload = null) => { setPage(next); setNavPayload(payload); };
  const isAdmin = user?.perfil === 'admin';

  // Itens do menu — cada item tem ícone lucide + cores pastel próprias.
  // Item ativo recebe fundo roxo sólido; mantemos o "tile" do ícone na cor pastel
  // para preservar a identidade visual da seção.
  const ITEM_DASHBOARD     = { key: 'dashboard',     label: 'Dashboard',      Icon: LayoutDashboard, bg: '#DCFCE7', fg: '#16A34A' };
  const ITEM_MINHA_EMPRESA = { key: 'minha_empresa', label: 'Minha Empresa',  Icon: Building2,       bg: '#DBEAFE', fg: '#2563EB' };
  const ITEM_EMITIR        = { key: 'emitir',        label: 'Emitir NF-e',    Icon: FilePlus,        bg: '#E0F2FE', fg: '#0284C7' };
  const ITEM_NOTAS         = { key: 'notas',         label: 'Notas Emitidas', Icon: FileText,        bg: '#FEF3C7', fg: '#D97706' };
  const ITEM_NOTAS_LEGADO  = { key: 'notas_legado',  label: 'Notas Antigas (consulta)', Icon: Archive, bg: '#E2E8F0', fg: '#475569' };
  const ITEM_CLIENTES      = { key: 'clientes',      label: 'Clientes',       Icon: Users,           bg: '#EDE9FE', fg: '#7C3AED' };
  const ITEM_PRODUTOS      = { key: 'produtos',      label: 'Produtos',       Icon: Package,         bg: '#DCFCE7', fg: '#16A34A' };
  const ITEM_AJUDA         = { key: 'ajuda',         label: 'Ajuda',          Icon: HelpCircle,      bg: '#FCE7F3', fg: '#DB2777' };
  const ITEM_EMPRESAS      = { key: 'empresas',      label: 'Empresas',       Icon: Building,        bg: '#DBEAFE', fg: '#2563EB' };
  const ITEM_AUDITORIA     = { key: 'auditoria',     label: 'Histórico',      Icon: History,         bg: '#F3E8FF', fg: '#9333EA' };
  const ITEM_IBPT          = { key: 'ibpt',          label: 'Tabela IBPT',    Icon: FileCheck,       bg: '#FEF9C3', fg: '#A16207' };
  const ITEM_CONFERENCIA   = { key: 'conferencia',   label: 'Relatórios de conferência', Icon: ClipboardList, bg: '#FFEDD5', fg: '#C2410C' };
  const ITEM_DIFAL         = { key: 'difal',         label: 'Tabela do DIFAL', Icon: FileCheck,      bg: '#E0E7FF', fg: '#4338CA' };
  const ITEM_CREDITO_SN    = { key: 'credito_sn',    label: 'Crédito do Simples', Icon: FileCheck,   bg: '#DCFCE7', fg: '#15803D' };
  const ITEM_ALIQ_OFICIAIS = { key: 'aliquotas_oficiais', label: 'Alíquotas IBS/CBS', Icon: FileCheck, bg: '#FEE2E2', fg: '#B91C1C' };
  const ITEM_SIMPLES_2027  = { key: 'simples_2027', label: 'Simples 2027', Icon: FileCheck, bg: '#ECFCCB', fg: '#4D7C0F' };

  const menuOperador = [
    ITEM_DASHBOARD, ITEM_MINHA_EMPRESA, ITEM_EMITIR, ITEM_NOTAS, ITEM_NOTAS_LEGADO,
    ITEM_CLIENTES, ITEM_PRODUTOS, ITEM_AJUDA,
  ];
  const menuAdmin = [
    ITEM_DASHBOARD, ITEM_EMITIR, ITEM_NOTAS, ITEM_NOTAS_LEGADO, ITEM_CLIENTES,
    ITEM_PRODUTOS, ITEM_AJUDA, ITEM_EMPRESAS, ITEM_AUDITORIA, ITEM_IBPT, ITEM_DIFAL, ITEM_CREDITO_SN, ITEM_ALIQ_OFICIAIS, ITEM_SIMPLES_2027, ITEM_CONFERENCIA,
  ];
  const menu = isAdmin ? menuAdmin : menuOperador;

  const renderPage = () => {
    switch (page) {
      case 'empresas': return isAdmin ? <EmpresasPage /> : <DashboardPage />;
      case 'minha_empresa': return <MinhaEmpresaPage />;
      case 'emitir': return <EmitirPage />;
      case 'notas': return <NotasPage />;
      case 'notas_legado': return <NotasLegadoPage />;
      case 'clientes': return <ClientesPage />;
      case 'produtos': return <ProdutosPage />;
      case 'ajuda': return <AjudaPage />;
      case 'auditoria': return isAdmin ? <AuditoriaPage /> : <DashboardPage />;
      case 'ibpt': return isAdmin ? <IbptPage /> : <DashboardPage />;
      case 'difal': return isAdmin ? <DifalPage /> : <DashboardPage />;
      case 'credito_sn': return isAdmin ? <CreditoSnPage /> : <DashboardPage />;
      case 'aliquotas_oficiais': return isAdmin ? <AliquotasOficiaisPage /> : <DashboardPage />;
      case 'simples_2027': return isAdmin ? <Simples2027Page /> : <DashboardPage />;
      case 'conferencia': return isAdmin ? <ConferenciaPage /> : <DashboardPage />;
      default: return <DashboardPage />;
    }
  };

  return (
    <NavContext.Provider value={{ navigate, payload: navPayload, page }}>
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Sidebar */}
      <Sidebar
        menu={menu}
        currentPage={page}
        onNavigate={navigate}
        user={user}
        isAdmin={isAdmin}
        onLogout={logout}
        onAbrirAssistente={() => setAssistenteAberto(true)}
      />

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflowY: 'auto' }}>
        <EmpresaHeader />
        <div style={{ padding: '24px 32px', flex: 1 }}>
          {renderPage()}
        </div>
      </div>
      <AssistenteFlutuante aberto={assistenteAberto} setAberto={setAssistenteAberto} />
      <ChangelogModal />
    </div>
    </NavContext.Provider>
  );
}

// Header global com empresa ativa — aparece em todas as páginas autenticadas
function EmpresaHeader() {
  const { user } = useAuth();
  const emp = user?.empresa;
  if (!emp) return null;
  const nome = emp.razao_social || emp.nome_fantasia || '';
  if (!nome) return null;
  const cnpj = emp.cnpj && emp.cnpj.length === 14 ? fmtCnpj(emp.cnpj) : (emp.cnpj || '');
  return (
    <div style={{
      padding: '14px 32px',
      background: '#fafaf8',
      borderBottom: `1px solid ${cores.cinzaBorda}`,
    }}>
      <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>{nome}</p>
      {cnpj && (
        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>CNPJ: {cnpj}</p>
      )}
    </div>
  );
}

// ============================================================
// APP
// ============================================================
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { user } = useAuth();
  return user ? <Layout /> : <LoginPage />;
}
